import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { Camera, Video, Plane, Image as ImageIcon, Calendar as CalendarIcon, MapPin, Clock, DollarSign, CheckCircle2, LogIn, LogOut, Shield, Users, Settings as SettingsIcon, ArrowRight, ArrowLeft, Globe, Sun, CloudRain, Wind, AlertTriangle, Upload, Loader2, Mic, X, ChevronLeft, ChevronRight, Download, Play, Pause, Volume2, VolumeX, Maximize, Menu } from "lucide-react";
import { api, startLogin } from "./api";
import { QuickBooksSettings } from "./components/QuickBooksSettings";
import { GoogleCalendarSettings } from "./components/GoogleCalendarSettings";
import { PinMap } from "./components/PinMap";
import { t, translations, serviceName } from "./i18n";
import "./App.css";

// ===== Shell =====
// French-style money: comma decimal, space before $ ("36 $", "1 050,50 $")
const fmtCAD = (n, decimals = null) => {
  const v = Number(n || 0);
  const d = decimals ?? (Number.isInteger(v) ? 0 : 2);
  return `${v.toLocaleString("fr-CA", { minimumFractionDigits: d, maximumFractionDigits: d })} $`;
};
const vfLabel = (vf, lang) => !vf ? "" : (lang === "fr"
  ? { vertical: "Verticale", horizontal: "Horizontale", both: "Les deux formats" }[vf]
  : { vertical: "Vertical", horizontal: "Horizontal", both: "Both formats" }[vf]) || "";
const VS_LABELS = {
  pace: { slow: ["Lent et cinématique", "Slow and cinematic"], balanced: ["Équilibré", "Balanced"], fast: ["Rapide et dynamique", "Fast and dynamic"] },
  on_screen_text: { none: ["Aucun", "None"], some: ["Un peu", "Some"], lots: ["Beaucoup", "Lots"] },
  music: { calm: ["Calme et chic", "Calm and classy"], modern: ["Moderne et entraînante", "Modern and upbeat"], any: ["Peu importe", "No preference"] },
  platforms: { meta: ["Facebook et Instagram", "Facebook and Instagram"], tiktok: ["TikTok", "TikTok"], youtube: ["YouTube", "YouTube"], web: ["Site web ou fiche Centris", "Website or Centris listing"] },
};
const vsLabel = (group, key, lang) => (VS_LABELS[group]?.[key] || ["", ""])[lang === "fr" ? 0 : 1];
const VideoStyleBlock = ({ style, lang, id }) => {
  const fr = lang === "fr";
  const rows = [];
  if (style.trust_us) rows.push([fr ? "Style vidéo" : "Video style", fr ? "Je vous fais confiance, soyez créatifs" : "I trust you, be creative"]);
  else {
    if (style.pace) rows.push([fr ? "Montage" : "Edit pace", vsLabel("pace", style.pace, lang)]);
    if (style.on_screen_text) rows.push([fr ? "Texte à l'écran" : "On-screen text", vsLabel("on_screen_text", style.on_screen_text, lang)]);
    if (style.music) rows.push([fr ? "Musique" : "Music", vsLabel("music", style.music, lang)]);
    if (style.on_camera != null) rows.push([fr ? "Devant la caméra" : "On camera", style.on_camera ? (fr ? "Oui" : "Yes") : (fr ? "Non" : "No")]);
  }
  if ((style.platforms || []).length) rows.push([fr ? "Diffusion" : "Publishing", style.platforms.map(p => vsLabel("platforms", p, lang)).join(", ")]);
  if (!rows.length) return null;
  return (
    <div className="mt-1 text-xs text-zinc-300 space-y-0.5" data-testid={`session-video-style-${id}`}>
      {rows.map(([l, v]) => <div key={l}><span className="text-zinc-500">{l} :</span> {v}</div>)}
    </div>
  );
};
const fmtDur = (min, lang) => {
  const h = Math.floor((min || 0) / 60), m = (min || 0) % 60;
  const about = t(lang, "about");
  if (!h) return `${about} ${m} min`;
  return `${about} ${h} h${m ? " " + String(m).padStart(2, "0") : ""}`;
};
const fill = (s, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(v), s || "");

// Phone: (514) 000-0000
const fmtPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : (p || "");
};
// Decimal numbers with a comma (22,1)
const fmtNum = (n, d = 1) => Number(n ?? 0).toLocaleString("fr-CA", { minimumFractionDigits: 0, maximumFractionDigits: d });

// Sessions are stored as America/Toronto wall clock. A naive string is rendered as-is
// (no browser-timezone shift); a string carrying an offset is converted to Toronto.
const fmtDateTime = (iso, lang) => {
  const loc = lang === "en" ? "en-CA" : "fr-CA";
  const s = String(iso || "");
  const naive = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/(Z|[+-]\d{2}:?\d{2})$/.test(s);
  const d = naive
    ? new Date(+s.slice(0,4), +s.slice(5,7) - 1, +s.slice(8,10), +s.slice(11,13), +s.slice(14,16))
    : new Date(s);
  const tz = naive ? {} : { timeZone: "America/Toronto" };
  const date = d.toLocaleDateString(loc, { day: "numeric", month: "long", year: "numeric", ...tz });
  const time = d.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit", ...tz });
  return `${date}, ${time}`;
};

// Merge gallery refreshes without changing media URLs: keep existing signed URLs
// (so <img>/<video> src strings stay stable -> no reload, no flicker, no playback cut).
const mergeGalleryData = (prev, next) => {
  if (!prev?.gallery || !next?.gallery) return next;
  const prevAssets = {};
  for (const d of prev.gallery.deliverables || [])
    for (const a of d.assets || []) prevAssets[a.id] = a;
  const deliverables = (next.gallery.deliverables || []).map(d => ({
    ...d,
    assets: (d.assets || []).map(a => {
      const p = prevAssets[a.id];
      return p ? { ...a, preview_url: p.preview_url || a.preview_url, original_url: p.original_url || a.original_url } : a;
    }),
  }));
  return { ...next, gallery: { ...next.gallery, deliverables } };
};

function useLang() {
  const [lang, setLang] = useState(() => localStorage.getItem("kr_lang") || "fr");
  useEffect(() => localStorage.setItem("kr_lang", lang), [lang]);
  return [lang, setLang];
}

function Header({ lang, setLang, user, onLogout }) {
  const [open, setOpen] = useState(false);
  const dashPath = user?.role === "courtier" ? "/mon-espace" : "/seances";
  return (
    <header className="kr-nav-glass sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/60 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-home-link" aria-label="Kréatif">
          <img src="https://www.kreatif.ca/wp-content/uploads/2025/01/logo_kreatif_white.svg" alt="Kréatif" className="h-8 sm:h-9 w-auto" />
        </Link>
        <nav className="hidden sm:flex items-center gap-2 sm:gap-4">
          <button data-testid="lang-switch-btn" onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="kr-btn kr-btn-ghost text-sm px-3 py-1.5">
            <Globe className="w-4 h-4" />{lang.toUpperCase()}
          </button>
          <Link to="/reserver" className="kr-btn kr-btn-primary text-sm" data-testid="nav-book-btn">{t(lang,"nav_book")}</Link>
          {user ? (
            <>
              <Link to={dashPath} className="kr-btn kr-btn-ghost text-sm" data-testid="nav-dashboard-link">{t(lang,"nav_dashboard")}</Link>
              {user.role === "admin" && (
                <Link to="/admin" className="kr-btn kr-btn-ghost text-sm" data-testid="nav-admin-link"><Shield className="w-4 h-4"/><span>{lang === "fr" ? "Administration" : "Admin"}</span></Link>
              )}
              <button onClick={onLogout} className="kr-btn kr-btn-ghost text-sm" data-testid="nav-logout-btn"><LogOut className="w-4 h-4" /></button>
            </>
          ) : (
            <Link to="/connexion" className="kr-btn kr-btn-ghost text-sm" data-testid="nav-login-btn"><LogIn className="w-4 h-4"/><span>{t(lang,"nav_login")}</span></Link>
          )}
        </nav>
        <div className="flex sm:hidden items-center gap-2 min-w-0">
          <Link to="/reserver" className="kr-btn kr-btn-primary text-sm px-4 py-2 shrink-0" data-testid="nav-book-btn-mobile">{t(lang,"nav_book")}</Link>
          <button onClick={() => setOpen(o => !o)} className="kr-btn kr-btn-ghost px-3 py-2 shrink-0" aria-label="Menu" data-testid="nav-menu-toggle">
            {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
          </button>
        </div>
      </div>
      {open && (
        <div className="sm:hidden kr-glass-strong border-t border-white/10 px-4 py-3 flex flex-col gap-2" data-testid="nav-mobile-menu">
          <button data-testid="lang-switch-btn-mobile" onClick={() => { setLang(lang === "fr" ? "en" : "fr"); setOpen(false); }} className="kr-btn kr-btn-ghost text-sm justify-start">
            <Globe className="w-4 h-4" />{lang.toUpperCase()}
          </button>
          {user ? (
            <>
              <Link to={dashPath} onClick={() => setOpen(false)} className="kr-btn kr-btn-ghost text-sm justify-start" data-testid="nav-dashboard-link-mobile">{t(lang,"nav_dashboard")}</Link>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="kr-btn kr-btn-ghost text-sm justify-start" data-testid="nav-admin-link-mobile"><Shield className="w-4 h-4"/>{lang === "fr" ? "Administration" : "Admin"}</Link>
              )}
              <button onClick={() => { setOpen(false); onLogout(); }} className="kr-btn kr-btn-ghost text-sm justify-start" data-testid="nav-logout-btn-mobile"><LogOut className="w-4 h-4" />{lang === "fr" ? "Déconnexion" : "Log out"}</button>
            </>
          ) : (
            <Link to="/connexion" onClick={() => setOpen(false)} className="kr-btn kr-btn-ghost text-sm justify-start" data-testid="nav-login-btn-mobile"><LogIn className="w-4 h-4"/>{t(lang,"nav_login")}</Link>
          )}
        </div>
      )}
    </header>
  );
}

// ===== Landing =====
// Single delegated pointermove listener feeding --mx/--my to the hovered glass card
function CardGlow() {
  useEffect(() => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let frame = null, pending = null;
    const apply = () => {
      frame = null;
      if (!pending) return;
      const { card, x, y } = pending;
      card.style.setProperty("--mx", `${x}px`);
      card.style.setProperty("--my", `${y}px`);
    };
    const onMove = (e) => {
      const card = e.target.closest?.(".kr-card");
      if (!card) return;
      const r = card.getBoundingClientRect();
      pending = { card, x: e.clientX - r.left, y: e.clientY - r.top };
      if (!frame) frame = requestAnimationFrame(apply);
    };
    document.addEventListener("pointermove", onMove, { passive: true });
    return () => { document.removeEventListener("pointermove", onMove); if (frame) cancelAnimationFrame(frame); };
  }, []);
  return null;
}

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true" data-testid="google-logo" className="shrink-0">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.2 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 3-2.26 5.54-4.78 7.25l7.73 6c4.51-4.18 7.09-10.36 7.09-17.72z"/>
    <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 0 1 9.77 24c0-1.6.27-3.15.76-4.59l-7.98-6.19A23.96 23.96 0 0 0 0 24c0 3.88.93 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.9-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.17 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

function ProofLine({ lang }) {
  const [pl, setPl] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setPl(r.data.proof_line || {})).catch(() => setPl({})); }, []);
  if (!pl) return null;
  const v = (k) => pl[lang]?.[k] || t(lang, `proof_${k}`);
  const Block = ({ id, strong, rest, logo }) => (
    <div className="flex flex-col items-center text-center min-w-[7.5rem]" data-testid={`proof-${id}`}>
      <span className="text-[#D8301C] font-medium text-base sm:text-lg leading-tight">{strong}</span>
      <span className="text-zinc-500 text-xs sm:text-sm inline-flex items-center gap-1.5">{logo && <GoogleG/>}{rest}</span>
    </div>
  );
  return (
    <div className="mt-9 flex flex-wrap items-start justify-center gap-6 sm:gap-12" data-testid="proof-line">
      <Block id="since" strong={v("since")} rest={v("since_rest")}/>
      <Block id="amount" strong={v("amount")} rest={v("amount_rest")}/>
      {pl.google_url ? (
        <a href={pl.google_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity" data-testid="proof-google">
          <Block id="rating" strong={v("rating")} rest={v("google")} logo/>
        </a>
      ) : <Block id="rating" strong={v("rating")} rest={v("google")} logo/>}
    </div>
  );
}

function Landing({ lang }) {
  const [services, setServices] = useState([]);
  const [cfg, setCfg] = useState({});
  useEffect(() => { api.get("/services").then(r => setServices(r.data)); api.get("/settings").then(r => setCfg(r.data)).catch(() => {}); }, []);
  const faqVars = (() => {
    const prices = (code) => (services.find(s => s.code === code)?.tiers || []).map(t => t.price);
    const p = prices("photo"), v = prices("video");
    const vt = cfg.video_texts || {};
    return {
      photo_min: fmtCAD(p.length ? Math.min(...p) : 175), photo_max: fmtCAD(p.length ? Math.max(...p) : 275),
      video_min: fmtCAD(v.length ? Math.min(...v) : 897), video_max: fmtCAD(v.length ? Math.max(...v) : 1247),
      extra_photo: fmtCAD(cfg.extra_photo_price ?? 2), evening: vt[`evening_${lang}`] || vt.evening_fr || "",
      photos_eta: (lang === "en" ? (cfg.deliverable_eta?.photos_en || cfg.deliverable_eta?.photos) : cfg.deliverable_eta?.photos) || "",
      plans_price: fmtCAD(cfg.plans_price ?? 0),
      drone_price: fmtCAD(cfg.drone_prices?.with_photo_session ?? 0),
      drone_alone: fmtCAD(cfg.drone_prices?.alone ?? 0),
      both_formats: fmtCAD(cfg.vertical_add_fee ?? 0),
      rush_fee: fmtCAD(cfg.rush_delivery_fee ?? 0),
    };
  })();

  // Inject Service/Offer JSON-LD dynamically from fetched services
  useEffect(() => {
    if (!services.length) return;
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-kr-schema", "services");
    tag.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": services.map(s => ({
        "@type": "Service",
        "name": s.name,
        "description": s.description,
        "provider": { "@id": "https://photo-video-dispatch.preview.emergentagent.com/#business" },
        "areaServed": "Québec",
        "offers": (s.tiers || []).map(ti => ({
          "@type": "Offer",
          "name": `${s.name} — ${ti.label}`,
          "price": ti.price,
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock",
          "url": "https://photo-video-dispatch.preview.emergentagent.com/reserver",
        })),
      })),
    });
    document.head.appendChild(tag);
    return () => { document.querySelectorAll('script[data-kr-schema="services"]').forEach(n => n.remove()); };
  }, [services]);

  const icons = { photo: Camera, video: Video, photo_video: ImageIcon, drone: Plane };
  const faq = (translations[lang] && translations[lang].landing_faq) || translations.fr.landing_faq;

  return (
    <main data-testid="landing-page">
      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-10 lg:pt-[72px] pb-16 text-center">
        <div className="kr-caption mb-4" data-testid="hero-eyebrow">{t(lang,"landing_eyebrow")}</div>
        <h1 className="hero-title font-display" data-testid="hero-title">
          {lang === "en" ? (
            <>
              <span className="hero-line">Book your <span className="gradient-text italic">real estate</span> photo and video</span>{" "}
              <br className="hero-br"/>
              <span className="hero-line">shoot in just a few clicks</span>
            </>
          ) : (
            <>
              <span className="hero-line">Réservez votre séance photos et vidéo</span>{" "}
              <br className="hero-br"/>
              <span className="hero-line"><span className="gradient-text italic">immobilière</span> en quelques clics</span>
            </>
          )}
        </h1>
        <p className="text-sm text-zinc-500 mt-4 tracking-wide">{t(lang,"landing_regions")}</p>
        <p className="text-base sm:text-lg text-zinc-400 max-w-xl mx-auto mt-5 leading-relaxed">{t(lang,"landing_paragraph")}</p>
        <div className="mt-8">
          <Link to="/reserver" className="kr-btn kr-btn-primary text-base px-8 py-4" data-testid="hero-cta-primary">
            {t(lang,"landing_cta")} <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
        <ProofLine lang={lang}/>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-24" aria-label="Forfaits">
        <h2 className="sr-only">{t(lang,"services_title")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
          {services.filter(s => ["photo", "video", "photo_video"].includes(s.code)).map(s => {
            const Icon = icons[s.code] || Camera;
            const starting = ((s.tiers||[])[0]?.price) || s.base_price;
            return (
              <Link key={s.id} to={`/reserver?service=${s.code}`} className="kr-card p-5 block" data-testid={`service-card-${s.code}`}>
                <span className="kr-icon-pill mb-3"><Icon className="w-5 h-5" aria-hidden="true" /></span>
                <h3 className="font-display text-sm">{serviceName(lang, s)}</h3>
                <p className="text-xs text-zinc-500 mt-1">{t(lang,"landing_from")} <span className="gold-text font-display">{starting} $</span></p>
              </Link>
            );
          })}
        </div>
        <p className="text-xs text-zinc-500 text-center mt-6">{t(lang,"landing_travel_note")}</p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-24" aria-labelledby="faq-title" data-testid="faq-section">
        <h2 id="faq-title" className="font-display text-3xl sm:text-4xl text-center mb-10">{t(lang,"faq_title")}</h2>
        <div className="space-y-3">
          {faq.map(([q, a], i) => (
            <details key={i} className="kr-card p-5 group" data-testid={`faq-item-${i}`}>
              <summary className="font-display text-base cursor-pointer list-none flex items-center justify-between">
                <span>{q}</span>
                <span className="gold-text ml-4 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{fill(a, faqVars)}</p>
            </details>
          ))}
        </div>
      </section>
    </main>
  );
}

// ===== Booking Wizard =====
function readUtm() {
  try {
    const q = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source","utm_medium","utm_campaign","utm_content"].forEach(k => {
      const v = q.get(k); if (v) utm[k] = v;
    });
    // Store for later steps too
    if (Object.keys(utm).length) sessionStorage.setItem("kr_utm", JSON.stringify(utm));
    const promo = q.get("promo");
    if (promo) sessionStorage.setItem("kr_promo", promo.toUpperCase());
  } catch {}
}

// ===== Address autocomplete (server-side Google Places proxy, session tokens) =====
function AddressInput({ lang, value, onChange, onSelect, validated, forceWarn }) {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [blurred, setBlurred] = useState(false);
  const [picking, setPicking] = useState(false);
  const sess = useRef(crypto.randomUUID());
  const timer = useRef(null);
  const fetchSugs = (q) => {
    clearTimeout(timer.current);
    if (q.trim().length < 3) { setSugs([]); return; }
    timer.current = setTimeout(() => {
      api.post("/places/autocomplete", { input: q, session_token: sess.current }).then(r => { setSugs(r.data.suggestions || []); setOpen(true); }).catch(() => setSugs([]));
    }, 250);
  };
  const pick = async (s) => {
    clearTimeout(timer.current);
    setPicking(true); setSugs([]); setOpen(false);
    onChange(s.text);
    try {
      const { data } = await api.get("/places/details", { params: { place_id: s.place_id, session_token: sess.current } });
      onSelect({ ...data, formatted_address: data.formatted_address || s.text, place_id: s.place_id });
      sess.current = crypto.randomUUID();
    } catch {
      toast.error(t(lang, "addr_pick_failed"));
    }
    setPicking(false);
  };
  return (
    <div className="relative" style={{ zIndex: 50 }} data-testid="address-autocomplete">
      <input data-testid="input-property-address" value={value} autoComplete="off" inputMode="text"
        onChange={e => { onChange(e.target.value); setBlurred(false); fetchSugs(e.target.value); }}
        onBlur={() => setTimeout(() => { setOpen(false); setBlurred(true); }, 250)} onFocus={() => sugs.length && setOpen(true)}
        placeholder={t(lang,"property_address_placeholder")} />
      {open && sugs.length > 0 && (
        <ul style={{ backgroundColor: "#141414", backdropFilter: "none", WebkitBackdropFilter: "none" }}
          className="absolute z-[60] top-full left-0 right-0 mt-1 rounded-lg border border-[#3A3A40] shadow-2xl overflow-hidden" data-testid="address-suggestions">
          {sugs.map(s => (
            <li key={s.place_id}>
              <button type="button" onMouseDown={e => { e.preventDefault(); pick(s); }} onTouchStart={e => { e.preventDefault(); pick(s); }}
                style={{ backgroundColor: "#141414" }}
                className="w-full text-left px-4 py-3 text-sm hover:brightness-150 border-b border-[rgba(216,48,28,0.35)] last:border-0" data-testid={`address-suggestion-${s.place_id}`}>
                <div className="font-medium flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#D8301C] shrink-0"/>{s.main}</div>
                <div className="text-xs text-zinc-500 ml-5">{s.secondary}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="h-5 mt-1 overflow-hidden">
        {picking ? <div className="text-xs text-zinc-400 flex items-center gap-1" data-testid="address-picking"><Loader2 className="w-3.5 h-3.5 animate-spin"/>{t(lang,"addr_checking")}</div>
          : validated ? <div className="text-xs text-emerald-400 flex items-center gap-1" data-testid="address-validated"><CheckCircle2 className="w-3.5 h-3.5"/>{t(lang,"addr_validated")}</div>
          : (forceWarn || blurred) && value.trim().length >= 3 ? (
            <div className="text-xs text-[#E6412D] flex items-center gap-1" data-testid="address-unvalidated"><AlertTriangle className="w-3.5 h-3.5"/>{t(lang,"err_address_pick")}</div>
          ) : null}
      </div>
    </div>
  );
}

// ===== Address autocomplete (server-side Google Places proxy, session tokens) =====
function BookingWizard({ lang }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    property_address: "", area_sqft: "", property_status: "occupied", access_mode: "keybox", lockbox_code: "",
    centris_number: "", notes: "", service_codes: [], video_format: "vertical", start_at: "", team_member_id: "",
    directions_note: "", onsite_contact: "", pin_lat: null, pin_lng: null, pin_adjusted: false,
    coords_mode: false, location_description: "", plans_addon: false,
    video_style: { trust_us: false, pace: null, on_screen_text: null, music: null, platforms: [], on_camera: null },
    client_name: "", client_first_name: "", client_last_name: "", client_email: "", client_phone: "", client_lang: lang,
    tiers: {}, options: {}, property_lat: null, property_lng: null, property_place_id: null, address_validated: false,
  });
  const [services, setServices] = useState([]);
  const [cfg, setCfg] = useState({});
  const [travel, setTravel] = useState(null);
  const [weather, setWeather] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [addrWarn, setAddrWarn] = useState(false);
  const [coordsOpen, setCoordsOpen] = useState(false);
  const [coordsInput, setCoordsInput] = useState("");
  const [coordsErr, setCoordsErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // {code, discount, discount_requested, capped_to_floor, message}
  const [promoError, setPromoError] = useState("");
  const [promoBanner, setPromoBanner] = useState("");
  const [dur, setDur] = useState(null); // from /duration/estimate

  useEffect(() => { readUtm(); api.get("/services").then(r => setServices(r.data)); api.get("/settings").then(r => setCfg(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const pre = new URLSearchParams(window.location.search).get("service");
    if (pre && ["photo", "video", "photo_video"].includes(pre)) setForm(f => f.service_codes.length ? f : { ...f, service_codes: [pre] });
  }, []);

  useEffect(() => {
    api.post("/duration/estimate", {
      service_codes: form.service_codes, tier_ids: tierIdsFor(), option_ids: form.options,
      plans_addon: form.plans_addon,
      area_sqft: form.area_sqft ? parseInt(form.area_sqft) : null, access_mode: form.access_mode,
    }).then(r => {
      setDur(r.data);
      if (r.data.combined) {
        const tiers = {}; r.data.service_codes.forEach((c, i) => { if (r.data.tier_ids[i]) tiers[c] = r.data.tier_ids[i]; });
        setForm(f => ({ ...f, service_codes: r.data.service_codes, tiers: { ...f.tiers, ...tiers }, options: { ...f.options, ...r.data.option_ids } }));
        toast.info(t(lang,"combo_switched"), { id: "combo-switched", duration: 4000 });
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_codes, form.tiers, form.options, form.plans_addon, form.area_sqft, form.access_mode, services]);

  // Auto-apply promo from URL once we reach step 4 if email is set
  useEffect(() => {
    const auto = sessionStorage.getItem("kr_promo");
    if (auto && !promo && !promoInput) setPromoInput(auto);
  }, [promo, promoInput]);

  const toggleService = (code) => {
    setForm(f => {
      if (f.service_codes.includes(code)) return { ...f, service_codes: f.service_codes.filter(c => c !== code) };
      // The combo replaces "photo" and "video": never bill the same service twice
      let codes = f.service_codes.filter(c => code === "photo_video" ? !["photo", "video"].includes(c) : (["photo", "video"].includes(code) ? c !== "photo_video" : true));
      const svc = services.find(x => x.code === code);
      // No paid pre-selection: a single-tier service is set, otherwise the broker picks
      const tier = (svc?.tiers || []).length === 1 ? svc.tiers[0].id : undefined;
      const opt = code === "photo_video" ? (svc?.options || [])[0]?.id : undefined;
      return { ...f, service_codes: [...codes, code], tiers: tier ? { ...f.tiers, [code]: tier } : f.tiers, options: opt ? { ...f.options, [code]: opt } : f.options };
    });
  };

  const servicesTotal = () => dur?.services_price ?? 0;
  const tierIdsFor = () => form.service_codes.map(c => form.tiers[c] || null);
  const plansPrice = cfg.plans_price ?? 97;

  // Drone photos cost less with a photo session: keep the card price identical to the billed price
  const tierPrice = (code, ti) => {
    const dp = cfg.drone_prices || {};
    if (ti.id === "drone_photos" && dp.with_photo_session != null) {
      return form.service_codes.some(c => ["photo", "photo_video"].includes(c)) ? dp.with_photo_session : dp.alone;
    }
    return ti.price;
  };
  const totalDuration = () => dur?.onsite_min || 0;
  const taxes = (sub) => {
    const g = Math.round(sub * (cfg.tax_gst_rate ?? 0.05) * 100) / 100, q = Math.round(sub * (cfg.tax_qst_rate ?? 0.09975) * 100) / 100;
    return { gst: g, qst: q, total: Math.round((sub + g + q) * 100) / 100 };
  };
  const hasVideo = () => form.service_codes.some(c => c === "video" || c === "photo_video");
  const bothFee = (hasVideo() && form.video_format === "horizontal_plus_vertical") ? (cfg.vertical_add_fee || 0) : 0;
  const presenterIncluded = () => Object.values(form.tiers || {}).some(id => ["video_avec", "combo_avec", "video_prestige", "combo_prestige"].includes(id));
  const vs = form.video_style || {};
  const cameraFee = (hasVideo() && vs.on_camera && !vs.trust_us && !presenterIncluded()) ? (cfg.on_camera_fee || 0) : 0;
  const setVs = (patch) => setForm(f => ({ ...f, video_style: { ...f.video_style, ...patch } }));
  const togglePlatform = (p) => setForm(f => {
    const cur = f.video_style.platforms || [];
    return { ...f, video_style: { ...f.video_style, platforms: cur.includes(p) ? cur.filter(x => x !== p) : [...cur, p] } };
  });
  const steps = hasVideo() ? [1, 2, 3, 4, 5, 6] : [1, 2, 4, 5, 6];
  const stepNo = (n) => steps.indexOf(n) + 1;

  const applyPromo = async () => {
    setPromoError(""); setPromo(null);
    if (!promoInput.trim()) return;
    if (!form.client_email) { setPromoError(t(lang,"promo_need_email")); return; }
    try {
      const { data } = await api.post("/promo-codes/validate", {
        code: promoInput.trim(), client_email: form.client_email,
        service_codes: form.service_codes, tier_ids: tierIdsFor(),
        services_price: servicesTotal(),
      });
      setPromo(data);
    } catch (e) {
      setPromoError(e.response?.data?.detail || t(lang,"err_generic"));
    }
  };

  const removePromo = () => { setPromo(null); setPromoInput(""); setPromoError(""); setPromoBanner(""); sessionStorage.removeItem("kr_promo"); };

  const applyCoords = (raw) => {
    const m = String(raw || "").trim().match(/^(-?\d{1,2}(?:\.\d+)?)\s*[,; ]\s*(-?\d{1,3}(?:\.\d+)?)$/);
    if (!m) { setCoordsErr(t(lang,"coords_invalid")); return false; }
    const lat = parseFloat(m[1]), lng = parseFloat(m[2]);
    if (!(lat >= 44.9 && lat <= 62.7 && lng >= -79.9 && lng <= -56.9)) { setCoordsErr(t(lang,"coords_not_qc")); return false; }
    setCoordsErr("");
    setForm(f => ({ ...f, coords_mode: true, property_lat: lat, property_lng: lng, pin_lat: lat, pin_lng: lng,
      pin_adjusted: false, property_place_id: null, address_validated: true }));
    return true;
  };

  const next = async () => {
    if (step === 1) {
      if (form.coords_mode) {
        if (!applyCoords(coordsInput || `${form.pin_lat}, ${form.pin_lng}`)) return toast.error(coordsErr || t(lang,"coords_invalid"));
        if (!form.location_description.trim()) return toast.error(t(lang,"err_location_desc"));
      } else {
        if (!form.property_address) { setAddrWarn(true); return toast.error(t(lang,"err_address")); }
        if (!form.address_validated) { setAddrWarn(true); return toast.error(t(lang,"err_address_pick")); }
      }
      if (form.access_mode === "owner_present" && !form.onsite_contact.trim()) return toast.error(t(lang,"err_onsite_contact"));
      setLoading(true);
      const { data } = await api.post("/travel/estimate", { property_address: form.coords_mode ? form.location_description : form.property_address, lat: form.pin_lat ?? form.property_lat, lng: form.pin_lng ?? form.property_lng });
      setTravel(data); setLoading(false); setStep(2);
    } else if (step === 2) {
      if (!form.service_codes.length) return toast.error(t(lang,"err_service"));
      const missing = form.service_codes.filter(c => {
        const svc = services.find(x => x.code === c);
        return (svc?.tiers || []).length > 1 && !form.tiers[c];
      });
      if (missing.length) return toast.error(t(lang,"err_tier"));
      setStep(hasVideo() ? 3 : 4);
    } else if (step === 3) {
      if (!(vs.platforms || []).length) return toast.error(lang === "fr" ? "Indiquez où la vidéo sera publiée." : "Please tell us where the video will be published.");
      if (!vs.trust_us && (!vs.pace || !vs.on_screen_text || !vs.music || vs.on_camera == null))
        return toast.error(lang === "fr" ? "Répondez aux questions sur le style de la vidéo." : "Please answer the video style questions.");
      setStep(4);
    } else if (step === 4) {
      if (!form.start_at || !form.team_member_id) return toast.error(t(lang,"err_slot"));
      setStep(5);
    } else if (step === 5) {
      if (!form.client_first_name.trim() || !form.client_last_name.trim() || !form.client_email || !form.client_phone) return toast.error(t(lang,"err_contact"));
      setLoading(true);
      try {
        const utm = JSON.parse(sessionStorage.getItem("kr_utm") || "{}");
        const payload = {
          ...form,
          area_sqft: form.area_sqft ? parseInt(form.area_sqft) : null,
          client_lang: lang,
          tier_ids: tierIdsFor(),
          option_ids: form.options,
          tiers: undefined, options: undefined,
          option_ids: form.options,
          video_format: form.video_format || "vertical",
          video_style: hasVideo() ? form.video_style : null,
          client_name: `${form.client_first_name.trim()} ${form.client_last_name.trim()}`.trim(),
          pin_lat: form.pin_lat, pin_lng: form.pin_lng, pin_adjusted: form.pin_adjusted,
          coords_mode: form.coords_mode, location_description: form.location_description,
          property_address: form.coords_mode ? form.location_description : form.property_address,
          directions_note: form.directions_note, onsite_contact: form.onsite_contact,
          tiers: undefined, options: undefined,
          prep_confirmed: true,
          promo_code: promo?.code || null,
          utm_source: utm.utm_source || null,
          utm_medium: utm.utm_medium || null,
          utm_campaign: utm.utm_campaign || null,
          utm_content: utm.utm_content || null,
        };
        const { data } = await api.post("/bookings", payload);
        setConfirmed(data); setStep(6);
        sessionStorage.removeItem("kr_promo");
      } catch (e) {
        toast.error(e.response?.data?.detail || t(lang,"err_generic"));
      }
      setLoading(false);
    }
  };

  // Show banner once promo auto-applied
  useEffect(() => {
    if (promo && !promoBanner) {
      const base = t(lang,"promo_banner_applied").replace("{amount}", promo.discount.toFixed(2));
      setPromoBanner(base + (promo.capped_to_floor ? " " + t(lang,"promo_floor_note") : ""));
    }
  }, [promo, promoBanner, lang]);

  const loadDate = async (d) => {
    setDate(d); setForm(f => ({ ...f, start_at: "", team_member_id: "" }));
    if (!d) return;
    setLoading(true);
    const [w, s] = await Promise.all([
      api.get("/weather", { params: { address: form.property_address, date: d, lat: form.pin_lat ?? form.property_lat, lng: form.pin_lng ?? form.property_lng } }).then(r => r.data).catch(() => null),
      api.post("/availability", { date: d, service_codes: form.service_codes, tier_ids: tierIdsFor(), option_ids: form.options, plans_addon: form.plans_addon, area_sqft: form.area_sqft ? parseInt(form.area_sqft) : null, access_mode: form.access_mode, property_address: form.property_address, property_lat: form.pin_lat ?? form.property_lat, property_lng: form.pin_lng ?? form.property_lng }).then(r => r.data),
    ]);
    setWeather(w); setSlots(s.slots || []); setLoading(false);
  };

  return (
    <div className="max-w-[720px] mx-auto px-4 sm:px-6 pt-5 pb-10" data-testid="booking-wizard">
      {promoBanner && (
        <div className="mb-6 p-4 rounded-xl border border-[#D8301C]/50 bg-[#141414] flex items-center justify-between" data-testid="promo-banner">
          <span className="gold-text font-medium">🎁 {promoBanner}</span>
          <button onClick={removePromo} className="text-xs text-zinc-400 hover:text-white" data-testid="promo-banner-remove">{t(lang,"promo_remove")}</button>
        </div>
      )}
      <div className="flex items-center gap-2 mb-4">
        {steps.map(n => (
          <div key={n} className={`step-dot ${step===n?"active":""} ${step>n?"done":""}`} data-testid={`step-dot-${n}`}/>
        ))}
        <span className="ml-3 kr-caption">{t(lang, `book_step_${step}`)}</span>
      </div>

      {step === 1 && (
        <div className="kr-card kr-form-panel animate-in">
          <h2 className="font-display text-2xl mb-4">1 — {t(lang,"book_step_1")}</h2>
          <label className="kr-caption">{t(lang,"property_address")}</label>
          <AddressInput lang={lang} value={form.property_address}
            onChange={v => { setAddrWarn(false); setForm(f => ({ ...f, property_address: v, property_lat: null, property_lng: null, property_place_id: null, address_validated: false })); }}
            onSelect={p => { setAddrWarn(false); setForm(f => ({ ...f, property_address: p.formatted_address, property_lat: p.lat, property_lng: p.lng, property_place_id: p.place_id, address_validated: true })); }}
            validated={form.address_validated} forceWarn={addrWarn}/>
          {!coordsOpen ? (
            <button type="button" onClick={() => setCoordsOpen(true)} data-testid="coords-toggle"
              className="text-xs text-[#D8301C] hover:underline mt-1">{t(lang,"coords_link")}</button>
          ) : (
            <div className="mt-2 p-3 rounded-lg border border-[rgba(216,48,28,0.35)] bg-[#141414]" data-testid="coords-panel">
              <label className="kr-caption">{t(lang,"coords_label")}</label>
              <input data-testid="input-coords" value={coordsInput} placeholder="45.6612, -73.8394"
                onChange={e => { setCoordsInput(e.target.value); setCoordsErr(""); }}
                onBlur={() => applyCoords(coordsInput)} />
              <p className="text-xs text-zinc-500 mt-1">{t(lang,"coords_help")}</p>
              {coordsErr && <p className="text-xs text-[#E6412D] mt-1" data-testid="coords-error">{coordsErr}</p>}
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => applyCoords(coordsInput)} className="kr-btn kr-btn-ghost text-xs" data-testid="coords-apply">{t(lang,"coords_apply")}</button>
                <button type="button" onClick={() => { setCoordsOpen(false); setCoordsErr(""); setCoordsInput(""); setForm(f => ({ ...f, coords_mode: false, location_description: "", pin_lat: null, pin_lng: null, pin_adjusted: false, address_validated: false })); }} className="kr-btn kr-btn-ghost text-xs" data-testid="coords-cancel">{t(lang,"back")}</button>
              </div>
              {form.coords_mode && (
                <div className="mt-3" data-testid="location-desc-field">
                  <label className="kr-caption">{t(lang,"location_desc")} *</label>
                  <input data-testid="input-location-desc" value={form.location_description}
                    onChange={e => setForm(f => ({ ...f, location_description: e.target.value }))} placeholder={t(lang,"location_desc_ph")} />
                </div>
              )}
            </div>
          )}
          {form.address_validated && form.property_lat != null && (
            <PinMap lang={lang} lat={form.pin_lat ?? form.property_lat} lng={form.pin_lng ?? form.property_lng}
              adjusted={form.pin_adjusted}
              onMove={({ lat, lng }) => setForm(f => ({ ...f, pin_lat: lat, pin_lng: lng, pin_adjusted: true }))}/>
          )}
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <div><label className="kr-caption">{t(lang,"property_area")}</label><input data-testid="input-area" type="number" value={form.area_sqft} onChange={e => setForm({...form, area_sqft: e.target.value})} /></div>
            <div><label className="kr-caption">{t(lang,"property_status")}</label>
              <select data-testid="select-status" value={form.property_status} onChange={e => setForm({...form, property_status: e.target.value})}>
                <option value="occupied">{t(lang,"status_occupied")}</option><option value="vacant">{t(lang,"status_vacant")}</option>
              </select>
            </div>
            <div><label className="kr-caption">{t(lang,"access_mode")}</label>
              <select data-testid="select-access" value={form.access_mode} onChange={e => setForm({...form, access_mode: e.target.value})}>
                <option value="keybox">{t(lang,"access_keybox")}</option><option value="owner_present">{t(lang,"access_owner")}</option><option value="broker_present">{t(lang,"access_broker")}</option>
              </select>
            </div>
          </div>
          {form.access_mode === "keybox" && (
            <div className="mt-4" data-testid="lockbox-field">
              <label className="kr-caption">{t(lang,"lockbox_code")}</label>
              <input data-testid="input-lockbox" value={form.lockbox_code} onChange={e => setForm({...form, lockbox_code: e.target.value})} placeholder={lang === "fr" ? "Ex. : 1234" : "e.g. 1234"} />
              <p className="text-xs text-zinc-500 mt-1">{t(lang,"lockbox_help")}</p>
            </div>
          )}
          <div className="mt-4"><label className="kr-caption">{t(lang,"directions_note")}</label>
            <input data-testid="input-directions" value={form.directions_note} onChange={e => setForm({...form, directions_note: e.target.value})} placeholder={t(lang,"directions_note_ph")} />
          </div>
          {form.access_mode === "owner_present" && (
            <div className="mt-4" data-testid="onsite-contact-field">
              <label className="kr-caption">{t(lang,"onsite_contact")} *</label>
              <input data-testid="input-onsite-contact" value={form.onsite_contact} onChange={e => setForm({...form, onsite_contact: e.target.value})} placeholder={t(lang,"onsite_contact_ph")} />
              <p className="text-xs text-zinc-500 mt-1">{t(lang,"onsite_contact_help")}</p>
            </div>
          )}
          <div className="mt-4"><label className="kr-caption">{t(lang,"centris")}</label><input data-testid="input-centris" value={form.centris_number} onChange={e => setForm({...form, centris_number: e.target.value})} /></div>
          <div className="mt-4"><label className="kr-caption">{t(lang,"notes")}</label><textarea data-testid="input-notes" rows="3" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          <div className="flex justify-end mt-6"><button data-testid="btn-next-1" onClick={next} disabled={loading} className="kr-btn kr-btn-primary">{loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>{t(lang,"continue")} <ArrowRight className="w-4 h-4"/></>}</button></div>
        </div>
      )}

      {step === 2 && (
        <div className="kr-card kr-form-panel animate-in">
          <h2 className="font-display text-2xl mb-4">2 — {t(lang,"book_step_2")}</h2>
          {travel && travel.distance_km != null && (
            <div className="mb-6 p-4 rounded-lg bg-[#141414] border border-[rgba(216,48,28,0.35)] text-sm" data-testid="travel-box">
              <div className="font-semibold" data-testid="travel-fee-line">
                {travel.travel_fee > 0 ? fill(t(lang,"travel_fee_line"), { fee: fmtCAD(travel.travel_fee) }) : t(lang,"travel_free")}
              </div>
              <div className="text-xs text-zinc-500 mt-1" data-testid="travel-detail">
                {fill(t(lang,"travel_detail"), { km: travel.distance_km, inc: travel.included_km_oneway ?? 30, rate: fmtCAD(travel.price_per_extra_km ?? 1) })}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {services.map(s => {
              const selSvc = form.service_codes.includes(s.code);
              const dp = cfg.drone_prices || {};
              const hasPhotoSession = form.service_codes.some(c => ["photo", "photo_video"].includes(c));
              const fromPrice = s.code === "drone" && dp.with_photo_session != null
                ? (hasPhotoSession ? dp.with_photo_session : dp.alone)
                : Math.min(...(s.tiers||[]).map(x => x.price), s.base_price);
              return (
              <button key={s.id} onClick={() => toggleService(s.code)} data-testid={`service-select-${s.code}`}
                className={`text-left p-4 rounded-xl border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D8301C]/50 ${selSvc ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414] hover:border-[#D8301C]/40"}`}>
                <div className="font-display font-semibold flex items-start gap-2">
                  {selSvc && <CheckCircle2 className="w-4 h-4 text-[#D8301C] shrink-0 mt-1" data-testid={`service-check-${s.code}`}/>}
                  <span className="break-words">{serviceName(lang, s)}</span>
                </div>
                <div className="gold-text font-semibold text-sm mt-1 whitespace-nowrap">{t(lang,"svc_from")} {fmtCAD(fromPrice)}</div>
                {s.code === "drone" && dp.with_photo_session != null && (
                  <div className="text-xs text-zinc-400 mt-1" data-testid="drone-price-note">
                    {lang === "fr" ? `${fmtCAD(dp.with_photo_session)} avec une séance photo, ${fmtCAD(dp.alone)} seul` : `${fmtCAD(dp.with_photo_session)} with a photo session, ${fmtCAD(dp.alone)} alone`}
                  </div>
                )}
                {s.description && <div className="text-xs text-zinc-500 mt-1">{s.description}</div>}
                {dur?.cards?.[s.code] && (
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1" data-testid={`service-duration-${s.code}`}>
                    <Clock className="w-3 h-3 text-[#D8301C]"/>{fill(t(lang,"onsite_card"), { dur: fmtDur(dur.cards[s.code].default, lang) })}
                  </div>
                )}
              </button>
            );})}
          </div>
          {form.service_codes.map(code => {
            const s = services.find(x => x.code === code); if (!s || !(s.tiers || []).length) return null;
            const rec = dur?.recommend || {};
            const comboOn = form.service_codes.includes("photo_video");
            const vt = cfg.video_texts || {};
            return (
              <div key={code} className="mt-5" data-testid={`tiers-${code}`}>
                <div className="kr-caption mb-2">{serviceName(lang, s)} — {t(lang,"choose_tier")}</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {s.tiers.filter(ti => !(comboOn && code === "drone" && ["drone_combo", "drone_ext_photos"].includes(ti.id))).map(ti => {
                    const sel = form.tiers[code] === ti.id;
                    const isIncluded = (dur?.included || []).includes(code) && (dur?.lines || []).some(l => l.code === code && l.tier_id === ti.id);
                    const isRec = rec[code] === ti.id;
                    return (
                      <button key={ti.id} onClick={() => setForm(f => ({ ...f, tiers: { ...f.tiers, [code]: ti.id } }))} data-testid={`tier-${ti.id}`}
                        className={`text-left p-3 rounded-lg border text-sm transition-all overflow-hidden ${sel ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414] hover:border-[#D8301C]/40"}`}>
                        <div className="flex justify-between items-start gap-2">
                          <span className="font-medium flex items-start gap-1.5 min-w-0">
                            {sel && <CheckCircle2 className="w-4 h-4 text-[#D8301C] shrink-0 mt-0.5" data-testid={`tier-check-${ti.id}`}/>}
                            <span className="break-words">{lang === "en" && ti.label_en ? ti.label_en : ti.label}</span>
                          </span>
                          <span className="gold-text font-semibold whitespace-nowrap">{isIncluded ? fmtCAD(0) : fmtCAD(tierPrice(code, ti))}</span>
                        </div>
                        {isIncluded && (
                          <div className="mt-1">
                            <span className="inline-block max-w-full truncate px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] uppercase tracking-wide" data-testid={`included-badge-${ti.id}`}>
                              {lang === "fr" ? "Inclus avec le combo" : "Included with the combo"}
                            </span>
                          </div>
                        )}
                        {ti.subtitle && <div className="text-xs text-zinc-500 mt-1">{ti.subtitle}</div>}
                        <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
                          {dur?.cards?.[code]?.tiers?.[ti.id] != null && <span>{fill(t(lang,"onsite_card"), { dur: fmtDur(dur.cards[code].tiers[ti.id], lang) })}</span>}
                          {isRec && <span className="px-1.5 py-0.5 rounded bg-[#D8301C]/20 text-[#D8301C] text-[10px] uppercase tracking-wide" data-testid={`rec-${ti.id}`}>{t(lang,"recommended")}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {code === "photo_video" && (s.options || []).length > 0 && (
                  <div className="mt-3" data-testid="combo-options">
                    <div className="kr-caption mb-2">{t(lang,"combo_photos_opt")}</div>
                    <div className="grid grid-cols-3 gap-2">
                      {s.options.map(o => {
                        const sel = (form.options[code] || s.options[0].id) === o.id;
                        const isRec = rec.photo_video_option === o.id;
                        return (
                          <button key={o.id} onClick={() => setForm(f => ({ ...f, options: { ...f.options, [code]: o.id } }))} data-testid={`option-${o.id}`}
                            className={`p-2 rounded-lg border text-sm ${sel ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] hover:border-[#D8301C]/40"}`}>
                            <div className="flex items-center justify-center gap-1.5">{sel && <CheckCircle2 className="w-3.5 h-3.5 text-[#D8301C]"/>}{lang === "en" && o.label_en ? o.label_en : o.label}</div>
                            <div className="text-xs gold-text">{o.extra > 0 ? `+ ${fmtCAD(o.extra)}` : (lang === "fr" ? "inclus" : "included")}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {code === "photo" && <div className="text-xs text-zinc-500 mt-2">{fill(t(lang,"extra_photo_note"), { price: fmtCAD(cfg.extra_photo_price ?? 2) })}</div>}
                {code === "photo" && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, plans_addon: !f.plans_addon }))} data-testid="plans-addon-toggle"
                    className={`w-full text-left mt-3 p-3 rounded-lg border text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D8301C]/50 ${form.plans_addon ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414] hover:border-[#D8301C]/40"}`}>
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-medium flex items-start gap-1.5">
                        {form.plans_addon && <CheckCircle2 className="w-4 h-4 text-[#D8301C] shrink-0 mt-0.5" data-testid="plans-addon-check"/>}
                        <span className="break-words">{lang === "fr" ? `Ajoutez la prise de mesures et les plans 2D : + ${fmtCAD(plansPrice)}` : `Add measurements and 2D floor plans: + ${fmtCAD(plansPrice)}`}</span>
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{lang === "fr" ? "Plans 2D de la propriété, livrés le jour même. Environ 15 minutes de plus sur place." : "2D plans of the property, delivered the same day. About 15 extra minutes on site."}</div>
                  </button>
                )}
                {(code === "video" || code === "photo_video") && (
                  <div className="text-xs text-zinc-500 mt-2 space-y-1" data-testid="video-texts">
                    <div>{vt[`ai_${lang}`] || vt.ai_fr}</div>
                    <div>{vt[`presenter_${lang}`] || vt.presenter_fr}</div>
                    <div>{vt[`custom_${lang}`] || vt.custom_fr}</div>
                    <div className="text-zinc-400">{vt[`evening_${lang}`] || vt.evening_fr}</div>
                    <div className="mt-3 pt-3 border-t border-[rgba(216,48,28,0.35)]" data-testid="video-format">
                      <div className="kr-caption mb-2">{lang === "fr" ? "Format de la vidéo" : "Video format"} *</div>
                      <div className="grid sm:grid-cols-3 gap-2">
                        {[["vertical", lang === "fr" ? "Verticale" : "Vertical", lang === "fr" ? "Reels, TikTok, Shorts, stories" : "Reels, TikTok, Shorts, stories", true],
                          ["horizontal", lang === "fr" ? "Horizontale" : "Horizontal", lang === "fr" ? "YouTube, site web, fiche de propriété" : "YouTube, website, listing", false],
                          ["horizontal_plus_vertical", lang === "fr" ? "Horizontale + version verticale" : "Horizontal + vertical version", `+ ${fmtCAD(cfg.vertical_add_fee ?? 0)} · ${lang === "fr" ? "Tournée à l'horizontale, avec une version verticale recadrée." : "Shot horizontally, with a cropped vertical version."}`, false]
                        ].map(([val, label, note, isRec]) => {
                          const sel = form.video_format === val;
                          return (
                            <button key={val} type="button" onClick={() => setForm(f => ({ ...f, video_format: val }))} data-testid={`vf-${val}`}
                              className={`p-3 rounded-lg border text-center transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D8301C]/50 ${sel ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414] hover:border-[#D8301C]/40"}`}>
                              <div className="text-sm font-medium text-zinc-200 flex items-center justify-center gap-1.5">{sel && <CheckCircle2 className="w-3.5 h-3.5 text-[#D8301C]"/>}{label}</div>
                              <div className={`text-xs mt-1 ${val === "horizontal_plus_vertical" ? "gold-text" : "text-zinc-500"}`}>{note}</div>
                              {isRec && <div className="mt-2"><span className="px-1.5 py-0.5 rounded bg-[#D8301C]/20 text-[#D8301C] text-[10px] uppercase tracking-wide">{t(lang,"recommended")}</span></div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {form.service_codes.length > 0 && dur && (
            <div className="mt-5 p-4 rounded-lg bg-[#141414] border border-[rgba(216,48,28,0.35)] text-sm" data-testid="onsite-box">
              <div className="font-semibold flex items-center gap-2" data-testid="onsite-time"><Clock className="w-4 h-4 text-[#D8301C]"/>{fill(t(lang,"onsite_time"), { dur: fmtDur(dur.onsite_min, lang) })}</div>
              <div className="text-xs text-zinc-500 mt-1">{t(lang,"onsite_note")}</div>
              <div className="pt-3 mt-3 border-t border-[rgba(216,48,28,0.35)]" data-testid="your-selection">
                <div className="kr-caption mb-2">{lang === "fr" ? "Votre sélection" : "Your selection"}</div>
                <div className="space-y-1">
                  {(dur?.lines || []).map(l => (
                    <div key={l.code} className="flex justify-between gap-3" data-testid={`sel-line-${l.code}`}>
                      <span className="text-zinc-400 min-w-0 break-words">{lang === "en" && l.name_en ? l.name_en : l.name}{l.tier_label ? `, ${lang === "en" && l.tier_label_en ? l.tier_label_en : l.tier_label}` : ""}{l.option_label ? ` · ${lang === "en" && l.option_label_en ? l.option_label_en : l.option_label}` : ""}</span>
                      <span className="whitespace-nowrap">{l.included ? (lang === "fr" ? "inclus" : "included") : fmtCAD(l.price)}</span>
                    </div>
                  ))}
                  {bothFee > 0 && (
                    <div className="flex justify-between gap-3" data-testid="sel-line-both-formats">
                      <span className="text-zinc-400">{lang === "fr" ? "Version verticale ajoutée" : "Added vertical version"}</span>
                      <span className="whitespace-nowrap">{fmtCAD(bothFee)}</span>
                    </div>
                  )}
                  {travel?.travel_fee > 0 && (
                    <div className="flex justify-between gap-3" data-testid="sel-line-travel">
                      <span className="text-zinc-400">{t(lang,"travel_fee")}</span>
                      <span className="whitespace-nowrap">{fmtCAD(travel.travel_fee)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between font-semibold pt-3 mt-3 border-t border-[rgba(216,48,28,0.35)]"><span>{t(lang,"running_total")}</span><span className="gold-text" data-testid="running-total">{fmtCAD(servicesTotal() + (travel?.travel_fee || 0) + bothFee + cameraFee, 2)}</span></div>
            </div>
          )}
          <div className="flex justify-between mt-6">
            <button data-testid="btn-back-2" onClick={() => setStep(1)} className="kr-btn kr-btn-ghost"><ArrowLeft className="w-4 h-4"/>{t(lang,"back")}</button>
            <button data-testid="btn-next-2" onClick={next} className="kr-btn kr-btn-primary">{t(lang,"continue")} <ArrowRight className="w-4 h-4"/></button>
          </div>
        </div>
      )}

      {step === 3 && hasVideo() && (
        <div className="kr-card kr-form-panel animate-in" data-testid="video-style-step">
          <h2 className="font-display text-2xl mb-4">{stepNo(3)} — {t(lang,"book_step_3")}</h2>
          <button type="button" onClick={() => setVs({ trust_us: !vs.trust_us })} data-testid="vs-trust"
            className={`w-full text-left p-4 rounded-xl border-2 mb-5 transition-all ${vs.trust_us ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414] hover:border-[#D8301C]/40"}`}>
            <span className="font-display font-semibold flex items-center gap-2">
              {vs.trust_us && <CheckCircle2 className="w-4 h-4 text-[#D8301C] shrink-0"/>}
              {lang === "fr" ? "Je vous fais confiance, soyez créatifs" : "I trust you, be creative"}
            </span>
          </button>
          {!vs.trust_us && (
            <div className="space-y-5">
              {[["pace", lang === "fr" ? "Style de montage" : "Edit style", ["slow", "balanced", "fast"]],
                ["on_screen_text", lang === "fr" ? "Texte à l'écran" : "On-screen text", ["none", "some", "lots"]],
                ["music", lang === "fr" ? "Musique" : "Music", ["calm", "modern", "any"]]].map(([key, title, opts]) => (
                <div key={key}>
                  <div className="kr-caption mb-2">{title}</div>
                  <div className="grid sm:grid-cols-3 gap-2">
                    {opts.map(o => (
                      <button key={o} type="button" onClick={() => setVs({ [key]: o })} data-testid={`vs-${key}-${o}`}
                        className={`p-3 rounded-lg border text-sm text-center transition-all ${vs[key] === o ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414] hover:border-[#D8301C]/40"}`}>
                        <span className="flex items-center justify-center gap-1.5">{vs[key] === o && <CheckCircle2 className="w-3.5 h-3.5 text-[#D8301C]"/>}{vsLabel(key, o, lang)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-5">
            <div className="kr-caption mb-2">{lang === "fr" ? "Où allez-vous publier la vidéo ?" : "Where will you publish the video?"}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {["meta", "tiktok", "youtube", "web"].map(p => {
                const sel = (vs.platforms || []).includes(p);
                return (
                  <button key={p} type="button" onClick={() => togglePlatform(p)} data-testid={`vs-platform-${p}`}
                    className={`p-3 rounded-lg border text-sm text-center transition-all ${sel ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414] hover:border-[#D8301C]/40"}`}>
                    <span className="flex items-center justify-center gap-1.5">{sel && <CheckCircle2 className="w-3.5 h-3.5 text-[#D8301C]"/>}{vsLabel("platforms", p, lang)}</span>
                  </button>
                );
              })}
            </div>
            {(vs.platforms || []).some(p => ["tiktok", "youtube"].includes(p)) && (
              <div className="text-xs text-zinc-400 mt-2" data-testid="vs-license-note">
                {lang === "fr" ? "Nous utiliserons une musique sous licence pour éviter que la vidéo soit bloquée sur cette plateforme."
                  : "We will use licensed music so the video is not blocked on that platform."}
              </div>
            )}
          </div>
          {!vs.trust_us && (
            <div className="mt-5">
              <div className="kr-caption mb-2">{lang === "fr" ? "Voulez-vous parler devant la caméra ?" : "Do you want to speak on camera?"}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {[[true, lang === "fr" ? "Oui" : "Yes"], [false, lang === "fr" ? "Non" : "No"]].map(([val, label]) => (
                  <button key={String(val)} type="button" onClick={() => setVs({ on_camera: val })} data-testid={`vs-oncamera-${val ? "yes" : "no"}`}
                    className={`p-3 rounded-lg border text-sm text-center transition-all ${vs.on_camera === val ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414] hover:border-[#D8301C]/40"}`}>
                    <span className="flex items-center justify-center gap-1.5">{vs.on_camera === val && <CheckCircle2 className="w-3.5 h-3.5 text-[#D8301C]"/>}{label}</span>
                  </button>
                ))}
              </div>
              {vs.on_camera === true && (
                <div className="text-xs gold-text mt-2" data-testid="vs-oncamera-fee-note">
                  {presenterIncluded()
                    ? (lang === "fr" ? "Inclus avec le forfait choisi." : "Included with the selected package.")
                    : (lang === "fr" ? `Des frais s'appliquent : ${fmtCAD(cfg.on_camera_fee ?? 0)}, plus taxes.` : `Fees apply: ${fmtCAD(cfg.on_camera_fee ?? 0)}, plus taxes.`)}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between mt-6">
            <button data-testid="btn-back-video-style" onClick={() => setStep(2)} className="kr-btn kr-btn-ghost"><ArrowLeft className="w-4 h-4"/>{t(lang,"back")}</button>
            <button data-testid="btn-next-video-style" onClick={next} className="kr-btn kr-btn-primary">{t(lang,"continue")}<ArrowRight className="w-4 h-4"/></button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="kr-card kr-form-panel animate-in">
          <h2 className="font-display text-2xl mb-4">{stepNo(4)} — {t(lang,"book_step_4")}</h2>
          <label className="kr-caption">{t(lang,"pick_date")}</label>
          <div className="kr-date">
            <input data-testid="input-date" type="date" value={date} onChange={e => loadDate(e.target.value)} min={new Date().toISOString().slice(0,10)} />
            <CalendarIcon className="kr-date-icon" data-testid="input-date-icon" />
          </div>
          {weather && weather.available && (
            <div className="mt-5 p-4 rounded-lg bg-[#141414] border border-[rgba(216,48,28,0.35)]">
              <div className="kr-caption mb-2">{t(lang,"weather_forecast")}</div>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2"><Sun className="w-4 h-4 text-[#E6412D]"/>{fmtNum(weather.temp_min)}° / {fmtNum(weather.temp_max)}°</div>
                <div className="flex items-center gap-2"><CloudRain className="w-4 h-4 text-blue-400"/>{fmtNum(weather.precip_prob, 0)}% {t(lang,"rain_label")}</div>
                <div className="flex items-center gap-2"><Wind className="w-4 h-4 text-cyan-400"/>{fmtNum(weather.wind_max)} km/h</div>
                <div className="flex items-center gap-2 text-zinc-400"><Sun className="w-4 h-4"/>{t(lang,"sunset")}: {weather.sunset_time || weather.sunset?.split("T")[1]?.slice(0,5)}</div>
              </div>
              {weather.precip_prob >= 50 && <div className="mt-3 text-sm text-[#E6412D] flex items-center gap-2"><AlertTriangle className="w-4 h-4"/>{t(lang,"rain_warning")}</div>}
              {weather.wind_max >= 30 && form.service_codes.includes("drone") && <div className="mt-2 text-sm text-[#E6412D] flex items-center gap-2"><AlertTriangle className="w-4 h-4"/>{t(lang,"drone_warning")}</div>}
            </div>
          )}
          {date && (
            <div className="mt-6">
              <div className="kr-caption mb-3">{t(lang,"pick_slot")}</div>
              {loading ? <Loader2 className="w-5 h-5 animate-spin"/> : slots.length === 0 ? <div className="text-zinc-400">{t(lang,"no_slots")}</div> : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {slots.map(s => (
                    <button key={s.start} onClick={() => {
                      const sunset = weather?.sunset_time || weather?.sunset?.split("T")[1]?.slice(0,5);
                      const endTime = s.end_label || s.end?.slice(11,16);
                      if (sunset && endTime && endTime > sunset && (form.service_codes.includes("drone"))) toast.warning(t(lang,"sunset_warning"));
                      setForm({...form, start_at: s.start, team_member_id: s.members[0].id});
                    }} data-testid={`slot-${s.label || s.start.slice(11,16)}`}
                      className={`p-2 rounded-lg border text-sm ${form.start_at===s.start ? "border-[#D8301C] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] hover:border-[#D8301C]/50"}`}>
                      {s.label || s.start.slice(11,16)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between mt-6">
            <button data-testid="btn-back-3" onClick={() => setStep(hasVideo() ? 3 : 2)} className="kr-btn kr-btn-ghost"><ArrowLeft className="w-4 h-4"/>{t(lang,"back")}</button>
            <button data-testid="btn-next-3" onClick={next} className="kr-btn kr-btn-primary">{t(lang,"continue")}<ArrowRight className="w-4 h-4"/></button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="kr-card kr-form-panel animate-in">
          <h2 className="font-display text-2xl mb-4">{stepNo(5)} — {t(lang,"contact_info")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="kr-caption">{t(lang,"first_name")}</label><input data-testid="input-first-name" value={form.client_first_name} onChange={e => setForm({...form, client_first_name: e.target.value})}/></div>
            <div><label className="kr-caption">{t(lang,"last_name")}</label><input data-testid="input-last-name" value={form.client_last_name} onChange={e => setForm({...form, client_last_name: e.target.value})}/></div>
            <div><label className="kr-caption">{t(lang,"email")}</label><input data-testid="input-email" type="email" value={form.client_email} onChange={e => setForm({...form, client_email: e.target.value})}/></div>
            <div><label className="kr-caption">{t(lang,"phone")}</label><input data-testid="input-phone" value={form.client_phone} onChange={e => setForm({...form, client_phone: e.target.value})}/></div>
          </div>

          <div className="mt-6">
            <label className="kr-caption">{t(lang,"promo_label")}</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="input-promo" value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} placeholder="OCTOBRE300" disabled={!!promo}/>
              {promo ? (
                <button onClick={removePromo} className="kr-btn kr-btn-ghost whitespace-nowrap" data-testid="btn-remove-promo">{t(lang,"promo_remove")}</button>
              ) : (
                <button onClick={applyPromo} className="kr-btn kr-btn-primary whitespace-nowrap" data-testid="btn-apply-promo">{t(lang,"promo_apply")}</button>
              )}
            </div>
            {promoError && <div className="text-sm text-red-400 mt-2" data-testid="promo-error">{promoError}</div>}
            {promo && (
              <div className="text-sm gold-text mt-2" data-testid="promo-success">
                {t(lang,"promo_applied_prefix")} {promo.code} {t(lang,"promo_applied_mid")} {promo.discount.toFixed(2)} $
                {promo.capped_to_floor && <span className="text-[#E6412D]"> {t(lang,"promo_floor_note")}</span>}
              </div>
            )}
          </div>

          <div className="mt-6 p-4 rounded-lg bg-[#141414] border border-[rgba(216,48,28,0.35)]">
            <div className="kr-caption mb-3">{t(lang,"summary")}</div>
            <div className="text-sm space-y-1">
              {(dur?.lines || []).map(l => (
                <div key={l.code} className="flex justify-between" data-testid={`recap-line-${l.code}`}>
                  <span className="text-zinc-400">{lang === "en" && l.name_en ? l.name_en : l.name}{l.tier_label ? ` — ${lang === "en" && l.tier_label_en ? l.tier_label_en : l.tier_label}` : ""}{l.option_label ? ` · ${lang === "en" && l.option_label_en ? l.option_label_en : l.option_label}` : ""}</span>
                  <span>{fmtCAD(l.price, 2)}{l.included ? ` — ${lang === "fr" ? "Inclus avec le combo" : "Included with the combo"}` : ""}</span>
                </div>
              ))}
              <div className="flex justify-between"><span className="text-zinc-400">{t(lang,"travel_fee")}</span><span data-testid="summary-travel">{travel?.travel_fee > 0 ? fmtCAD(travel.travel_fee, 2) : t(lang,"travel_free")}</span></div>
              {hasVideo() && <div className="flex justify-between"><span className="text-zinc-400">{lang === "fr" ? "Format vidéo" : "Video format"}</span><span data-testid="summary-video-format">{form.video_format === "vertical" ? (lang === "fr" ? "Verticale" : "Vertical") : form.video_format === "horizontal" ? (lang === "fr" ? "Horizontale" : "Horizontal") : (lang === "fr" ? "Les deux formats" : "Both formats")}</span></div>}
              {bothFee > 0 && <div className="flex justify-between"><span className="text-zinc-400">{lang === "fr" ? "Supplément deux formats" : "Both-formats surcharge"}</span><span data-testid="summary-both-fee">{fmtCAD(bothFee, 2)}</span></div>}
              {cameraFee > 0 && <div className="flex justify-between"><span className="text-zinc-400">{lang === "fr" ? "Présentation devant la caméra" : "On-camera presentation"}</span><span data-testid="summary-camera-fee">{fmtCAD(cameraFee, 2)}</span></div>}
              {promo && <div className="flex justify-between gold-text" data-testid="summary-discount"><span>{t(lang,"promo_discount_label")} ({promo.code})</span><span>− {fmtCAD(promo.discount, 2)}</span></div>}
              {(() => { const sub = Math.round((servicesTotal() - (promo?.discount || 0) + (travel?.travel_fee || 0) + bothFee + cameraFee) * 100) / 100; const tx = taxes(sub); return (<>
                <div className="flex justify-between font-semibold pt-2 border-t border-[rgba(216,48,28,0.35)] mt-2"><span>{t(lang,"subtotal")}</span><span data-testid="summary-subtotal">{fmtCAD(sub, 2)}</span></div>
                <div className="flex justify-between text-zinc-400"><span>{t(lang,"gst")} ({((cfg.tax_gst_rate ?? 0.05) * 100).toLocaleString("fr-CA")} %)</span><span data-testid="summary-gst">{fmtCAD(tx.gst, 2)}</span></div>
                <div className="flex justify-between text-zinc-400"><span>{t(lang,"qst")} ({((cfg.tax_qst_rate ?? 0.09975) * 100).toLocaleString("fr-CA")} %)</span><span data-testid="summary-qst">{fmtCAD(tx.qst, 2)}</span></div>
                <div className="flex justify-between font-semibold text-base pt-2 border-t border-[rgba(216,48,28,0.35)] mt-2"><span>{t(lang,"total")}</span><span className="gold-text" data-testid="summary-total">{fmtCAD(tx.total, 2)}</span></div>
                {(cfg.gst_number || cfg.qst_number) && <div className="text-[11px] text-zinc-600" data-testid="summary-tax-numbers">{fill(t(lang,"tax_numbers"), { gst: cfg.gst_number || "—", qst: cfg.qst_number || "—" })}</div>}
              </>); })()}
              {hasVideo() && <div className="text-xs text-zinc-400 pt-2 border-t border-[rgba(216,48,28,0.35)] mt-2" data-testid="recap-revision-mention">{lang === "fr" ? `Une ronde de correction vidéo est incluse. Regroupez toutes vos demandes avant de les envoyer. Une ronde supplémentaire coûte ${fmtCAD(cfg.extra_revision_fee, 0)}, plus taxes.` : `One video revision round is included. Please group all your requests before sending. An extra round costs ${fmtCAD(cfg.extra_revision_fee, 0)}, plus taxes.`}</div>}
              <div className="text-xs text-zinc-500 pt-1" data-testid="summary-onsite">{fill(t(lang,"onsite_time"), { dur: fmtDur(totalDuration(), lang) })}</div>
              <div className="text-xs text-zinc-600">{t(lang,"onsite_note")}</div>
            </div>
          </div>
          <div className="flex justify-between mt-6">
            <button data-testid="btn-back-4" onClick={() => setStep(4)} className="kr-btn kr-btn-ghost"><ArrowLeft className="w-4 h-4"/>{t(lang,"back")}</button>
            <button data-testid="btn-confirm" onClick={next} disabled={loading} className="kr-btn kr-btn-primary">{loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>{t(lang,"confirm_booking")} <CheckCircle2 className="w-4 h-4"/></>}</button>
          </div>
        </div>
      )}

      {step === 6 && confirmed && (
        <div className="kr-card p-10 text-center animate-in" data-testid="booking-confirmed">
          <CheckCircle2 className="w-16 h-16 text-[#D8301C] mx-auto mb-4"/>
          <h2 className="font-display text-4xl mb-3">{t(lang,"booking_confirmed")}</h2>
          <p className="text-zinc-400 mb-6">{t(lang,"confirm_email_sent")} {confirmed.booking.client_email}.</p>
          <div className="text-sm text-zinc-500">{t(lang,"confirm_reference")}: <span className="font-mono gold-text">{confirmed.booking.id}</span></div>
          <Link to="/" className="kr-btn kr-btn-ghost mt-6 inline-flex" data-testid="back-home">{t(lang,"back_home")}</Link>
        </div>
      )}
    </div>
  );
}

// ===== Auth Callback =====
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const match = location.hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/connexion?auth=failed", { replace: true }); return; }
    const sid = match[1];
    api.post("/auth/session", { session_id: sid })
      .then(r => {
        let back = "";
        try { back = sessionStorage.getItem("kr_post_login") || ""; sessionStorage.removeItem("kr_post_login"); } catch { /* private mode */ }
        const dest = back || (r.data.user.role === "courtier" ? "/mon-espace" : "/seances");
        // Full navigation so the shell re-reads /auth/me and lands authenticated on the dashboard.
        window.location.replace(window.location.origin + dest);
      })
      .catch(() => { navigate("/connexion?auth=failed", { replace: true }); });
  }, [location.hash, navigate]);
  return <div className="p-20 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin inline"/> Connexion…</div>;
}

// ===== Admin Dashboard =====
function AdminDashboard({ lang }) {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [team, setTeam] = useState([]);
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [promos, setPromos] = useState([]);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showAddPromo, setShowAddPromo] = useState(false);
  const [uploadsLog, setUploadsLog] = useState([]);
  const [smsLog, setSmsLog] = useState([]);
  const [smsTestPhone, setSmsTestPhone] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  const confirmAsk = (opts) => setConfirmState({ danger: true, ...opts });

  const refresh = useCallback(async () => {
    const [b, tm, s, sv] = await Promise.all([
      api.get("/bookings"), api.get("/team"), api.get("/settings/admin"), api.get("/services"),
    ]);
    setBookings(b.data); setTeam(tm.data); setSettings(s.data); setServices(sv.data);
    try { const p = await api.get("/promo-codes"); setPromos(p.data); } catch {}
    try { const u = await api.get("/admin/uploads-log"); setUploadsLog(u.data); } catch {}
    try { const sl = await api.get("/admin/sms/log"); setSmsLog(sl.data); } catch {}
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const saveSettings = async () => { await api.put("/settings", settings); toast.success("Paramètres enregistrés"); };
  const setSms = (patch) => setSettings(s => ({ ...s, sms: { ...(s.sms || {}), ...patch } }));
  const setSmsTpl = (key, val) => setSettings(s => ({ ...s, sms: { ...(s.sms || {}), templates: { ...((s.sms || {}).templates || {}), [key]: val } } }));
  const sendSmsTest = async () => {
    if (!smsTestPhone) { toast.error("Entrez un numéro"); return; }
    try {
      const r = await api.post("/admin/sms/test", { phone: smsTestPhone });
      toast.success(`Test envoyé — statut ${r.data.status}`);
      const sl = await api.get("/admin/sms/log"); setSmsLog(sl.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const addTeamMember = async (data) => {
    await api.post("/team", data); toast.success("Membre ajouté"); setShowAddTeam(false); refresh();
  };
  const deleteTeamMember = (id) => confirmAsk({ message: "Retirer ce membre ? Son compte redeviendra courtier.", confirmLabel: lang === "fr" ? "Retirer" : "Remove", onConfirm: async () => { try { await api.delete(`/team/${id}`); toast.success("Membre retiré"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || t(lang, "err_generic")); } } });
  const saveMemberField = async (id, patch) => {
    await api.put(`/team/${id}`, patch); toast.success("Nom affiché mis à jour"); refresh();
  };
  const saveTierField = async (svcId, tierId, field, value) => {
    const svc = services.find(x => x.id === svcId); if (!svc) return;
    const newTiers = svc.tiers.map(t => t.id === tierId ? {...t, [field]: value} : t);
    await api.put(`/services/${svcId}`, { tiers: newTiers });
    toast.success(field === "price" ? "Prix enregistré" : "Prix plancher enregistré"); refresh();
  };
  const saveTierFloor = (svcId, tierId, newFloor) => saveTierField(svcId, tierId, "min_price_after_discount", parseFloat(newFloor) || 0);
  const setRecommended = async (svcId, field, id) => {
    const svc = services.find(x => x.id === svcId); if (!svc) return;
    await api.put(`/services/${svcId}`, { [field]: (svc[field] || []).map(x => ({...x, recommended: x.id === id})) });
    toast.success("Recommandation enregistrée"); refresh();
  };
  const saveOptionExtra = async (svcId, optId, value) => {
    const svc = services.find(x => x.id === svcId); if (!svc) return;
    await api.put(`/services/${svcId}`, { options: (svc.options || []).map(o => o.id === optId ? {...o, extra: value} : o) });
    toast.success("Supplément enregistré"); refresh();
  };
  const addPromo = async (data) => {
    try { await api.post("/promo-codes", data); toast.success("Code créé"); setShowAddPromo(false); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const togglePromo = async (code, active) => {
    await api.put(`/promo-codes/${code}`, { active }); refresh();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10" data-testid="admin-dashboard">
      <div className="kr-caption mb-2">Administration</div>
      <h1 className="font-display text-4xl mb-8">Tableau de bord</h1>
      <div className="flex gap-2 mb-6 border-b border-[rgba(216,48,28,0.35)] overflow-x-auto scrollbar-thin">
        {[["bookings","Réservations"],["team","Équipe"],["promos","Codes promo"],["floors","Grille de prix"],["uploads","Journal"],["notify","Notifications"],["settings","Paramètres"]].map(([k,label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 font-display text-sm border-b-2 transition whitespace-nowrap ${tab===k ? "border-[#D8301C] text-[#D8301C]" : "border-transparent text-zinc-400"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "bookings" && (
        <div className="kr-card p-6" data-testid="bookings-list">
          <h2 className="font-display text-3xl mb-6">Toutes les réservations ({bookings.length})</h2>
          <div className="space-y-2">
            {bookings.map(b => (
              <div key={b.id} className="p-4 bg-[#141414] rounded-lg border border-[rgba(216,48,28,0.35)] flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-semibold">{b.client_name} — {b.property_address}</div>
                  <div className="text-sm text-zinc-400">{new Date(b.start_at).toLocaleString("fr-CA")} · {b.service_codes.join(", ")}</div>
                  {b.video_format && <div className="text-xs text-zinc-400 mt-1" data-testid={`booking-video-format-${b.id}`}>Format vidéo : {vfLabel(b.video_format, "fr")}</div>}
                  {b.video_style && <VideoStyleBlock style={b.video_style} lang="fr" id={`adm-${b.id}`}/>}
                  {b.promo_code && <div className="text-xs gold-text mt-1">Promo {b.promo_code} · −{b.promo_discount?.toFixed(2)} $</div>}
                </div>
                <div className="text-right">
                  <div className="gold-text font-semibold">{b.subtotal} $</div>
                  <Link to={`/galerie/${b.gallery_id || b.id}`} className="text-xs text-[#D8301C] hover:underline" data-testid={`project-link-${b.id}`}>Projet / galerie →</Link>
                </div>
              </div>
            ))}
            {!bookings.length && <div className="text-zinc-400">Aucune réservation.</div>}
          </div>
        </div>
      )}

      {tab === "team" && (
        <div className="kr-card p-6">
          <div className="flex justify-between mb-4">
            <h2 className="font-display text-2xl">Équipe ({team.length})</h2>
            <button onClick={() => setShowAddTeam(true)} className="kr-btn kr-btn-primary text-sm" data-testid="btn-add-team"><Users className="w-4 h-4"/>Ajouter</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {team.map(m => (
              <div key={m.id} className="p-4 bg-[#141414] rounded-lg border border-[rgba(216,48,28,0.35)]" data-testid={`team-member-${m.id}`}>
                <div className="flex justify-between gap-3">
                  <div>
                    <div className="font-semibold">{m.display_name || m.name} <span className={`ml-1 text-[10px] px-2 py-0.5 rounded-full ${m.role === "admin" ? "bg-[#D8301C]/20 text-[#D8301C]" : "bg-zinc-800 text-zinc-300"}`}>{m.role === "admin" ? "Admin" : "Équipe"}</span></div>
                    <div className="text-xs text-zinc-400">{m.email}</div>
                    {m.phone && <div className="text-xs text-zinc-400" data-testid={`team-phone-${m.id}`}>{fmtPhone(m.phone)}</div>}
                    <div className="text-xs gold-text mt-1">{(m.skills || []).join(" · ")}</div>
                  </div>
                  <button onClick={() => deleteTeamMember(m.id)} className="text-xs text-red-400 self-start hover:underline" data-testid={`remove-team-${m.id}`}>Retirer</button>
                </div>
                <div className="mt-3">
                  <label className="kr-caption">Nom affiché (remplace le nom Google partout)</label>
                  <input defaultValue={m.display_name || m.name || ""} onBlur={e => { if (e.target.value !== (m.display_name || m.name || "")) saveMemberField(m.id, {display_name: e.target.value}); }} data-testid={`team-displayname-${m.id}`}/>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-4">Seuls les courriels de cette liste ont accès à l'espace équipe. Tout autre compte connecté est un courtier.</p>
          {showAddTeam && <AddTeamModal onClose={() => setShowAddTeam(false)} onSave={addTeamMember}/>}
        </div>
      )}

      {tab === "promos" && (
        <div className="kr-card p-6" data-testid="promos-tab">
          <div className="flex justify-between mb-4">
            <h2 className="font-display text-2xl">Codes promo ({promos.length})</h2>
            <button onClick={() => setShowAddPromo(true)} className="kr-btn kr-btn-primary text-sm" data-testid="btn-add-promo">+ Nouveau code</button>
          </div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-[rgba(216,48,28,0.35)]">
                  <th className="p-2">Code</th><th className="p-2">Rabais</th><th className="p-2">Validité</th>
                  <th className="p-2">Utilisations</th><th className="p-2">Rabais accordés</th>
                  <th className="p-2">Ventes générées</th><th className="p-2">Statut</th><th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {promos.map(p => (
                  <tr key={p.code} className="border-b border-[#141414]" data-testid={`promo-row-${p.code}`}>
                    <td className="p-2 font-mono gold-text">{p.code}</td>
                    <td className="p-2">{p.type === "percent" ? `${p.value}%` : `${p.value} $`}</td>
                    <td className="p-2 text-xs text-zinc-400">{(p.start_date || "—").slice(0,10)} → {(p.end_date || "—").slice(0,10)}</td>
                    <td className="p-2">{p.uses_count || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                    <td className="p-2">{(p.total_discount || 0).toFixed(2)} $</td>
                    <td className="p-2">{(p.total_sales || 0).toFixed(2)} $</td>
                    <td className="p-2">
                      <button onClick={() => togglePromo(p.code, !p.active)} className={`text-xs px-2 py-1 rounded ${p.active ? "bg-emerald-900/40 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`} data-testid={`toggle-promo-${p.code}`}>
                        {p.active ? "Actif" : "Inactif"}
                      </button>
                    </td>
                    <td className="p-2 text-xs text-zinc-500">
                      {p.min_amount > 0 && <div>Min: {p.min_amount} $</div>}
                    </td>
                  </tr>
                ))}
                {!promos.length && <tr><td colSpan="8" className="text-zinc-400 p-6 text-center">Aucun code promo.</td></tr>}
              </tbody>
            </table>
          </div>
          {showAddPromo && <AddPromoModal services={services} onClose={() => setShowAddPromo(false)} onSave={addPromo}/>}
          <p className="text-xs text-zinc-500 mt-4">Lien publicitaire : partagez <span className="font-mono text-zinc-400">?promo=CODE</span> en URL pour appliquer automatiquement le rabais. UTM (source/medium/campaign/content) sont capturés et transmis au CRM.</p>
        </div>
      )}

      {tab === "floors" && (
        <div className="kr-card p-6" data-testid="floors-tab">
          <h2 className="font-display text-2xl mb-2">Grille de prix et prix planchers</h2>
          <p className="text-sm text-zinc-400 mb-5">Source unique des prix : accueil, formulaire, FAQ, courriels et données SEO. Un code promo ne pourra jamais faire descendre le prix sous le plancher ; si c'est le cas, le rabais est automatiquement réduit.</p>
          <div className="space-y-5">
            {services.map(s => (
              <div key={s.id} className="p-4 bg-[#141414] rounded-lg border border-[rgba(216,48,28,0.35)]">
                <div className="font-display font-semibold mb-3">{s.name}</div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {(s.tiers || []).map(ti => (
                    <div key={ti.id} className="p-3 rounded-lg border border-[rgba(216,48,28,0.35)]">
                      <div className="text-sm font-medium mb-1 flex justify-between gap-2">{ti.label}
                        <label className="text-[10px] uppercase tracking-wide text-[#D8301C] flex items-center gap-1 cursor-pointer"><input type="radio" name={`rec-${s.code}`} checked={!!ti.recommended} onChange={() => setRecommended(s.id, "tiers", ti.id)} style={{ width: "0.8rem", height: "0.8rem", padding: 0 }} data-testid={`rec-radio-${ti.id}`}/>Recommandé</label>
                      </div>
                      <div className="text-xs text-zinc-500 mb-2">Prix ($)</div>
                      <input type="number" step="1" defaultValue={ti.price} className="mb-2"
                        onBlur={e => { const v = parseFloat(e.target.value) || 0; if (v !== ti.price) saveTierField(s.id, ti.id, "price", v); }}
                        data-testid={`price-${ti.id}`}/>
                      <label className="kr-caption">Plancher ($)</label>
                      <input type="number" defaultValue={ti.min_price_after_discount || 0}
                        onBlur={e => { const v = parseFloat(e.target.value) || 0; if (v !== (ti.min_price_after_discount || 0)) saveTierFloor(s.id, ti.id, v); }}
                        data-testid={`floor-${ti.id}`}/>
                    </div>
                  ))}
                </div>
                {(s.options || []).length > 0 && (
                  <div className="mt-3">
                    <div className="kr-caption mb-2">Suppléments photos du combo ($)</div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {s.options.map(o => (
                        <div key={o.id}><div className="text-xs text-zinc-400 mb-1 flex justify-between gap-2">{o.label}
                            <label className="text-[10px] uppercase tracking-wide text-[#D8301C] flex items-center gap-1 cursor-pointer"><input type="radio" name={`rec-opt-${s.code}`} checked={!!o.recommended} onChange={() => setRecommended(s.id, "options", o.id)} style={{ width: "0.8rem", height: "0.8rem", padding: 0 }} data-testid={`rec-radio-${o.id}`}/>Recommandé</label></div>
                          <input type="number" step="1" defaultValue={o.extra} onBlur={e => { const v = parseFloat(e.target.value) || 0; if (v !== o.extra) saveOptionExtra(s.id, o.id, v); }} data-testid={`option-extra-${o.id}`}/></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "uploads" && (
        <div className="kr-card p-6" data-testid="uploads-tab">
          <h2 className="font-display text-3xl mb-6">Journal des téléversements ({uploadsLog.length})</h2>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-[rgba(216,48,28,0.35)]">
                  <th className="p-2">Date</th><th className="p-2">Par</th><th className="p-2">Projet</th>
                  <th className="p-2">Livrable</th><th className="p-2">Fichier</th><th className="p-2">Type</th>
                </tr>
              </thead>
              <tbody>
                {uploadsLog.map((u, i) => (
                  <tr key={i} className="border-b border-[#141414]" data-testid={`upload-row-${i}`}>
                    <td className="p-2 text-xs text-zinc-400">{u.uploaded_at ? new Date(u.uploaded_at).toLocaleString("fr-CA") : "—"}</td>
                    <td className="p-2">{u.uploaded_by_name || "—"}</td>
                    <td className="p-2 text-xs">{u.property_address} · {u.client_name}</td>
                    <td className="p-2">{u.deliverable}</td>
                    <td className="p-2 text-xs text-zinc-400">{u.filename}</td>
                    <td className="p-2">{u.kind}</td>
                  </tr>
                ))}
                {!uploadsLog.length && <tr><td colSpan="6" className="text-zinc-400 p-6 text-center">Aucun téléversement.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "notify" && (
        <div className="kr-card p-6" data-testid="notify-tab">
          <h2 className="font-display text-2xl mb-2">Notifications internes</h2>
          <p className="text-sm text-zinc-400 mb-5">Tous les courriels internes (nouvelle réservation, annulation, paiement reçu, demande de correction, problème signalé) sont envoyés à ces adresses. Les courtiers reçoivent toujours leurs propres confirmations et le membre assigné son texto.</p>
          <div className="space-y-2">
            {(settings.internal_notify_emails || []).map((em, i) => (
              <div key={i} className="flex gap-2">
                <input type="email" value={em} placeholder="adresse@kreatif.ca"
                  onChange={e => { const arr = [...settings.internal_notify_emails]; arr[i] = e.target.value; setSettings({...settings, internal_notify_emails: arr}); }}
                  data-testid={`notify-email-${i}`}/>
                <button onClick={() => setSettings({...settings, internal_notify_emails: settings.internal_notify_emails.filter((_, j) => j !== i)})} className="kr-btn kr-btn-ghost text-sm whitespace-nowrap" data-testid={`notify-remove-${i}`}>Retirer</button>
              </div>
            ))}
          </div>
          <button onClick={() => setSettings({...settings, internal_notify_emails: [...(settings.internal_notify_emails || []), ""]})} className="kr-btn kr-btn-ghost text-sm mt-3" data-testid="notify-add">+ Ajouter une adresse</button>
          <div><button onClick={saveSettings} className="kr-btn kr-btn-primary mt-5" data-testid="btn-save-notify">Enregistrer</button></div>
        </div>
      )}

      {tab === "settings" && (
        <div className="kr-card p-6" data-testid="settings-form">
          <h2 className="font-display text-2xl mb-6">Paramètres globaux</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 flex items-center justify-between kr-card p-4" data-testid="watermark-toggle-row">
              <div>
                <label className="kr-caption block">Filigrane sur les aperçus</label>
                <p className="text-xs text-zinc-500 mt-1">Incruste le logo Kréatif sur les aperçus non payés. Désactivé par défaut. La vidéo n'est jamais filigranée.</p>
              </div>
              <button
                type="button"
                onClick={() => setSettings({ ...settings, watermark_enabled: !settings.watermark_enabled })}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${settings.watermark_enabled ? "bg-[var(--kr-accent,#D8301C)]" : "bg-zinc-600"}`}
                data-testid="watermark-enabled-toggle"
                aria-pressed={!!settings.watermark_enabled}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${settings.watermark_enabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            <div><label className="kr-caption">Km inclus (aller simple)</label><input type="number" value={settings.included_km_oneway || 30} onChange={e => setSettings({...settings, included_km_oneway: parseInt(e.target.value)})}/></div>
            <div><label className="kr-caption">Prix / km extra ($)</label><input type="number" step="0.1" value={settings.price_per_extra_km || 1} onChange={e => setSettings({...settings, price_per_extra_km: parseFloat(e.target.value)})}/></div>
            <div><label className="kr-caption">Marge après photo (min)</label><input type="number" value={settings.margin_after_photo_min || 15} onChange={e => setSettings({...settings, margin_after_photo_min: parseInt(e.target.value)})}/></div>
            <div><label className="kr-caption">Marge après vidéo (min)</label><input type="number" value={settings.margin_after_video_min || 30} onChange={e => setSettings({...settings, margin_after_video_min: parseInt(e.target.value)})}/></div>
            <div><label className="kr-caption">Révisions incluses</label><input type="number" value={settings.included_revisions || 1} onChange={e => setSettings({...settings, included_revisions: parseInt(e.target.value)})}/></div>
            <div><label className="kr-caption">Ronde supplémentaire ($)</label><input type="number" value={settings.extra_revision_fee ?? ""} onChange={e => setSettings({...settings, extra_revision_fee: parseFloat(e.target.value)})} data-testid="input-extra-revision-fee"/></div>
            <div><label className="kr-caption">Approbation auto de la vidéo (jours)</label><input type="number" value={settings.revision_window_days ?? 5} onChange={e => setSettings({...settings, revision_window_days: parseInt(e.target.value)})} data-testid="input-revision-window"/><div className="text-[11px] text-zinc-500 mt-1">Délai de la ronde incluse : passé ce délai, la vidéo est approuvée automatiquement (rappel 24 h avant).</div></div>
            <div><label className="kr-caption">Photo excédentaire ($)</label><input type="number" step="0.5" value={settings.extra_photo_price ?? 2} onChange={e => setSettings({...settings, extra_photo_price: parseFloat(e.target.value)})} data-testid="input-extra-photo-price"/></div>
            <div><label className="kr-caption">Version verticale ajoutée ($)</label><input type="number" step="1" value={settings.vertical_add_fee ?? 0} onChange={e => setSettings({...settings, vertical_add_fee: parseFloat(e.target.value) || 0})} data-testid="input-vertical-add-fee"/></div>
            <div><label className="kr-caption">Présentation devant la caméra ($)</label><input type="number" step="1" value={settings.on_camera_fee ?? 0} onChange={e => setSettings({...settings, on_camera_fee: parseFloat(e.target.value) || 0})} data-testid="input-on-camera-fee"/></div>
            {[["with_photo_session", "Photos drone avec séance photo ($)"], ["alone", "Photos drone seules ($)"], ["ext_photos", "Photos extérieures + drone ($)"], ["video", "Vidéo drone ($)"], ["photo_video", "Photos + vidéo drone ($)"]].map(([k, label]) => (
              <div key={k}><label className="kr-caption">{label}</label>
                <input type="number" step="1" value={settings.drone_prices?.[k] ?? ""} data-testid={`input-drone-${k}`}
                  onChange={e => setSettings({...settings, drone_prices: {...(settings.drone_prices || {}), [k]: parseFloat(e.target.value) || 0}})}/></div>
            ))}
            <div><label className="kr-caption">Taux TPS (%)</label><input type="number" step="0.001" value={Math.round((settings.tax_gst_rate ?? 0.05) * 100000) / 1000} onChange={e => setSettings({...settings, tax_gst_rate: (parseFloat(e.target.value) || 0) / 100})} data-testid="input-gst-rate"/></div>
            <div><label className="kr-caption">Taux TVQ (%)</label><input type="number" step="0.001" value={Math.round((settings.tax_qst_rate ?? 0.09975) * 100000) / 1000} onChange={e => setSettings({...settings, tax_qst_rate: (parseFloat(e.target.value) || 0) / 100})} data-testid="input-qst-rate"/></div>
            <div><label className="kr-caption">N° d'inscription TPS</label><input value={settings.gst_number || ""} onChange={e => setSettings({...settings, gst_number: e.target.value})} placeholder="123456789 RT0001" data-testid="input-gst-number"/></div>
            <div><label className="kr-caption">N° d'inscription TVQ</label><input value={settings.qst_number || ""} onChange={e => setSettings({...settings, qst_number: e.target.value})} placeholder="1234567890 TQ0001" data-testid="input-qst-number"/></div>
          </div>
          <h3 className="font-display text-xl mt-8 mb-1" data-testid="onsite-durations-title">Durées sur place</h3>
          <p className="text-xs text-zinc-500 mb-4">Source unique pour l'affichage client et le temps bloqué dans l'agenda (durée sur place + trajet + marge). Arrondi au quart d'heure.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              ["photo_30", "Photos — forfait 30 photos (min)"], ["photo_40_plus", "Photos — 40 photos et plus (min)"],
              ["video_small", "Vidéo — sous le seuil (min)"], ["video_large", "Vidéo — seuil et plus (min)"],
              ["video_sqft_threshold", "Seuil de superficie vidéo (pi²)"], ["measures", "Prise de mesures (min)"],
              ["drone_with_video", "Drone avec vidéo (min)"], ["drone_photos_only", "Drone photos seulement (min)"],
            ].map(([k, label]) => (
              <div key={k}><label className="kr-caption">{label}</label>
                <input type="number" value={settings.onsite_durations?.[k] ?? ""} data-testid={`onsite-${k}`}
                  onChange={e => setSettings({...settings, onsite_durations: {...(settings.onsite_durations || {}), [k]: parseInt(e.target.value) || 0}})}/></div>
            ))}
          </div>

          <QuickBooksSettings settings={settings} setSettings={setSettings}/>
          <GoogleCalendarSettings team={team}/>

          <h3 className="font-display text-xl mt-8 mb-1">Courriels : expéditeur et objets</h3>          <p className="text-xs text-zinc-500 mb-4">Variables : {"{date}"}, {"{heure}"}, {"{adresse}"}, {"{livrable}"}, {"{titre}"}, {"{type}"}.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className="kr-caption">Nom d'expéditeur</label><input value={settings.email_from_name ?? ""} onChange={e => setSettings({...settings, email_from_name: e.target.value})} data-testid="email-from-name"/></div>
            {[["booking_confirmed_fr", "Confirmation client (FR)"], ["booking_confirmed_en", "Confirmation client (EN)"], ["deliverable_ready_fr", "Livrable prêt (FR)"], ["deliverable_ready_en", "Livrable prêt (EN)"], ["internal_new_booking", "Interne — nouvelle réservation"], ["internal_cancelled", "Interne — annulation"], ["internal_not_ready", "Interne — propriété non prête"], ["internal_revision", "Interne — commentaires vidéo"], ["internal_approved", "Interne — vidéo approuvée"], ["internal_correction", "Interne — demande de correction"], ["internal_payment", "Interne — paiement reçu"]].map(([k, label]) => (
              <div key={k}><label className="kr-caption">{label}</label>
                <input value={settings.email_subjects?.[k] ?? ""} data-testid={`subject-${k}`}
                  onChange={e => setSettings({...settings, email_subjects: {...(settings.email_subjects || {}), [k]: e.target.value}})}/></div>
            ))}
          </div>

          <h3 className="font-display text-xl mt-8 mb-1">Conseil dans le courriel de confirmation</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {["fr", "en"].map(l => (
              <div key={l}><label className="kr-caption">Texte {l.toUpperCase()}</label>
                <textarea rows="3" value={settings.email_tips?.[l] ?? ""} data-testid={`email-tip-${l}`}
                  onChange={e => setSettings({...settings, email_tips: {...(settings.email_tips || {}), [l]: e.target.value}})}/></div>
            ))}
          </div>

          <h3 className="font-display text-xl mt-8 mb-1">Délais</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="kr-caption">Téléphone public (annulations, limites)</label>
              <input value={settings.phone ?? ""} data-testid="input-contact-phone"
                onChange={e => setSettings({...settings, phone: e.target.value})}/></div>
            <div><label className="kr-caption">Courriel public</label>
              <input value={settings.contact_email ?? ""} data-testid="input-contact-email"
                onChange={e => setSettings({...settings, contact_email: e.target.value})}/></div>
            <div><label className="kr-caption">Domaines de retour autorisés (Stripe), séparés par une virgule</label>
              <input value={(settings.allowed_return_domains || []).join(", ")} data-testid="input-return-domains"
                onChange={e => setSettings({...settings, allowed_return_domains: e.target.value.split(",").map(x => x.trim()).filter(Boolean)})}/></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <div><label className="kr-caption">Délai minimum avant une séance (heures)</label>
              <input type="number" value={settings.min_lead_hours ?? 12} data-testid="min-lead-hours"
                onChange={e => setSettings({...settings, min_lead_hours: parseInt(e.target.value) || 0})}/></div>
            <div><label className="kr-caption">Délai de livraison des photos (FR)</label>
              <input value={settings.deliverable_eta?.photos ?? ""} data-testid="eta-photos-fr"
                onChange={e => setSettings({...settings, deliverable_eta: {...(settings.deliverable_eta || {}), photos: e.target.value}})}/></div>
            <div><label className="kr-caption">Délai de livraison des photos (EN)</label>
              <input value={settings.deliverable_eta?.photos_en ?? ""} data-testid="eta-photos-en"
                onChange={e => setSettings({...settings, deliverable_eta: {...(settings.deliverable_eta || {}), photos_en: e.target.value}})}/></div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Ce délai s'affiche partout : FAQ de la page d'accueil et galeries clients.</p>

          <h3 className="font-display text-xl mt-8 mb-1">Texto de rappel</h3>
          <p className="text-xs text-zinc-500 mb-4">Le délai déclenche l'envoi du texto « booking.reminder ». Le contenu se modifie dans la section Textos ci-dessous.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="kr-caption">Délai avant la séance (heures)</label>
              <input type="number" value={settings.reminder?.hours_before ?? 48} data-testid="reminder-hours"
                onChange={e => setSettings({...settings, reminder: {...(settings.reminder || {}), hours_before: parseInt(e.target.value) || 0}})}/></div>
          </div>

          <h3 className="font-display text-xl mt-8 mb-1">Textes vidéo (formulaire et FAQ)</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["evening", "Images de soir"], ["ai", "Plan IA inclus"], ["presenter", "Présentation du courtier"], ["custom", "Prix sur mesure"]].flatMap(([k, label]) => ["fr", "en"].map(l => (
              <div key={`${k}_${l}`}><label className="kr-caption">{label} ({l.toUpperCase()})</label>
                <textarea rows="2" value={settings.video_texts?.[`${k}_${l}`] ?? ""} data-testid={`video-text-${k}-${l}`}
                  onChange={e => setSettings({...settings, video_texts: {...(settings.video_texts || {}), [`${k}_${l}`]: e.target.value}})}/></div>
            )))}
          </div>

          <h3 className="font-display text-xl mt-8 mb-1">Ligne de preuves (page d'accueil)</h3>
          <p className="text-xs text-zinc-500 mb-4">Trois blocs : élément fort (orange) et texte gris dessous.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {["fr", "en"].map(l => (
              <div key={l} className="space-y-3">
                <div className="kr-caption">{l.toUpperCase()}</div>
                {[["since", "Bloc 1 — fort (ex. Depuis 2011)"], ["since_rest", "Bloc 1 — gris"], ["amount", "Bloc 2 — fort"], ["amount_rest", "Bloc 2 — gris"], ["rating", "Bloc 3 — fort (ex. 4,9 ★)"], ["google", "Bloc 3 — gris"]].map(([k, label]) => (
                  <input key={k} placeholder={label} value={settings.proof_line?.[l]?.[k] ?? ""} data-testid={`proof-${l}-${k}`}
                    onChange={e => setSettings({...settings, proof_line: {...(settings.proof_line || {}), [l]: {...(settings.proof_line?.[l] || {}), [k]: e.target.value}}})}/>
                ))}
              </div>
            ))}
            <div className="sm:col-span-2"><label className="kr-caption">Lien de la fiche Google</label>
              <input value={settings.proof_line?.google_url ?? ""} placeholder="https://g.page/..." data-testid="proof-google-url"
                onChange={e => setSettings({...settings, proof_line: {...(settings.proof_line || {}), google_url: e.target.value}})}/></div>
          </div>
          <h3 className="font-display text-xl mt-8 mb-1">Textos (SMS via CRM)</h3>
          <p className="text-xs text-zinc-500 mb-4">Tous les textos partent en POST JSON vers l'adresse du webhook du CRM. Si elle est vide, aucun envoi. Variables : {"{prenom}"} {"{client}"} {"{date}"} {"{adresse}"} {"{services}"} {"{options}"} {"{duree}"} {"{acces}"} {"{lien}"} {"{solde}"}. Toute ligne dont la variable est vide est retirée. Les textos à l'équipe sont toujours en français.</p>
          <div className="mb-4"><label className="kr-caption">Adresse publique de l'app</label><input value={settings.public_app_url ?? ""} onChange={e => setSettings({...settings, public_app_url: e.target.value})} placeholder="https://app.kreatif.ca" data-testid="public-app-url"/><p className="text-xs text-zinc-500 mt-1">Tous les liens des textos et courriels sont construits à partir de cette adresse. Laissez vide pour utiliser l'adresse courante.</p></div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div><label className="kr-caption">Adresse du webhook du CRM</label><input value={settings.sms?.crm_webhook_url ?? ""} onChange={e => setSms({crm_webhook_url: e.target.value})} placeholder="https://..." data-testid="sms-webhook-url"/></div>
            <div><label className="kr-caption">Numéro d'équipe (repli si aucun membre assigné)</label><input value={settings.sms?.team_phone ?? ""} onChange={e => setSms({team_phone: e.target.value})} placeholder="+15143609520" data-testid="sms-team-phone"/></div>
          </div>
          {[["booking_confirmed","Confirmation (client)",true],["booking_reminder","Rappel (client)",true],["booking_updated","Modification (client)",true],["booking_cancelled","Annulation (client)",true],["delivery_ready","Livraison prête (client)",true],["team_new_booking","Nouveau RDV (équipe, FR)",false]].map(([key,label,hasEn]) => (
            <div key={key} className="grid sm:grid-cols-2 gap-4 mb-4">
              <div><label className="kr-caption">{label} — FR</label>
                <textarea rows="5" value={settings.sms?.templates?.[key+"_fr"] ?? ""} onChange={e => setSmsTpl(key+"_fr", e.target.value)} data-testid={`sms-tpl-${key}-fr`}/></div>
              {hasEn && <div><label className="kr-caption">{label} — EN</label>
                <textarea rows="5" value={settings.sms?.templates?.[key+"_en"] ?? ""} onChange={e => setSmsTpl(key+"_en", e.target.value)} data-testid={`sms-tpl-${key}-en`}/></div>}
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-3 mt-2">
            <div className="flex-1 min-w-[12rem] max-w-xs"><label className="kr-caption">Envoyer un texto de test à</label><input value={smsTestPhone} onChange={e => setSmsTestPhone(e.target.value)} placeholder="+15141234567" data-testid="sms-test-phone"/></div>
            <button onClick={sendSmsTest} className="kr-btn kr-btn-ghost" data-testid="sms-test-btn">Envoyer un texto de test</button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Enregistrez d'abord l'adresse du webhook avant d'envoyer un test.</p>
          <h4 className="font-display text-lg mt-6 mb-2">Journal des 50 derniers envois</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sms-log-table">
              <thead><tr className="text-left text-zinc-400"><th className="p-2">Date</th><th className="p-2">Événement</th><th className="p-2">Numéro</th><th className="p-2">Réponse CRM</th></tr></thead>
              <tbody>
                {(smsLog || []).map((l, i) => (
                  <tr key={i} className="border-t border-[rgba(216,48,28,0.35)]"><td className="p-2 whitespace-nowrap">{(l.at || "").slice(0,16).replace("T"," ")}</td><td className="p-2">{l.event}</td><td className="p-2">{l.phone}</td><td className="p-2">{String(l.status)}{l.response ? ` — ${String(l.response).slice(0,40)}` : ""}</td></tr>
                ))}
                {!(smsLog || []).length && <tr><td colSpan="4" className="text-zinc-400 p-4 text-center">Aucun envoi.</td></tr>}
              </tbody>
            </table>
          </div>
          <button onClick={saveSettings} className="kr-btn kr-btn-primary mt-6" data-testid="btn-save-settings">Enregistrer</button>
        </div>
      )}
      {confirmState && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="admin-confirm-modal" onClick={() => setConfirmState(null)}>
          <div className="kr-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="text-sm whitespace-pre-line mb-4">{confirmState.message}</div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmState(null)} className="kr-btn kr-btn-ghost text-sm" data-testid="admin-confirm-cancel">{lang === "fr" ? "Annuler" : "Cancel"}</button>
              <button onClick={() => { const cs = confirmState; setConfirmState(null); if (cs.onConfirm) cs.onConfirm(); }} className="kr-btn kr-btn-primary text-sm" data-testid="admin-confirm-ok">{confirmState.confirmLabel || (lang === "fr" ? "Confirmer" : "Confirm")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddPromoModal({ services, onClose, onSave }) {
  const [f, setF] = useState({
    code: "", type: "fixed", value: 0, start_date: "", end_date: "",
    max_uses: "", min_amount: 0, eligible_services: [], eligible_tiers: [], active: true,
  });
  const toggleArr = (field, v) => setF(x => ({...x, [field]: x[field].includes(v) ? x[field].filter(k => k!==v) : [...x[field], v]}));
  return createPortal((
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="kr-card p-6 max-w-lg w-full my-10 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="font-display text-2xl mb-4">Nouveau code promo</h3>
        <div className="space-y-3">
          <div><label className="kr-caption">Code (ex: OCTOBRE300)</label><input value={f.code} onChange={e => setF({...f, code: e.target.value.toUpperCase()})} data-testid="promo-field-code"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="kr-caption">Type</label>
              <select value={f.type} onChange={e => setF({...f, type: e.target.value})} data-testid="promo-field-type">
                <option value="fixed">Montant fixe ($)</option><option value="percent">Pourcentage (%)</option>
              </select>
            </div>
            <div><label className="kr-caption">Valeur</label><input type="number" value={f.value} onChange={e => setF({...f, value: e.target.value})} data-testid="promo-field-value"/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="kr-caption">Début</label><div className="kr-date"><input type="date" value={f.start_date} onChange={e => setF({...f, start_date: e.target.value})} data-testid="promo-field-start"/><CalendarIcon className="kr-date-icon"/></div></div>
            <div><label className="kr-caption">Fin</label><div className="kr-date"><input type="date" value={f.end_date} onChange={e => setF({...f, end_date: e.target.value})} data-testid="promo-field-end"/><CalendarIcon className="kr-date-icon"/></div></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="kr-caption">Max utilisations</label><input type="number" value={f.max_uses} onChange={e => setF({...f, max_uses: e.target.value})} placeholder="illimité"/></div>
            <div><label className="kr-caption">Montant min. avant taxes ($)</label><input type="number" value={f.min_amount} onChange={e => setF({...f, min_amount: e.target.value})}/></div>
          </div>
          <div>
            <label className="kr-caption">Services admissibles (vide = tous)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {services.map(s => (
                <button key={s.code} onClick={() => toggleArr("eligible_services", s.code)} data-testid={`promo-elig-${s.code}`}
                  className={`px-2 py-1 rounded border text-xs ${f.eligible_services.includes(s.code) ? "border-[#D8301C] bg-[#141414] text-[#D8301C]" : "border-[rgba(216,48,28,0.35)] text-zinc-400"}`}>
                  {s.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="kr-caption">Forfaits admissibles (vide = tous)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {services.flatMap(s => (s.tiers||[]).map(ti => (
                <button key={ti.id} onClick={() => toggleArr("eligible_tiers", ti.id)}
                  className={`px-2 py-1 rounded border text-xs ${f.eligible_tiers.includes(ti.id) ? "border-[#D8301C] bg-[#141414] text-[#D8301C]" : "border-[rgba(216,48,28,0.35)] text-zinc-400"}`}>
                  {s.name} · {ti.label}
                </button>
              )))}
            </div>
          </div>
          <p className="text-xs text-zinc-500">Rabais applicable aux services seulement. Jamais sur les frais de déplacement. Une utilisation par courriel.</p>
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="kr-btn kr-btn-ghost">Annuler</button>
          <button onClick={() => onSave(f)} className="kr-btn kr-btn-primary" data-testid="btn-save-promo">Créer</button>
        </div>
      </div>
    </div>
  ), document.body);
}

function AddTeamModal({ onClose, onSave }) {
  const [f, setF] = useState({ name: "", display_name: "", email: "", phone: "", role: "team", skills: ["photo"] });
  const toggle = (s) => setF(x => ({ ...x, skills: x.skills.includes(s) ? x.skills.filter(k => k !== s) : [...x.skills, s] }));
  return createPortal((
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="kr-card p-6 max-w-md w-full my-10 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="font-display text-2xl mb-4">Nouveau membre</h3>
        <input placeholder="Nom (interne)" value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="input-team-name"/>
        <input placeholder="Nom affiché (visible partout)" value={f.display_name} onChange={e => setF({...f, display_name: e.target.value})} className="mt-3" data-testid="input-team-displayname"/>
        <input placeholder="Email" type="email" value={f.email} onChange={e => setF({...f, email: e.target.value})} className="mt-3" data-testid="input-team-email"/>
        <input placeholder="Téléphone" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} className="mt-3" data-testid="input-team-phone"/>
        <div className="mt-4"><div className="kr-caption mb-2">Rôle</div>
          <div className="flex gap-2">
            {[["team","Équipe"],["admin","Admin"]].map(([v,label]) => (
              <button key={v} onClick={() => setF({...f, role: v})} data-testid={`role-${v}`}
                className={`px-3 py-1.5 rounded-full border text-sm ${f.role === v ? "border-[#D8301C] bg-[#141414] text-[#D8301C]" : "border-[rgba(216,48,28,0.35)] text-zinc-400"}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="mt-4"><div className="kr-caption mb-2">Compétences</div>
          <div className="flex gap-2 flex-wrap">
            {["photo","video","photo_video","drone"].map(s => (
              <button key={s} onClick={() => toggle(s)} data-testid={`skill-${s}`}
                className={`px-3 py-1.5 rounded-full border text-sm ${f.skills.includes(s) ? "border-[#D8301C] bg-[#141414] text-[#D8301C]" : "border-[rgba(216,48,28,0.35)] text-zinc-400"}`}>{s}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="kr-btn kr-btn-ghost">Annuler</button>
          <button onClick={() => onSave(f)} className="kr-btn kr-btn-primary" data-testid="btn-save-team">Enregistrer</button>
        </div>
      </div>
    </div>
  ), document.body);
}

// ===== Sessions (team/admin) =====
// Quick on-the-road actions: directions to the pin, call/text the broker, call the person on site.
function SessionActions({ b, lang }) {
  const lat = b.pin_lat ?? b.property_lat, lng = b.pin_lng ?? b.property_lng;
  const dest = lat != null && lng != null ? `${lat},${lng}` : encodeURIComponent(b.property_address || "");
  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
  const waze = lat != null && lng != null ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` : `https://waze.com/ul?q=${dest}`;
  const onsitePhone = (b.onsite_contact || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  const Btn = ({ href, children, tid }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={tid}
      className="px-3 py-2 rounded-full border border-[rgba(216,48,28,0.35)] text-xs text-zinc-200 hover:border-[#D8301C]/60 hover:text-[#D8301C] transition-colors whitespace-nowrap">{children}</a>
  );
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid={`session-actions-${b.id}`}>
      <Btn href={gmaps} tid={`route-gmaps-${b.id}`}>{lang === "fr" ? "Itinéraire" : "Directions"}</Btn>
      <Btn href={waze} tid={`route-waze-${b.id}`}>Waze</Btn>
      {b.client_phone && <Btn href={`tel:${b.client_phone}`} tid={`call-broker-${b.id}`}>{lang === "fr" ? "Appeler le courtier" : "Call broker"}</Btn>}
      {b.client_phone && <Btn href={`sms:${b.client_phone}`} tid={`sms-broker-${b.id}`}>{lang === "fr" ? "Texto au courtier" : "Text broker"}</Btn>}
      {onsitePhone.length === 10 && <Btn href={`tel:+1${onsitePhone}`} tid={`call-onsite-${b.id}`}>{lang === "fr" ? "Appeler la personne sur place" : "Call person on site"}</Btn>}
    </div>
  );
}

function SessionsPage({ user, lang }) {
  const [bookings, setBookings] = useState([]);
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState("mine");
  useEffect(() => {
    api.get("/bookings").then(r => setBookings(r.data)).catch(() => {});
    api.get("/team").then(r => setTeam(r.data)).catch(() => {});
  }, []);
  const memberName = (id) => { const m = team.find(x => x.id === id); return m ? (m.display_name || m.name) : "—"; };
  const mine = bookings.filter(b => b.team_member_id && b.team_member_id === user?.team_member_id);
  const list = tab === "mine" ? mine : bookings;
  const tabs = [["mine", lang === "fr" ? "Mes séances" : "My sessions"], ["all", lang === "fr" ? "Toutes les séances" : "All sessions"]];
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10" data-testid="sessions-page">
      <div className="kr-caption mb-2">{lang === "fr" ? "Espace équipe" : "Team space"}</div>
      <h1 className="font-display text-4xl mb-6">{lang === "fr" ? "Séances" : "Sessions"}</h1>
      <div className="flex gap-2 mb-6 border-b border-[rgba(216,48,28,0.35)]">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-sessions-${k}`}
            className={`px-4 py-2 font-display text-sm border-b-2 transition ${tab === k ? "border-[#D8301C] text-[#D8301C]" : "border-transparent text-zinc-400"}`}>
            {label} {k === "mine" ? `(${mine.length})` : `(${bookings.length})`}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {list.map(b => (
          <div key={b.id} className="kr-card kr-card-interactive p-5" data-testid={`session-card-${b.id}`}>
            <div className="flex justify-between flex-wrap gap-3">
              <div>
                <div className="font-semibold">{b.property_address}{b.is_demo && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#D8301C]/20 text-[#D8301C]">DÉMO</span>}{b.is_test && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#D8301C]/20 text-[#D8301C] font-semibold" data-testid={`test-badge-${b.id}`}>TEST</span>}</div>
                <div className="text-sm text-zinc-400">{fmtDateTime(b.start_at, lang)} · {b.onsite_min ? `${fmtDur(b.onsite_min, lang)} ${lang === "fr" ? "sur place" : "on site"}` : `${b.duration_min} min`}{b.travel?.duration_min ? ` · ${lang === "fr" ? "trajet" : "travel"} ${b.travel.duration_min} min` : ""}{b.travel?.distance_km != null ? ` · ${b.travel.distance_km} km` : ""}</div>
                <div className="mt-1" data-testid={`access-mode-${b.id}`}><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${b.access_mode === "owner_present" ? "bg-[#141414] text-[#E6412D]" : "bg-[#D8301C]/15 text-[#D8301C]"}`}>{t(lang, b.access_mode === "owner_present" ? "access_owner" : b.access_mode === "broker_present" ? "access_broker" : "access_keybox")}</span></div>
                {b.video_format && <div className="mt-1"><span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300" data-testid={`session-video-format-${b.id}`}>{lang === "fr" ? "Format vidéo" : "Video format"} : {vfLabel(b.video_format, lang)}</span></div>}
                {b.video_style && <VideoStyleBlock style={b.video_style} lang={lang} id={b.id}/>}
                <div className="text-sm text-zinc-500 mt-1">{lang === "fr" ? "Client" : "Client"}: {b.client_name} · {fmtPhone(b.client_phone)}</div>
                {b.pin_adjusted && <div className="text-xs text-emerald-400 mt-1" data-testid={`pin-adjusted-${b.id}`}>{lang === "fr" ? "Emplacement ajusté par le courtier" : "Location adjusted by the broker"}</div>}
                {b.coords_mode && <div className="text-xs text-[#E6412D] mt-1" data-testid={`coords-mode-${b.id}`}>{lang === "fr" ? "Emplacement par coordonnées" : "Location by coordinates"} : {b.pin_lat?.toFixed(5)}, {b.pin_lng?.toFixed(5)}{b.location_description ? ` — ${b.location_description}` : ""}</div>}
                {b.directions_note && <div className="text-sm text-zinc-300 mt-1" data-testid={`directions-${b.id}`}>{lang === "fr" ? "Indications" : "Directions"} : {b.directions_note}</div>}
                {b.onsite_contact && <div className="text-sm text-zinc-300 mt-1" data-testid={`onsite-contact-${b.id}`}>{lang === "fr" ? "Sur place" : "On site"} : {b.onsite_contact}</div>}
                <SessionActions b={b} lang={lang}/>
                <div className="text-xs gold-text mt-1">{lang === "fr" ? "Assigné à" : "Assigned to"}: {memberName(b.team_member_id)}</div>
              </div>
              <Link to={`/galerie/${b.gallery_id || b.id}`} className="kr-btn kr-btn-primary text-sm self-start" data-testid={`open-session-${b.id}`}>{lang === "fr" ? "Ouvrir la séance" : "Open session"}<ArrowRight className="w-4 h-4"/></Link>
            </div>
          </div>
        ))}
        {!list.length && <div className="text-zinc-400" data-testid="sessions-empty">{tab === "mine" ? (lang === "fr" ? "Aucune séance assignée." : "No assigned session.") : (lang === "fr" ? "Aucune séance." : "No session.")}</div>}
      </div>
    </div>
  );
}

// ===== Courtier dashboard (Mes propriétés) =====
function CourtierDashboard({ lang }) {
  const [bookings, setBookings] = useState([]);
  const [cfg, setCfg] = useState({});
  const [confirmId, setConfirmId] = useState(null);
  const load = () => api.get("/bookings").then(r => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/settings").then(r => setCfg(r.data)).catch(() => {}); }, []);
  const contact = `${cfg.phone || ""}${cfg.phone && cfg.contact_email ? " · " : ""}${cfg.contact_email || ""}`.trim();
  const cancellable = (b) => (new Date(b.start_at).getTime() - Date.now()) > 48 * 3600 * 1000 && b.status !== "cancelled";
  const doCancel = async (id) => {
    try { await api.put(`/bookings/${id}/cancel`); toast.success(lang === "fr" ? "Séance annulée." : "Session cancelled."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || (lang === "fr" ? "Annulation impossible." : "Cancellation failed.")); }
    setConfirmId(null);
  };
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10" data-testid="courtier-dashboard">
      <div className="kr-caption mb-2">{lang === "fr" ? "Mon compte" : "My account"}</div>
      <h1 className="font-display text-4xl mb-8">{lang === "fr" ? "Mes propriétés" : "My properties"}</h1>
      {confirmId && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid="cancel-confirm-dialog">
          <div className="kr-card p-6 max-w-md w-full">
            <h3 className="font-display text-xl mb-2">{lang === "fr" ? "Annuler cette séance ?" : "Cancel this session?"}</h3>
            <p className="text-sm text-zinc-400 mb-4">{lang === "fr" ? "La séance sera retirée de l'agenda et notre équipe sera avisée." : "The session will be removed from the calendar and our team notified."}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmId(null)} className="kr-btn kr-btn-ghost text-sm" data-testid="cancel-confirm-no">{lang === "fr" ? "Garder la séance" : "Keep session"}</button>
              <button onClick={() => doCancel(confirmId)} className="kr-btn kr-btn-primary text-sm" data-testid="cancel-confirm-yes">{lang === "fr" ? "Confirmer l'annulation" : "Confirm cancellation"}</button>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {bookings.map(b => (
          <div key={b.id} className="kr-card kr-card-interactive p-5" data-testid={`property-card-${b.id}`}>
            <div className="flex justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold">{b.property_address}</div>
                <div className="text-sm text-zinc-400">{fmtDateTime(b.start_at, lang)} · {(b.service_codes || []).join(", ")}</div>
              </div>
              <Link to={`/galerie/${b.gallery_id || b.id}`} className="kr-btn kr-btn-ghost text-sm self-start" data-testid={`property-gallery-${b.id}`}>{lang === "fr" ? "Voir mes livrables" : "View deliverables"}</Link>
            </div>
            {b.status !== "cancelled" && (cancellable(b) ? (
              <button onClick={() => setConfirmId(b.id)} className="text-xs text-zinc-400 hover:text-[#D8301C] mt-3" data-testid={`property-cancel-${b.id}`}>
                {lang === "fr" ? "Annuler cette séance" : "Cancel this session"}
              </button>
            ) : (
              <div className="text-xs text-zinc-500 mt-3" data-testid={`property-cancel-locked-${b.id}`}>
                {lang === "fr" ? "Pour annuler, contactez-nous" : "To cancel, contact us"}{contact ? ` : ${contact}` : ""}
              </div>
            ))}
          </div>
        ))}
        {!bookings.length && <div className="text-zinc-400" data-testid="properties-empty">{lang === "fr" ? "Aucune propriété pour l'instant." : "No property yet."}</div>}
      </div>
    </div>
  );
}

// ===== Team access login (/equipe) =====
// Never bounce silently to the home page: say what is missing and how to sign in.
function RequireRole({ user, loaded, roles, lang, area, children }) {
  if (!loaded) return <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 text-zinc-400" data-testid="auth-loading"><Loader2 className="w-5 h-5 animate-spin"/></div>;
  if (user && roles.includes(user.role)) return children;
  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-20" data-testid="access-denied">
      <div className="kr-card p-8">
        <h1 className="font-display text-2xl mb-2">{user ? (lang === "fr" ? "Accès refusé" : "Access denied") : (lang === "fr" ? "Connexion requise" : "Sign-in required")}</h1>
        <p className="text-sm text-zinc-400 mb-6">
          {user
            ? (lang === "fr" ? `Votre compte (${user.email}) n'a pas accès à la section ${area}. Rôle actuel : ${user.role}.` : `Your account (${user.email}) cannot access the ${area} section. Current role: ${user.role}.`)
            : (lang === "fr" ? `Connectez-vous avec votre compte Kréatif pour ouvrir la section ${area}.` : `Sign in with your Kréatif account to open the ${area} section.`)}
        </p>
        <Link to="/equipe" className="kr-btn kr-btn-primary text-sm" data-testid="access-denied-login">{lang === "fr" ? "Aller à la connexion" : "Go to sign-in"}</Link>
      </div>
    </div>
  );
}

function TeamLogin({ user, loaded, lang }) {
  const navigate = useNavigate();
  const isTeam = user && ["admin", "team", "photographer"].includes(user.role);
  useEffect(() => { if (isTeam) navigate("/seances"); }, [isTeam, navigate]);
  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="team-login-page">
      <Shield className="w-12 h-12 text-[#D8301C] mx-auto mb-4"/>
      <h1 className="font-display text-3xl mb-3">{lang === "fr" ? "Accès équipe" : "Team access"}</h1>
      {!loaded ? (
        <Loader2 className="w-6 h-6 animate-spin inline text-zinc-400"/>
      ) : user && !isTeam ? (
        <div data-testid="team-no-access">
          <p className="text-zinc-400 mb-6">{lang === "fr" ? "Votre compte n'a pas accès à l'espace équipe. Contactez un administrateur Kréatif pour être ajouté." : "Your account does not have team access. Contact a Kréatif admin to be added."}</p>
          <Link to="/" className="kr-btn kr-btn-ghost">{t(lang, "back_home")}</Link>
        </div>
      ) : (
        <div>
          <p className="text-zinc-400 mb-6">{lang === "fr" ? "Connectez-vous avec votre compte Google Kréatif pour accéder à vos séances." : "Sign in with your Kréatif Google account to access your sessions."}</p>
          <button onClick={() => startLogin()} className="kr-btn kr-btn-primary" data-testid="team-google-login"><LogIn className="w-4 h-4"/>{lang === "fr" ? "Se connecter avec Google" : "Sign in with Google"}</button>
          <p className="text-xs text-zinc-500 mt-5">{lang === "fr" ? "Une fenêtre sécurisée va s'ouvrir pour confirmer votre identité." : "A secure window will open to confirm your identity."}</p>
        </div>
      )}
    </div>
  );
}

// ===== Branded login (client) =====
function LoginPage({ lang }) {
  const location = useLocation();
  const failed = new URLSearchParams(location.search).get("auth") === "failed";
  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="login-page">
      <img src="https://www.kreatif.ca/wp-content/uploads/2025/01/logo_kreatif_white.svg" alt="Kréatif" className="h-10 w-auto mx-auto mb-8"/>
      <h1 className="font-display text-3xl mb-3">{lang === "fr" ? "Connexion" : "Sign in"}</h1>
      {failed && (
        <div className="mb-5 p-3 rounded-lg text-sm" style={{ background: "rgba(216,48,28,0.10)", color: "#D8301C" }} data-testid="login-failed-msg">
          {lang === "fr" ? "La connexion n'a pas fonctionné. Veuillez réessayer." : "Sign-in didn't work. Please try again."}
        </div>
      )}
      <p className="text-zinc-400 mb-6">{lang === "fr" ? "Accédez à votre espace Kréatif pour suivre vos propriétés, vos livrables et vos paiements." : "Access your Kréatif space to follow your properties, deliverables and payments."}</p>
      <button onClick={() => startLogin()} className="kr-btn kr-btn-primary" data-testid={failed ? "login-retry-btn" : "login-google-btn"}><LogIn className="w-4 h-4"/>{failed ? (lang === "fr" ? "Réessayer" : "Try again") : (lang === "fr" ? "Se connecter avec Google" : "Sign in with Google")}</button>
      <p className="text-xs text-zinc-500 mt-5">{lang === "fr" ? "Une fenêtre sécurisée va s'ouvrir pour confirmer votre identité." : "A secure window will open to confirm your identity."}</p>
    </div>
  );
}

// ===== Voice recorder (Phase B1) =====
const pickVoiceMime = () => {
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of cands) { try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (e) {} }
  return "";
};
const vfmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

function VoiceRecorder({ lang, onSend, onCancel }) {
  const [state, setState] = useState("idle"); // idle|recording|recorded|error|uploading
  const [elapsed, setElapsed] = useState(0);
  const [err, setErr] = useState("");
  const [url, setUrl] = useState(null);
  const mrRef = useRef(null), chunksRef = useRef([]), timerRef = useRef(null);
  const streamRef = useRef(null), blobRef = useRef(null), mimeRef = useRef(""), durRef = useRef(0);
  const cleanupStream = () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } };
  const stop = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } if (mrRef.current && mrRef.current.state !== "inactive") mrRef.current.stop(); };
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); cleanupStream(); }, []);
  const start = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickVoiceMime();
      mimeRef.current = mime || "audio/webm";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        blobRef.current = blob; setUrl(URL.createObjectURL(blob)); setState("recorded"); cleanupStream();
      };
      mr.start(); mrRef.current = mr; setState("recording"); setElapsed(0); durRef.current = 0;
      const t0 = Date.now();
      timerRef.current = setInterval(() => { const e = (Date.now() - t0) / 1000; setElapsed(e); durRef.current = e; if (e >= 120) stop(); }, 200);
    } catch (e) {
      setErr(lang === "fr" ? "Micro refusé. Autorisez le micro (icône cadenas dans la barre d'adresse → Microphone → Autoriser), puis réessayez." : "Mic blocked. Allow the microphone (lock icon in the address bar → Microphone → Allow), then try again.");
      setState("error");
    }
  };
  const reset = () => { setUrl(null); blobRef.current = null; setState("idle"); setElapsed(0); durRef.current = 0; };
  const doSend = async () => { setState("uploading"); await onSend(blobRef.current, durRef.current || elapsed, mimeRef.current); };
  return (
    <div className="p-3 rounded-lg border border-[#D8301C]/40 bg-[#141414]" data-testid="voice-recorder">
      {state === "idle" && (
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={start} className="kr-btn kr-btn-primary text-sm" data-testid="voice-start"><Mic className="w-4 h-4"/>{lang === "fr" ? "Démarrer l'enregistrement" : "Start recording"}</button>
          <button onClick={onCancel} className="kr-btn kr-btn-ghost text-sm" data-testid="voice-cancel">{lang === "fr" ? "Annuler" : "Cancel"}</button>
          <span className="text-[10px] text-zinc-500">{lang === "fr" ? "max 2 min" : "max 2 min"}</span>
        </div>
      )}
      {state === "recording" && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"/>
          <span className="font-mono text-sm text-[#D8301C]" data-testid="voice-timer">{vfmt(elapsed)}</span>
          <button onClick={stop} className="kr-btn kr-btn-primary text-sm" data-testid="voice-stop">{lang === "fr" ? "Arrêter" : "Stop"}</button>
          <button onClick={() => { stop(); onCancel(); }} className="kr-btn kr-btn-ghost text-sm">{lang === "fr" ? "Annuler" : "Cancel"}</button>
        </div>
      )}
      {state === "recorded" && (
        <div className="flex flex-col gap-2">
          <audio controls src={url} className="w-full" data-testid="voice-preview"/>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={doSend} className="kr-btn kr-btn-primary text-sm" data-testid="voice-send">{lang === "fr" ? "Envoyer le vocal" : "Send voice note"}</button>
            <button onClick={reset} className="kr-btn kr-btn-ghost text-sm" data-testid="voice-redo">{lang === "fr" ? "Recommencer" : "Re-record"}</button>
            <button onClick={onCancel} className="kr-btn kr-btn-ghost text-sm">{lang === "fr" ? "Annuler" : "Cancel"}</button>
            <span className="text-[10px] text-zinc-500">{vfmt(durRef.current || elapsed)}</span>
          </div>
        </div>
      )}
      {state === "uploading" && <div className="text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1"/>{lang === "fr" ? "Envoi…" : "Uploading…"}</div>}
      {state === "error" && (
        <div className="flex flex-col gap-2">
          <div className="text-sm text-[#E6412D] flex gap-2" data-testid="voice-error"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>{err}</div>
          <div className="flex gap-2"><button onClick={start} className="kr-btn kr-btn-ghost text-sm">{lang === "fr" ? "Réessayer" : "Try again"}</button><button onClick={onCancel} className="kr-btn kr-btn-ghost text-sm">{lang === "fr" ? "Annuler" : "Cancel"}</button></div>
        </div>
      )}
    </div>
  );
}

// ===== Video review player (Phase A1) =====
function VideoReview({ gid, token, d, is_team, paid, lang, comments, refresh, extraFee, uploadFiles, downloadAsset, preview, confirmAsk, promptAsk }) {
  const vref = useRef(null);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [frozen, setFrozen] = useState(null);
  const [text, setText] = useState("");
  const [editId, setEditId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [active, setActive] = useState(null);
  const [recOpen, setRecOpen] = useState(false);
  const [replyRecFor, setReplyRecFor] = useState(null);
  const [trial, setTrial] = useState([]);
  const [vdim, setVdim] = useState({ w: 0, h: 0 });
  const [playing, setPlaying] = useState(false);
  const [vol, setVol] = useState(1);
  const [muted, setMuted] = useState(false);
  const [dragging, setDragging] = useState(false);
  const barRef = useRef(null);
  const togglePlay = () => { const v = vref.current; if (!v) return; if (v.paused) v.play(); else v.pause(); };
  const seekToClientX = (clientX) => { const el = barRef.current; if (!el || !dur) return; const r = el.getBoundingClientRect(); const f = Math.min(1, Math.max(0, (clientX - r.left) / r.width)); if (vref.current) { vref.current.currentTime = f * dur; setCur(f * dur); } };
  const onBarDown = (e) => { e.preventDefault(); try { barRef.current.setPointerCapture(e.pointerId); } catch {} setDragging(true); seekToClientX(e.clientX); };
  const onBarMove = (e) => { if (dragging) seekToClientX(e.clientX); };
  const onBarUp = (e) => { setDragging(false); try { barRef.current.releasePointerCapture(e.pointerId); } catch {} };
  const setVolume = (x) => { setVol(x); const v = vref.current; if (v) { v.volume = x; v.muted = x === 0; } setMuted(x === 0); };
  const toggleMute = () => { const v = vref.current; if (!v) return; const m = !v.muted; v.muted = m; setMuted(m); };
  const goFullscreen = () => { const v = vref.current; if (!v) return; if (v.requestFullscreen) v.requestFullscreen(); else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen(); };
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (document.querySelector('[data-testid="lightbox"]')) return;
      const v = vref.current; if (!v) return;
      if (e.code === "Space") { e.preventDefault(); if (v.paused) v.play(); else v.pause(); }
      else if (e.code === "ArrowLeft") { v.currentTime = Math.max(0, v.currentTime - 5); }
      else if (e.code === "ArrowRight") { v.currentTime = Math.min(v.duration || dur || 0, v.currentTime + 5); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dur]);
  useEffect(() => { if (!preview) setTrial([]); }, [preview]);
  const params = token ? { t: token } : {};
  const fmt = (s) => { s = Math.floor(s || 0); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
  const ver = d.version || 1;
  const vids = (d.assets || []).filter(a => a.kind === "video");
  const current = vids.filter(a => (a.version || 1) === ver);
  const show = current[0] || vids[vids.length - 1];
  const canDl = (paid && d.status === "delivered") || is_team || (preview && paid);
  const real = comments.filter(c => c.dtype === d.type && !(preview && c.internal));
  const mine = preview ? [...real, ...trial].sort((a, b) => (a.t || 0) - (b.t || 0)) : real;
  const stamp = frozen !== null ? frozen : cur;
  const numMap = (() => { const m = {}; let mx = 0; mine.forEach(c => { if (c.seq) mx = Math.max(mx, c.seq); }); let dc = 0; mine.forEach(c => { if (c.seq) m[c.id] = c.seq; else { dc += 1; m[c.id] = mx + dc; } }); return m; })();
  const aspect = (vdim.w && vdim.h) ? vdim.w / vdim.h : ((show && show.width && show.height) ? show.width / show.height : 16 / 9);
  const portrait = aspect < 0.95;
  const trialName = lang === "fr" ? "Vous (essai)" : "You (trial)";
  const newTrial = (extra) => ({ id: "trial_" + Date.now() + Math.random().toString(36).slice(2, 6), dtype: d.type, t: stamp, text: text.trim(), status: "draft", trial: true, author_name: trialName, edited: false, replies: [], ...extra });
  const seek = (tt, cid) => { const v = vref.current; if (v) { const apply = () => { v.currentTime = tt; v.pause(); setCur(tt); }; if (v.readyState >= 1) apply(); else v.addEventListener("loadedmetadata", apply, { once: true }); } if (cid !== undefined) { setActive(cid); setTimeout(() => { const el = document.getElementById(`cmt-${cid}`); if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, 50); } };
  const fmtVoice = (s) => `${Math.floor((s || 0) / 60)} min ${String(Math.floor((s || 0) % 60)).padStart(2, "0")} s`;
  const uploadVoice = async (blob, mime) => {
    const ext = (mime || "").includes("mp4") || (mime || "").includes("aac") ? "mp4" : "webm";
    const ct = mime || "audio/webm";
    const { data: pre } = await api.post(`/galleries/${gid}/voice-url`, { content_type: ct, ext }, { params });
    await axios.put(pre.upload_url, blob, { headers: { "Content-Type": ct } });
    return pre.voice_key;
  };
  const sendVoiceComment = async (blob, duration, mime) => {
    if (preview) {
      const url = URL.createObjectURL(blob);
      setTrial(ts => [...ts, newTrial({ voice_url: url, voice_duration: Math.round(duration) })]);
      setText(""); setFrozen(null); setRecOpen(false); return;
    }
    try {
      const key = await uploadVoice(blob, mime);
      await api.post(`/galleries/${gid}/comments`, { dtype: d.type, t: stamp, text: text.trim(), voice_key: key, voice_duration: Math.round(duration) }, { params });
      setText(""); setFrozen(null); setRecOpen(false); refresh();
    } catch { toast.error(t(lang, "err_generic")); setRecOpen(false); }
  };
  const sendVoiceReply = async (cid, blob, duration, mime) => {
    try {
      const key = await uploadVoice(blob, mime);
      await api.post(`/galleries/${gid}/comments/${cid}/reply`, { text: "", voice_key: key, voice_duration: Math.round(duration) });
      setReplyRecFor(null); refresh();
    } catch { toast.error(t(lang, "err_generic")); setReplyRecFor(null); }
  };
  const onType = (v) => { setText(v); if (v && vref.current && !vref.current.paused) vref.current.pause(); if (v && frozen === null) setFrozen(cur); };
  const add = async () => {
    if (!text.trim()) return;
    if (preview) {
      if (editId) setTrial(ts => ts.map(c => c.id === editId ? { ...c, text: text.trim(), t: stamp, edited: c.status !== "draft" } : c));
      else setTrial(ts => [...ts, newTrial({})]);
      setText(""); setFrozen(null); setEditId(null); return;
    }
    try {
      if (editId) await api.put(`/galleries/${gid}/comments/${editId}`, { dtype: d.type, t: stamp, text }, { params });
      else await api.post(`/galleries/${gid}/comments`, { dtype: d.type, t: stamp, text }, { params });
      setText(""); setFrozen(null); setEditId(null); refresh();
    } catch { toast.error(t(lang, "err_generic")); }
  };
  const del = (cid) => confirmAsk({ message: lang === "fr" ? "Supprimer ce commentaire ?" : "Delete this comment?", confirmLabel: lang === "fr" ? "Supprimer" : "Delete", onConfirm: async () => {
    if (preview) { setTrial(ts => ts.filter(c => c.id !== cid)); return; }
    try { await api.delete(`/galleries/${gid}/comments/${cid}`, { params }); refresh(); } catch { toast.error(t(lang, "err_generic")); }
  } });
  const startEdit = (c) => { setEditId(c.id); setText(c.text || ""); setFrozen(c.t); setRecOpen(false); };
  const cancelEdit = () => { setEditId(null); setText(""); setFrozen(null); setRecOpen(false); };
  const saveEditVoice = async (blob, duration, mime) => {
    if (preview) {
      const url = URL.createObjectURL(blob);
      setTrial(ts => ts.map(c => c.id === editId ? { ...c, voice_url: url, voice_duration: Math.round(duration), text, t: frozen ?? stamp, edited: c.status !== "draft" } : c));
      setText(""); setFrozen(null); setEditId(null); setRecOpen(false); return;
    }
    try {
      const key = await uploadVoice(blob, mime);
      await api.put(`/galleries/${gid}/comments/${editId}`, { dtype: d.type, t: frozen ?? stamp, text, voice_key: key, voice_duration: Math.round(duration) }, { params });
      setText(""); setFrozen(null); setEditId(null); setRecOpen(false); refresh();
    } catch { toast.error(t(lang, "err_generic")); setRecOpen(false); }
  };
  const reply = async (cid) => { if (!replyText.trim()) return; await api.post(`/galleries/${gid}/comments/${cid}/reply`, { text: replyText }); setReplyTo(null); setReplyText(""); refresh(); };
  const done = async (cid) => { await api.post(`/galleries/${gid}/comments/${cid}/done`); refresh(); };
  const addInternal = () => promptAsk({ message: lang === "fr" ? "Note interne (équipe seulement)" : "Internal note (team only)", confirmLabel: lang === "fr" ? "Ajouter la note" : "Add note", onConfirm: async (c) => { if (c && c.trim()) { await api.post(`/galleries/${gid}/comments`, { dtype: d.type, t: stamp, text: c.trim() }, {}); refresh(); } } });
  const copyList = () => { const txt = mine.filter(c => !c.internal).map((c, i) => { let b = `${i + 1}. ${fmt(c.t)} — ${c.text || ""}`.trimEnd(); if (c.voice_url) b += `${c.text ? " " : " "}(${lang === "fr" ? "vocal" : "voice"}, ${fmtVoice(c.voice_duration)})`; return b; }).join("\n"); navigator.clipboard.writeText(txt); toast.success(lang === "fr" ? "Liste copiée" : "List copied"); };
  const send = () => {
    const drafts = mine.filter(c => c.status === "draft" && !c.internal);
    if (!drafts.length) return toast.error(lang === "fr" ? "Aucun brouillon à envoyer" : "No draft to send");
    confirmAsk({
      message: lang === "fr" ? `Envoyer ${drafts.length} commentaire${drafts.length > 1 ? "s" : ""} ? Ceci utilise votre ronde de correction incluse.` : `Send ${drafts.length} comment${drafts.length > 1 ? "s" : ""}? This uses your included revision round.`,
      confirmLabel: lang === "fr" ? "Envoyer mes commentaires" : "Send my comments",
      onConfirm: async () => {
        if (preview) {
          setTrial(ts => { let mx = Math.max(0, ...ts.map(c => c.seq || 0)); const order = ts.filter(c => c.status === "draft").sort((a, b) => (a.t || 0) - (b.t || 0)).map(c => c.id); return ts.map(c => order.includes(c.id) ? { ...c, status: "sent", seq: mx + 1 + order.indexOf(c.id) } : c); });
          toast.success(lang === "fr" ? "Commentaires envoyés (essai) — rien n'est enregistré" : "Comments sent (trial) — nothing is saved"); return;
        }
        try {
          await api.post(`/galleries/${gid}/comments/send`, { dtype: d.type, origin_url: window.location.origin }, { params });
          toast.success(lang === "fr" ? "Commentaires envoyés" : "Comments sent"); refresh();
        } catch (e) {
          if (e.response?.status === 402) {
            confirmAsk({
              message: lang === "fr" ? `Ronde supplémentaire (${extraFee} $). Continuer vers le paiement ?` : `Extra round ($${extraFee}). Continue to payment?`,
              confirmLabel: lang === "fr" ? "Continuer vers le paiement" : "Continue to payment",
              onConfirm: async () => {
                try { const { data } = await api.post(`/galleries/${gid}/revision-checkout`, { origin_url: window.location.origin }, { params }); window.location.href = data.checkout_url; }
                catch { toast.error(t(lang, "err_generic")); }
              }
            });
          } else toast.error(t(lang, "err_generic"));
        }
      }
    });
  };
  const approve = () => confirmAsk({
    message: lang === "fr" ? "Approuver la vidéo sans correction ?" : "Approve the video with no change?",
    confirmLabel: lang === "fr" ? "Approuver" : "Approve",
    onConfirm: async () => {
      if (preview) { toast.success(lang === "fr" ? "Vidéo approuvée (essai) — rien n'est enregistré" : "Video approved (trial) — nothing is saved"); return; }
      await api.post(`/galleries/${gid}/approve`, { origin_url: window.location.origin }, { params }); toast.success(lang === "fr" ? "Vidéo approuvée. Merci !" : "Video approved. Thank you!");
    }
  });
  const newVersion = async (files) => { await api.post(`/galleries/${gid}/deliverables/${d.type}/new-version`); await uploadFiles(d.type, files); };
  if (d.status === "in_production" && !is_team && !preview) return null;
  if (!show) return null;
  const src = canDl ? (show.original_url || show.preview_url) : show.preview_url;
  return (
    <div className="flex flex-col lg:flex-row gap-5" data-testid={`video-review-${d.type}`}>
      <div className={portrait ? "lg:shrink-0" : "lg:flex-1 min-w-0"}>
        <div className={`bg-black rounded-xl overflow-hidden flex items-center justify-center ${portrait ? "mx-auto w-full lg:w-auto lg:h-[80vh]" : "w-full"}`} style={portrait ? { aspectRatio: String(aspect) } : { aspectRatio: String(aspect), maxHeight: "80vh" }}>
          {show.preview_status === "processing" ? (
            <div className="p-16 text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />{lang === "fr" ? "Aperçu en préparation…" : "Preview in progress…"}</div>
          ) : (
            <video ref={vref} src={src} preload="metadata" playsInline disablePictureInPicture onContextMenu={e => e.preventDefault()}
              onClick={togglePlay} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
              onTimeUpdate={e => setCur(e.target.currentTime)} onLoadedMetadata={e => { setDur(e.target.duration); setVdim({ w: e.target.videoWidth, h: e.target.videoHeight }); }}
              className="w-full h-full object-contain bg-black" data-testid={`video-player-${d.type}`} />
          )}
        </div>
        {show.preview_status !== "processing" && (
          <div className="mt-2 select-none" data-testid="video-controls">
            <div ref={barRef} onPointerDown={onBarDown} onPointerMove={onBarMove} onPointerUp={onBarUp}
              className="relative h-8 flex items-center cursor-pointer touch-none" data-testid="progress-wrap">
              <div className="absolute left-0 right-0 h-1.5 rounded-full bg-[#2A2E37]" style={{ top: "50%", transform: "translateY(-50%)" }} />
              <div className="absolute left-0 h-1.5 rounded-full bg-[#D8301C]" style={{ top: "50%", transform: "translateY(-50%)", width: `${dur ? Math.min(100, Math.max(0, (cur / dur) * 100)) : 0}%` }} />
              <div className="absolute w-3 h-3 rounded-full bg-[#D8301C] shadow pointer-events-none" style={{ top: "50%", left: `${dur ? Math.min(100, Math.max(0, (cur / dur) * 100)) : 0}%`, transform: "translate(-50%,-50%)" }} />
              {mine.map(c => {
                const left = dur ? Math.min(100, Math.max(0, (c.t / dur) * 100)) : 0;
                const done = c.status === "done";
                const color = done ? "#10b981" : "#D8301C";
                const isActive = active === c.id;
                const tip = (c.text || (c.voice_url ? (lang === "fr" ? "note vocale" : "voice note") : "")).slice(0, 48);
                return (
                  <button key={c.id} data-testid={`marker-${c.id}`} onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); seek(c.t, c.id); }}
                    title={`#${numMap[c.id]} · ${c.author_name}${c.internal ? (lang === "fr" ? " · interne" : " · internal") : ""}${tip ? " — " + tip : ""}`}
                    className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left: `${left}%`, top: 0, zIndex: isActive ? 20 : (done ? 5 : 10) }}>
                    <span className="font-mono leading-none" style={{ fontSize: 9, color, opacity: done ? 0.6 : 1 }}>{numMap[c.id]}</span>
                    <span className="block rounded-full" style={{ width: isActive ? 6 : 3, height: isActive ? 16 : 11, marginTop: 1, background: color, opacity: done ? 0.6 : 1, boxShadow: isActive ? `0 0 6px ${color}` : "none" }} />
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3 mt-1 text-zinc-300">
              <button onClick={togglePlay} data-testid="play-toggle" className="hover:text-white" aria-label="Play/Pause">{playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}</button>
              <span className="text-xs font-mono tabular-nums text-zinc-400" data-testid="time-display">{fmt(cur)} / {fmt(dur)}</span>
              <div className="flex items-center gap-1 ml-auto">
                <button onClick={toggleMute} className="hover:text-white" data-testid="mute-toggle" aria-label="Mute">{(muted || vol === 0) ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</button>
                <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : vol} onChange={e => setVolume(parseFloat(e.target.value))} className="kr-range-fill w-24" style={{ "--fill": `${(muted ? 0 : vol) * 100}%` }} data-testid="volume" aria-label="Volume" />
              </div>
              <button onClick={goFullscreen} className="hover:text-white" data-testid="fullscreen" aria-label="Fullscreen"><Maximize className="w-5 h-5" /></button>
            </div>
          </div>
        )}
        <div className="mt-2 text-xs text-zinc-500">V{ver} · {show.filename}</div>
        {canDl && (
          <button onClick={() => downloadAsset(d.type, show.id)} className="kr-btn kr-btn-primary text-sm mt-3" data-testid={`download-${show.id}`}>{t(lang, "gallery_download")}</button>
        )}
      </div>
      <div className={`flex flex-col ${portrait ? "lg:flex-1 min-w-0" : "lg:w-[360px] lg:shrink-0"}`}>
        <div className="font-display text-lg mb-2">{lang === "fr" ? "Commentaires" : "Comments"}</div>
        <div className="flex-1 space-y-2 overflow-y-auto max-h-[46vh] pr-1" data-testid="comments-list">
          {mine.map((c, i) => (
            <div key={c.id} id={`cmt-${c.id}`} onClick={() => seek(c.t, c.id)} className={`p-2 rounded-lg border text-sm cursor-pointer transition-all hover:border-[#D8301C]/60 active:scale-[.99] ${c.internal ? "border-[rgba(216,48,28,0.35)] bg-[#141414]" : "border-[rgba(216,48,28,0.35)] bg-[#141414]"} ${c.status === "done" ? "opacity-60" : ""} ${active === c.id ? "ring-2 ring-[#D8301C]" : ""}`} data-testid={`comment-${c.id}`}>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); seek(c.t, c.id); }} className="text-[#D8301C] font-mono text-xs hover:underline" data-testid={`seek-${c.id}`}>{fmt(c.t)}</button>
                <span className="text-[10px] text-zinc-500">#{numMap[c.id]} · {c.author_name}{c.internal ? (lang === "fr" ? " · interne" : " · internal") : ""}{c.trial ? (lang === "fr" ? " · essai" : " · trial") : ""}{c.status === "done" ? " · ✓" : ""}{c.status === "draft" ? (lang === "fr" ? " · brouillon" : " · draft") : ""}{c.edited ? (lang === "fr" ? " · modifié" : " · edited") : ""}</span>
              </div>
              {c.text && <div className="mt-1">{c.text}</div>}
              {c.voice_url && <audio controls src={c.voice_url} onClick={(e) => e.stopPropagation()} className="w-full mt-1.5 h-9" data-testid={`voice-audio-${c.id}`}/>}
              {(c.replies || []).map((r, ri) => (<div key={ri} className="mt-1 pl-2 border-l border-[rgba(216,48,28,0.35)] text-xs text-zinc-400" onClick={(e) => e.stopPropagation()}>↳ {r.author_name}: {r.voice_url ? <audio controls src={r.voice_url} className="w-full mt-1 h-8" data-testid={`reply-voice-${c.id}-${ri}`}/> : r.text}</div>))}
              <div className="flex gap-2 mt-1 text-[10px]" onClick={(e) => e.stopPropagation()}>
                {!is_team && ((!preview && c.status === "draft") || (preview && c.trial && c.status === "draft")) && (<><button onClick={() => startEdit(c)} data-testid={`edit-${c.id}`} className="text-zinc-400 hover:text-zinc-200">{lang === "fr" ? "Modifier" : "Edit"}</button><button onClick={() => del(c.id)} data-testid={`del-comment-${c.id}`} className="text-red-400 hover:text-red-300">{lang === "fr" ? "Supprimer" : "Delete"}</button></>)}
                {is_team && (<><button onClick={() => { setReplyTo(c.id); setReplyRecFor(null); }} className="text-zinc-400 hover:text-zinc-200">{lang === "fr" ? "Répondre" : "Reply"}</button><button onClick={() => done(c.id)} data-testid={`done-${c.id}`} className="text-emerald-400 hover:text-emerald-300">{c.status === "done" ? (lang === "fr" ? "Rouvrir" : "Reopen") : (lang === "fr" ? "Fait" : "Done")}</button>{c.author_role === "team" && <button onClick={() => startEdit(c)} data-testid={`edit-${c.id}`} className="text-zinc-400 hover:text-zinc-200">{lang === "fr" ? "Modifier" : "Edit"}</button>}<button onClick={() => del(c.id)} data-testid={`del-comment-${c.id}`} className="text-red-400 hover:text-red-300">{lang === "fr" ? "Supprimer" : "Delete"}</button></>)}
              </div>
              {replyTo === c.id && (<div className="mt-1" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-1"><input value={replyText} onChange={e => setReplyText(e.target.value)} className="text-xs" placeholder={lang === "fr" ? "Réponse…" : "Reply…"} data-testid={`reply-input-${c.id}`}/><button onClick={() => reply(c.id)} className="kr-btn kr-btn-ghost text-xs">OK</button><button onClick={() => setReplyRecFor(replyRecFor === c.id ? null : c.id)} className="kr-btn kr-btn-ghost text-xs" data-testid={`reply-voice-btn-${c.id}`}><Mic className="w-3.5 h-3.5"/></button></div>
                {replyRecFor === c.id && <div className="mt-2"><VoiceRecorder lang={lang} onCancel={() => setReplyRecFor(null)} onSend={(b, du, m) => sendVoiceReply(c.id, b, du, m)} /></div>}
              </div>)}
            </div>
          ))}
          {!mine.length && <div className="text-zinc-500 text-sm">{lang === "fr" ? "Aucun commentaire." : "No comment."}</div>}
        </div>
        <div className="mt-3 border-t border-[rgba(216,48,28,0.35)] pt-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] px-2 py-1 rounded-full bg-[#D8301C]/20 text-[#D8301C] font-mono" data-testid="timecode-pill">{fmt(stamp)}</span>
            {frozen !== null && <span className="text-[10px] text-zinc-500">{lang === "fr" ? "figé" : "frozen"}</span>}
          </div>
          <textarea rows="2" value={text} onFocus={() => { if (vref.current) vref.current.pause(); if (frozen === null) setFrozen(cur); }} onChange={e => onType(e.target.value)} placeholder={lang === "fr" ? "Votre commentaire à ce moment…" : "Your comment at this moment…"} data-testid="comment-input" />
          <div className="flex gap-2 mt-2 flex-wrap">
            <button onClick={add} className="kr-btn kr-btn-ghost text-sm" data-testid="comment-add">{editId ? (lang === "fr" ? "Enregistrer" : "Save") : (lang === "fr" ? "Ajouter" : "Add")}</button>
            {editId && <button onClick={cancelEdit} className="kr-btn kr-btn-ghost text-sm" data-testid="comment-cancel-edit">{lang === "fr" ? "Annuler" : "Cancel"}</button>}
            <button onClick={() => { if (vref.current) vref.current.pause(); if (frozen === null) setFrozen(cur); setRecOpen(true); }} className="kr-btn kr-btn-ghost text-sm" data-testid="voice-record-btn"><Mic className="w-4 h-4"/>{editId ? (lang === "fr" ? "Ré-enregistrer le vocal" : "Re-record voice") : (lang === "fr" ? "Enregistrer un vocal" : "Record a voice note")}</button>
            {is_team && <button onClick={addInternal} className="kr-btn kr-btn-ghost text-sm" data-testid="comment-internal">{lang === "fr" ? "Note interne" : "Internal note"}</button>}
          </div>
          {recOpen && <div className="mt-2"><VoiceRecorder lang={lang} onCancel={() => setRecOpen(false)} onSend={editId ? saveEditVoice : sendVoiceComment} /></div>}
          {!is_team && (
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={send} className="kr-btn kr-btn-primary text-sm" data-testid="comments-send">{lang === "fr" ? "Envoyer mes commentaires" : "Send my comments"}</button>
              <button onClick={approve} className="kr-btn kr-btn-ghost text-sm" data-testid="video-approve">{lang === "fr" ? "J'approuve la vidéo" : "I approve the video"}</button>
            </div>
          )}
          {is_team && (
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={copyList} className="kr-btn kr-btn-ghost text-sm" data-testid="copy-list">{lang === "fr" ? "Copier la liste" : "Copy list"}</button>
              <label className="kr-btn kr-btn-primary text-sm cursor-pointer" data-testid="new-version-btn">{lang === "fr" ? "Téléverser la version corrigée" : "Upload corrected version"}<input type="file" accept="video/*" className="hidden" onChange={e => e.target.files.length && newVersion(e.target.files)} /></label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Gallery (multi-deliverable project) =====
const DELIVERABLE_ORDER = ["photos", "drone", "video", "plans"];

function Lightbox({ images, index, setIndex, onClose, onDownload, lang }) {
  const img = images[index];
  const touch = useRef(null);
  const go = useCallback((dir) => setIndex(i => (i + dir + images.length) % images.length), [images.length, setIndex]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); else if (e.key === "ArrowLeft") go(-1); else if (e.key === "ArrowRight") go(1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);
  useEffect(() => {
    [index - 1, index + 1].forEach(j => { const it = images[(j + images.length) % images.length]; if (it && it.url) { const im = new window.Image(); im.src = it.url; } });
  }, [index, images]);
  if (!img) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col" data-testid="lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 text-white text-sm shrink-0" onClick={e => e.stopPropagation()}>
        <span data-testid="lightbox-counter">{index + 1} / {images.length}{img.section ? ` · ${img.section}` : ""}</span>
        <button onClick={onClose} className="p-2 hover:opacity-70" data-testid="lightbox-close" aria-label="Close"><X className="w-6 h-6"/></button>
      </div>
      <div className="flex-1 flex items-center justify-center relative px-2 sm:px-16 min-h-0"
        onTouchStart={e => { touch.current = e.changedTouches[0].clientX; }}
        onTouchEnd={e => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); touch.current = null; }}>
        <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="lightbox-prev" aria-label="Previous"><ChevronLeft className="w-6 h-6"/></button>
        <img src={img.url} alt="" onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full object-contain" data-testid="lightbox-image"/>
        <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="lightbox-next" aria-label="Next"><ChevronRight className="w-6 h-6"/></button>
      </div>
      <div className="px-4 py-4 flex justify-center shrink-0" onClick={e => e.stopPropagation()}>
        {img.canDl && <button onClick={() => onDownload(img)} className="kr-btn kr-btn-primary text-sm" data-testid="lightbox-download"><Download className="w-4 h-4"/>{lang === "fr" ? "Télécharger" : "Download"}</button>}
      </div>
    </div>
  );
}

function InvoiceRecap({ gallery, lang }) {
  const tx = gallery.taxes || {};
  return (
    <div className="mt-4 pt-4 border-t border-[rgba(216,48,28,0.35)] text-sm space-y-1" data-testid="invoice-recap">
      {(gallery.booking_lines || []).map((l, i) => (
        <div key={i} className="flex justify-between"><span className="text-zinc-400">{l.name}{l.tier_label ? ` — ${l.tier_label}` : ""}{l.option_label ? ` · ${l.option_label}` : ""}</span><span>{fmtCAD(l.price, 2)}</span></div>
      ))}
      <div className="flex justify-between"><span className="text-zinc-400">{t(lang,"travel_fee")}</span><span>{gallery.travel_fee > 0 ? fmtCAD(gallery.travel_fee, 2) : t(lang,"travel_free")}</span></div>
      {gallery.promo_discount > 0 && <div className="flex justify-between gold-text"><span>{t(lang,"promo_discount_label")}</span><span>− {fmtCAD(gallery.promo_discount, 2)}</span></div>}
      {(gallery.extra_lines || []).map(l => (
        <div key={l.id} className="flex justify-between" data-testid={`extra-line-${l.id}`}><span className="text-zinc-400">{l.label}</span><span>{l.amount < 0 ? "− " : ""}{fmtCAD(Math.abs(l.amount), 2)}</span></div>
      ))}
      <div className="flex justify-between font-semibold pt-2 border-t border-[rgba(216,48,28,0.35)] mt-2"><span>{t(lang,"subtotal")}</span><span data-testid="invoice-subtotal">{fmtCAD(gallery.invoice_subtotal, 2)}</span></div>
      <div className="flex justify-between text-zinc-400"><span>{t(lang,"gst")} ({((tx.gst_rate ?? 0.05) * 100).toLocaleString("fr-CA")} %)</span><span data-testid="invoice-gst">{fmtCAD(tx.gst, 2)}</span></div>
      <div className="flex justify-between text-zinc-400"><span>{t(lang,"qst")} ({((tx.qst_rate ?? 0.09975) * 100).toLocaleString("fr-CA")} %)</span><span data-testid="invoice-qst">{fmtCAD(tx.qst, 2)}</span></div>
      <div className="flex justify-between font-semibold text-base pt-2 border-t border-[rgba(216,48,28,0.35)] mt-2"><span>{t(lang,"total")}</span><span className="gold-text">{fmtCAD(gallery.invoice_total, 2)}</span></div>
      {(tx.gst_number || tx.qst_number) && <div className="text-[11px] text-zinc-600">{fill(t(lang,"tax_numbers"), { gst: tx.gst_number || "—", qst: tx.qst_number || "—" })}</div>}
    </div>
  );
}

function QboBadge({ gallery, refresh }) {
  const q = gallery.qbo || {};
  const [busy, setBusy] = useState(false);
  if (!q.status) return null;
  const created = q.status === "created" || q.status === "invoiced";
  const retry = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/qbo/galleries/${gallery.id}/sync`); refresh();
      data.status === "created" ? toast.success(`QuickBooks : facture ${data.doc_number} créée`) : toast.error(data.reason || "QuickBooks : en attente"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="qbo-badge">
      <span className={`px-2 py-1 rounded-full ${created ? "bg-emerald-900/40 text-emerald-400" : "bg-[#141414] text-[#E6412D]"}`}>
        {created ? `QuickBooks : facture ${q.doc_number} créée` : "QuickBooks : en attente"}
      </span>
      {!created && q.reason && <span className="text-[#E6412D]" data-testid="qbo-badge-reason">{q.reason}</span>}
      {!created && <button onClick={retry} disabled={busy} className="kr-btn kr-btn-ghost text-xs" data-testid="qbo-retry">Réessayer</button>}
    </div>
  );
}

const EXTRA_PRESETS = (g) => [
  { key: "revision", label: "Ronde de correction supplémentaire", amount: Number(g.extra_revision_fee ?? 0) },
  { key: "photos-10", label: "Photos en surplus (10)", amount: Number(g.extra_photos_block_fee ?? 0) },
  { key: "retouche", label: "Retouche spéciale", amount: 0 },
  { key: "deplacement", label: "Déplacement supplémentaire", amount: 0 },
];

function AdminInvoiceBox({ gallery, lang, refresh, confirmAsk }) {
  const [open, setOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockNote, setUnlockNote] = useState("");
  const [unlockOffline, setUnlockOffline] = useState(false);
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [testAmt, setTestAmt] = useState(gallery.test_amount ?? "");
  const [mode, setMode] = useState(null);
  useEffect(() => { api.get("/stripe/mode").then(r => setMode(r.data)).catch(() => {}); }, []);
  const saveTest = async (val) => {
    try { await api.put(`/galleries/${gallery.id}/test-amount`, { amount: val }); refresh(); toast.success(val ? `Montant de test : ${fmtCAD(val, 2)}` : "Montant de test retiré"); }
    catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
  };
  const testPay = async () => {
    try { const { data } = await api.post(`/galleries/${gallery.id}/checkout`, { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
  };
  const resetPay = async () => {
    try { await api.post(`/galleries/${gallery.id}/reset-payment`); refresh(); toast.success("Séance remise à non payée"); }
    catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
  };
  const add = async () => {
    const v = parseFloat(String(amount).replace(",", "."));
    if (!label.trim() || isNaN(v)) return toast.error(t(lang,"err_generic"));
    const save = async () => {
      try { await api.post(`/galleries/${gallery.id}/lines`, { label, amount: v }); setLabel(""); setAmount(""); setOpen(false); refresh(); toast.success("Supplément ajouté"); }
      catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
    };
    if (gallery.paid) {
      confirmAsk({
        danger: false,
        message: `Ce supplément de ${fmtCAD(v, 2)} (taxes en sus) sera facturé au courtier. Il recevra un courriel et pourra payer en ligne.`,
        confirmLabel: "Facturer le supplément",
        onConfirm: save,
      });
      return;
    }
    await save();
  };
  const del = async (lid) => { try { await api.delete(`/galleries/${gallery.id}/lines/${lid}`); refresh(); } catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); } };
  return (
    <div className="kr-card p-5 mb-8" data-testid="admin-invoice-box">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-display text-lg">{t(lang,"gallery_invoice")} · <span className="gold-text">{fmtCAD(gallery.invoice_total, 2)}</span>{gallery.paid && !(gallery.balance_due > 0) && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-400">{t(lang,"gallery_paid")}</span>}{gallery.paid && gallery.balance_due > 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#141414] text-[#E6412D]" data-testid="admin-balance-badge">Solde à payer : {fmtCAD(gallery.balance_due, 2)}</span>}{gallery.test_amount || gallery.is_test ? <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#D8301C]/20 text-[#D8301C] font-semibold" data-testid="test-badge">TEST</span> : null}</div>
        {!open && <button onClick={() => setOpen(true)} className="kr-btn kr-btn-ghost text-sm" data-testid="btn-add-line">+ {gallery.paid ? "Ajouter un supplément" : t(lang,"add_line")}</button>}
        {!gallery.paid && <button onClick={() => setUnlockOpen(true)} className="kr-btn kr-btn-ghost text-sm" data-testid="btn-manual-unlock">Débloquer le téléchargement</button>}
      </div>
      {unlockOpen && (
        <div className="mt-3 p-3 rounded-lg border border-[rgba(216,48,28,0.35)] space-y-2" data-testid="manual-unlock-form">
          <label className="kr-caption">Note (obligatoire)</label>
          <input value={unlockNote} onChange={e => setUnlockNote(e.target.value)} placeholder="ex. Client de confiance, facturation mensuelle" data-testid="input-unlock-note"/>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={unlockOffline} onChange={e => setUnlockOffline(e.target.checked)} className="w-4 h-4 accent-[#D8301C]" data-testid="input-unlock-offline"/>
            Marquer payée hors ligne
          </label>
          <div className="flex gap-2">
            <button onClick={async () => {
              try {
                await api.put(`/galleries/${gallery.id}/manual-unlock`, { note: unlockNote, mark_paid_offline: unlockOffline });
                setUnlockOpen(false); setUnlockNote(""); refresh(); toast.success("Téléchargement débloqué");
              } catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
            }} className="kr-btn kr-btn-primary text-sm" data-testid="btn-unlock-confirm">Débloquer</button>
            <button onClick={() => setUnlockOpen(false)} className="kr-btn kr-btn-ghost text-sm" data-testid="btn-unlock-cancel">Annuler</button>
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="test-amount-box">        <div className="w-40">
          <label className="kr-caption">Montant de test ($)</label>
          <input type="number" step="0.01" min="0.5" value={testAmt} onChange={e => setTestAmt(e.target.value)} placeholder="ex. 1.00" data-testid="input-test-amount"/>
        </div>
        <button onClick={() => saveTest(parseFloat(String(testAmt).replace(",", ".")) || null)} className="kr-btn kr-btn-primary text-sm" data-testid="save-test-amount">Appliquer</button>
        {gallery.test_amount ? <button onClick={() => { setTestAmt(""); saveTest(null); }} className="kr-btn kr-btn-ghost text-sm" data-testid="clear-test-amount">Retirer</button> : null}
        {mode && (
          <span className={`text-xs px-2 py-1 rounded-full ${mode.mode === "live" ? "bg-emerald-900/40 text-emerald-400" : "bg-[#141414] text-[#E6412D]"}`} data-testid="stripe-mode">
            Stripe : {mode.mode === "live" ? "mode réel" : mode.mode === "test" ? "mode test" : "clés non reconnues"}{mode.webhook_verified ? " · webhook actif" : " · webhook non vérifié"}
          </span>
        )}
        {mode?.mode === "test" && (gallery.test_amount || gallery.is_test) ? (
          <>
            {!gallery.paid && (
              <button onClick={testPay} className="kr-btn kr-btn-primary text-sm" data-testid="btn-test-stripe">
                <DollarSign className="w-4 h-4"/>Tester le paiement Stripe ({fmtCAD(gallery.test_amount || gallery.invoice_total, 2)})
              </button>
            )}
            <button onClick={resetPay} className="kr-btn kr-btn-ghost text-sm" data-testid="btn-reset-payment">Remettre à non payé</button>
          </>
        ) : null}
      </div>
      {open && (
        <div className="mt-3 space-y-2" data-testid="add-line-form">
          <div className="flex flex-wrap gap-2">
            {EXTRA_PRESETS(gallery).map(p => (
              <button key={p.label} type="button" onClick={() => { setLabel(p.label); setAmount(String(p.amount)); }}
                className="kr-btn kr-btn-ghost text-xs" data-testid={`preset-${p.key}`}>{p.label} · {fmtCAD(p.amount, 2)}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-[1fr_10rem_auto_auto] gap-2 items-end">
            <div><label className="kr-caption">{t(lang,"line_label")}</label><input value={label} onChange={e => setLabel(e.target.value)} data-testid="line-label"/></div>
            <div><label className="kr-caption">{t(lang,"line_amount")}</label><input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="line-amount"/></div>
            <button onClick={add} className="kr-btn kr-btn-primary text-sm" data-testid="line-save">OK</button>
            <button onClick={() => setOpen(false)} className="kr-btn kr-btn-ghost text-sm">{t(lang,"back")}</button>
          </div>
        </div>
      )}
      <InvoiceRecap gallery={gallery} lang={lang}/>
      <QboBadge gallery={gallery} refresh={refresh}/>

      {!gallery.paid && (gallery.extra_lines || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">{gallery.extra_lines.map(l => <button key={l.id} onClick={() => del(l.id)} className="text-xs text-red-400 hover:text-red-300" data-testid={`del-line-${l.id}`}>✕ {l.label}</button>)}</div>
      )}
      {(gallery.journal || []).length > 0 && (
        <details className="mt-4 text-xs text-zinc-500" data-testid="journal">
          <summary className="cursor-pointer kr-caption">{t(lang,"journal")} ({gallery.journal.length})</summary>
          <ul className="mt-2 space-y-1">{[...gallery.journal].reverse().map((j, i) => <li key={i}>{fmtDateTime(j.at, lang)} · {j.by} — {j.text}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

function NotReadyBox({ booking, lang, refresh }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const nr = booking.not_ready;
  const save = async (flag) => {
    await api.put(`/bookings/${booking.id}/not-ready`, { not_ready: flag, comment });
    setOpen(false); setComment(""); refresh();
    toast.success(flag ? t(lang,"not_ready_label") : "OK");
  };
  return (
    <div className="mb-6" data-testid="not-ready-box">
      {nr?.flag ? (
        <div className="p-3 rounded-lg bg-red-900/30 border border-red-700/50 text-sm flex justify-between gap-3 flex-wrap" data-testid="not-ready-flag">
          <div><AlertTriangle className="w-4 h-4 inline mr-2 text-red-400"/><span className="font-semibold text-red-300">{t(lang,"not_ready_label")}</span>{nr.comment && <span className="text-zinc-300"> — {nr.comment}</span>}<div className="text-xs text-zinc-500 mt-1">{t(lang,"not_ready_by")} {nr.by} · {fmtDateTime(nr.at, lang)}</div></div>
          <button onClick={() => save(false)} className="text-xs text-zinc-400 hover:text-white" data-testid="not-ready-clear">{t(lang,"not_ready_clear")}</button>
        </div>
      ) : !open ? (
        <button onClick={() => setOpen(true)} className="kr-btn kr-btn-ghost text-sm" data-testid="not-ready-open"><AlertTriangle className="w-4 h-4"/>{t(lang,"not_ready_label")}</button>
      ) : (
        <div className="p-3 rounded-lg bg-[#141414] border border-[rgba(216,48,28,0.35)]">
          <textarea rows="2" placeholder={t(lang,"not_ready_comment")} value={comment} onChange={e => setComment(e.target.value)} data-testid="not-ready-comment"/>
          <div className="flex gap-2 mt-2"><button onClick={() => save(true)} className="kr-btn kr-btn-primary text-sm" data-testid="not-ready-save">{t(lang,"not_ready_save")}</button><button onClick={() => setOpen(false)} className="kr-btn kr-btn-ghost text-sm">{t(lang,"back")}</button></div>
        </div>
      )}
    </div>
  );
}

function GalleryLogin({ lang }) {
  const goLogin = () => {
    startLogin(window.location.pathname + window.location.search);
  };
  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="gallery-login">
      <Shield className="w-12 h-12 text-[#D8301C] mx-auto mb-4"/>
      <h1 className="font-display text-3xl mb-3">{lang === "fr" ? "Connexion requise" : "Sign in required"}</h1>
      <p className="text-zinc-400 mb-6">{lang === "fr" ? "Connectez-vous pour voir cette séance." : "Sign in to view this session."}</p>
      <button onClick={goLogin} className="kr-btn kr-btn-primary" data-testid="gallery-login-btn"><LogIn className="w-4 h-4"/>{lang === "fr" ? "Se connecter avec Google" : "Sign in with Google"}</button>
    </div>
  );
}

function LockboxBox({ booking, lang, refresh }) {
  const [code, setCode] = useState(booking.lockbox_code || "");
  const [editing, setEditing] = useState(false);
  const save = async () => {
    try { await api.put(`/bookings/${booking.id}/lockbox`, { lockbox_code: code }); toast.success(lang === "fr" ? "Code enregistré" : "Code saved"); setEditing(false); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="mb-3 p-3 rounded-lg bg-[#D8301C]/10 border border-[#D8301C]/30 flex flex-wrap items-center gap-3" data-testid="lockbox-box">
      <span className="text-sm font-semibold text-[#D8301C]">{t(lang,"lockbox_code")}</span>
      {editing ? (
        <>
          <input value={code} onChange={e => setCode(e.target.value)} className="!w-40 !py-1 !text-sm" data-testid="lockbox-input" placeholder={lang === "fr" ? "Ex. : 1234" : "e.g. 1234"} />
          <button onClick={save} className="kr-btn kr-btn-primary text-xs !py-1" data-testid="lockbox-save">{lang === "fr" ? "Enregistrer" : "Save"}</button>
        </>
      ) : (
        <>
          <span className="text-sm text-zinc-100" data-testid="lockbox-value">{code || (lang === "fr" ? "Non renseigné" : "Not set")}</span>
          <button onClick={() => setEditing(true)} className="kr-btn kr-btn-ghost text-xs !py-1" data-testid="lockbox-edit">{code ? (lang === "fr" ? "Modifier" : "Edit") : (lang === "fr" ? "Ajouter" : "Add")}</button>
        </>
      )}
    </div>
  );
}

function GalleryPage({ lang }) {
  const { gid } = useParams();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("t") || "";
  const params = token ? { t: token } : {};
  const [data, setData] = useState(null);
  const [uploads, setUploads] = useState({});
  const uploadsRef = useRef({});
  useEffect(() => { uploadsRef.current = uploads; }, [uploads]);
  useEffect(() => {
    const active = Object.values(uploads).some(u => (u?.items || []).some(it => it.status === "uploading" || it.status === "pending"));
    if (!active) return;
    const h = (e) => { e.preventDefault(); e.returnValue = lang === "fr" ? "Un téléversement est en cours. Quitter la page l'interrompra." : "An upload is in progress. Leaving will interrupt it."; return e.returnValue; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [uploads, lang]);
  const MAX_VIDEO = 500 * 1024 * 1024;
  const humanSize = (b) => b >= 1073741824 ? (b / 1073741824).toFixed(2) + " Go" : b >= 1048576 ? (b / 1048576).toFixed(1) + " Mo" : Math.max(1, Math.round(b / 1024)) + " Ko";
  const humanTime = (s) => s == null ? "" : s >= 60 ? `${Math.floor(s / 60)} min ${String(Math.round(s % 60)).padStart(2, "0")} s` : `${Math.round(s)} s`;
  const validateFile = (file) => {
    const isVid = (file.type || "").startsWith("video");
    const isImg = (file.type || "").startsWith("image");
    if (!isVid && !isImg) return lang === "fr" ? "Format refusé" : "Unsupported format";
    if (isVid && file.size > MAX_VIDEO) return lang === "fr" ? "Fichier trop lourd (max 500 Mo)" : "File too large (max 500 MB)";
    return null;
  };
  const patchItem = (dtype, id, patch) => setUploads(u => { const g = u[dtype]; if (!g) return u; return { ...u, [dtype]: { ...g, items: g.items.map(it => it.id === id ? { ...it, ...patch } : it) } }; });
  const uploadItem = async (dtype, item) => {
    const file = item.file;
    const kind = (file.type || "").startsWith("video") ? "video" : "photo";
    const ctype = file.type || "application/octet-stream";
    const st = Date.now();
    patchItem(dtype, item.id, { status: "uploading", pct: 0, error: null, eta: null });
    try {
      const { data: pre } = await api.post(`/galleries/${gid}/upload-url`, { filename: file.name, content_type: ctype, kind, deliverable: dtype, size: file.size });
      await axios.put(pre.upload_url, file, { headers: { "Content-Type": ctype }, onUploadProgress: e => {
        const total = e.total || file.size || 1;
        const pct = Math.round((e.loaded / total) * 100);
        let eta = null;
        if (kind === "video") { const el = (Date.now() - st) / 1000; const rate = e.loaded / (el || 1); eta = rate > 0 ? Math.max(0, (total - e.loaded) / rate) : null; }
        patchItem(dtype, item.id, { pct, eta });
      } });
      const { data: res } = await api.post(`/galleries/${gid}/upload-complete`, { asset_id: pre.asset_id, orig_key: pre.orig_key, filename: file.name, content_type: ctype, kind, deliverable: dtype, ext: pre.ext, section: dtype === "photos" ? (item.section || "photos") : undefined });
      patchItem(dtype, item.id, { status: "done", pct: 100, eta: null, duplicate: !!res?.duplicate });
      return true;
    } catch (err) {
      const reason = !err?.response ? (lang === "fr" ? "Connexion perdue" : "Connection lost") : (err.response.status === 413 ? (lang === "fr" ? "Fichier trop lourd" : "File too large") : (lang === "fr" ? "Échec du téléversement" : "Upload failed"));
      patchItem(dtype, item.id, { status: "failed", error: reason, eta: null });
      return false;
    }
  };
  const retryItem = async (dtype, id) => {
    const it = (uploadsRef.current[dtype]?.items || []).find(x => x.id === id);
    if (!it) return;
    const err = validateFile(it.file);
    if (err) { patchItem(dtype, id, { status: "failed", error: err }); return; }
    await uploadItem(dtype, it); refresh();
  };
  const retryFailed = async (dtype) => {
    const failed = (uploadsRef.current[dtype]?.items || []).filter(x => x.status === "failed");
    for (const it of failed) { const err = validateFile(it.file); if (err) { patchItem(dtype, it.id, { status: "failed", error: err }); continue; } await uploadItem(dtype, it); }
    refresh();
  };
  const clearUploads = (dtype) => setUploads(u => { const n = { ...u }; delete n[dtype]; return n; });
  const [revModal, setRevModal] = useState(null);
  const [revText, setRevText] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [uploadSection, setUploadSection] = useState("photos");
  const [preview, setPreview] = useState(null);
  const [sel, setSel] = useState({ type: null, ids: [] });
  const lastIdxRef = useRef(null);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(location.search).get("tab") || "all");
  const [confirmState, setConfirmState] = useState(null);
  const confirmAsk = (opts) => setConfirmState({ danger: true, ...opts });
  const promptAsk = (opts) => setConfirmState({ withInput: true, inputValue: "", ...opts });
  const changeTab = (tab) => { setActiveTab(tab); const u = new URL(window.location.href); if (tab === "all") u.searchParams.delete("tab"); else u.searchParams.set("tab", tab); window.history.replaceState({}, "", u.toString()); };
  const zipUrl = (sec) => {
    const qs = []; if (sec) qs.push(`section=${encodeURIComponent(sec)}`); if (token) qs.push(`t=${encodeURIComponent(token)}`);
    return `${process.env.REACT_APP_BACKEND_URL}/api/galleries/${gid}/download-zip${qs.length ? "?" + qs.join("&") : ""}`;
  };
  const moveAsset = async (aid, section) => {
    try { await api.put(`/galleries/${gid}/photos/section`, { asset_ids: [aid], section }); refresh(); }
    catch { toast.error(t(lang, "err_generic")); }
  };
  const moveToDeliverable = async (aid, from, to) => {
    try { await api.put(`/galleries/${gid}/assets/move`, { asset_id: aid, from_deliverable: from, to_deliverable: to, section: to === "photos" ? "photos" : undefined }); refresh(); toast.success(lang === "fr" ? "Fichier déplacé" : "File moved"); }
    catch { toast.error(t(lang, "err_generic")); }
  };

  const refresh = useCallback(() => api.get(`/galleries/${gid}`, token ? { params: { t: token } } : {}).then(r => setData(prev => mergeGalleryData(prev, r.data))).catch((e) => { if (e?.response?.status === 403) setForbidden(true); }), [gid, token]);
  useEffect(() => {
    refresh();
    let id = null;
    const start = () => { if (!id) id = setInterval(refresh, 30000); };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => { if (document.hidden) stop(); else { refresh(); start(); } };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [refresh]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const sid = q.get("session_id"); const pay = q.get("payment");
    if (sid && (pay === "success" || pay === "revision_success" || pay === "balance_success")) {
      (async () => { for (let i=0;i<12;i++){ const r=await api.get(`/payments/status/${sid}`).catch(()=>null); if(r?.data?.payment_status==="paid"){ toast.success(t(lang,"gallery_paid")); refresh(); return;} await new Promise(s=>setTimeout(s,2000)); } })();
    }
    if (pay === "cancel") {
      toast.error(lang === "fr" ? "Paiement annulé. Vous pouvez réessayer quand vous voulez." : "Payment cancelled. You can try again anytime.");
      const u = new URL(window.location.href); u.searchParams.delete("payment"); window.history.replaceState({}, "", u.toString());
    }
  }, [location.search, lang, refresh]);

  if (forbidden) return <GalleryLogin lang={lang}/>;
  if (!data) return <div className="p-20 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin inline"/></div>;
  const { gallery, booking, is_team: is_team_real, is_owner } = data;
  const clientView = is_team_real && preview !== null;
  const is_team = is_team_real && !clientView;
  const paid = clientView ? (preview === "paid") : gallery.paid;
  const lightTheme = !is_team;
  const dels = [...gallery.deliverables].sort((a,b)=>DELIVERABLE_ORDER.indexOf(a.type)-DELIVERABLE_ORDER.indexOf(b.type));
  const tabDels = dels;
  const tabList = is_team ? dels : dels.filter(d => (d.assets || []).length > 0);
  const shownDels = activeTab !== "all" ? dels.filter(d => d.type === activeTab) : dels;
  const tabLabel = (ty) => ty === "photos" ? "Photos" : ty === "drone" ? (lang === "fr" ? "Photos drone" : "Drone photos") : ty === "video" ? (lang === "fr" ? "Vidéo" : "Video") : (lang === "fr" ? "Plans 2D" : "2D plans");
  const dLabel = (d) => d.type === "drone" ? (lang === "fr" ? "Photos drone" : "Drone photos") : d.label;
  const secName = (a, dd) => { if (dd.type === "photos") { const s = (gallery.photo_sections || []).find(x => x.id === (a.section || "photos")); return s ? (lang === "fr" ? s.fr : s.en) : "Photos"; } return dLabel(dd); };
  const canDlFor = (dd) => (paid && (clientView ? preview === "paid" : dd.status === "delivered")) || is_team;
  const lightboxImages = shownDels.flatMap(dd => { const isVid = dd.type === "video" || dd.type === "drone"; const grid = isVid ? (dd.assets || []).filter(a => a.kind !== "video") : (dd.assets || []); return grid.map(a => ({ id: a.id, url: a.preview_url || a.original_url, dtype: dd.type, section: secName(a, dd), canDl: canDlFor(dd) })); });
  const openLightbox = (id) => { const idx = lightboxImages.findIndex(x => x.id === id); if (idx >= 0) setLightbox(idx); };

  const uploadFiles = async (dtype, fileList, section) => {
    const sec = dtype === "photos" ? (section || uploadSection || "photos") : undefined;
    const base = Date.now();
    const items = Array.from(fileList).map((file, i) => {
      const err = validateFile(file);
      return { id: `${base}_${i}_${(file.name || "f").replace(/\W/g, "").slice(0, 24)}`, file, name: file.name, size: file.size, kind: (file.type || "").startsWith("video") ? "video" : "photo", section: sec, status: err ? "failed" : "pending", pct: 0, error: err, eta: null };
    });
    setUploads(u => ({ ...u, [dtype]: { items } }));
    for (const it of items) { if (it.status === "pending") await uploadItem(dtype, it); }
    refresh();
  };

  const isSel = (dtype, id) => sel.type === dtype && sel.ids.includes(id);
  const toggleSel = (dtype, id, idx, shift, orderedIds) => {
    setSel(prev => {
      if (prev.type !== dtype) { lastIdxRef.current = idx; return { type: dtype, ids: [id] }; }
      if (shift && lastIdxRef.current != null) {
        const [a, b] = [lastIdxRef.current, idx].sort((x, y) => x - y);
        const range = orderedIds.slice(a, b + 1);
        return { type: dtype, ids: Array.from(new Set([...prev.ids, ...range])) };
      }
      const has = prev.ids.includes(id);
      const ids = has ? prev.ids.filter(x => x !== id) : [...prev.ids, id];
      lastIdxRef.current = idx;
      return ids.length ? { type: dtype, ids } : { type: null, ids: [] };
    });
  };
  const clearSel = () => { lastIdxRef.current = null; setSel({ type: null, ids: [] }); };
  const selectAllSel = (dtype, ids) => { lastIdxRef.current = null; setSel({ type: dtype, ids: [...ids] }); };
  const downloadSelection = () => {
    const qs = [`dtype=${sel.type}`, `ids=${encodeURIComponent(sel.ids.join(","))}`]; if (token) qs.push(`t=${encodeURIComponent(token)}`);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/galleries/${gid}/download-zip?${qs.join("&")}`;
  };
  const moveSelection = async (dest) => {
    try {
      if (dest.startsWith("deliv:")) {
        const to = dest.slice(6);
        await api.post(`/galleries/${gid}/assets/bulk-move`, { asset_ids: sel.ids, from_deliverable: sel.type, to_deliverable: to, section: to === "photos" ? "photos" : undefined });
        const lbl = to === "plans" ? (lang === "fr" ? "Plans 2D et mesures" : "2D plans") : to === "drone" ? "Drone" : "Photos";
        toast.success(`${sel.ids.length} ${lang === "fr" ? "fichiers déplacés vers" : "files moved to"} ${lbl}`);
      } else {
        await api.put(`/galleries/${gid}/photos/section`, { asset_ids: sel.ids, section: dest });
        const s = (gallery.photo_sections || []).find(x => x.id === dest);
        toast.success(`${sel.ids.length} ${lang === "fr" ? "fichiers déplacés vers" : "files moved to"} ${s ? (lang === "fr" ? s.fr : s.en) : dest}`);
      }
      clearSel(); refresh();
    } catch { toast.error(t(lang, "err_generic")); }
  };
  const deleteSelection = () => {
    const dd = dels.find(x => x.type === sel.type);
    const note = dd && dd.status === "delivered" ? `\n\n${lang === "fr" ? "Ces fichiers ont déjà été livrés au courtier." : "These files have already been delivered to the client."}` : "";
    const n = sel.ids.length;
    const msg = `${lang === "fr" ? `Vous allez supprimer ${n} ${n > 1 ? "fichiers" : "fichier"}. Cette action est définitive.` : `You are about to delete ${n} file${n > 1 ? "s" : ""}. This action is permanent.`}${note}`;
    confirmAsk({ message: msg, confirmLabel: lang === "fr" ? "Supprimer" : "Delete", onConfirm: async () => {
      try {
        const { data } = await api.post(`/galleries/${gid}/assets/bulk-delete`, { deliverable: sel.type, asset_ids: sel.ids });
        toast.success(`${data?.deleted ?? n} ${lang === "fr" ? "fichiers supprimés" : "files deleted"}`);
        clearSel(); refresh();
      } catch (e) { toast.error(e.response?.data?.detail || t(lang, "err_generic")); }
    }});
  };

  const deliver = async (dtype) => {
    try {
      const { data: recap } = await api.get(`/galleries/${gid}/recap`, { params: { dtype } });
      const n = dtype === "video" ? recap.videos : recap.photos;
      const unit = dtype === "video"
        ? (n > 1 ? (lang === "fr" ? "vidéos" : "videos") : (lang === "fr" ? "vidéo" : "video"))
        : dtype === "plans" ? (n > 1 ? "plans" : "plan")
        : dtype === "drone" ? (lang === "fr" ? "photos drone" : "drone photos")
        : (lang === "fr" ? "photos" : "photos");
      confirmAsk({
        message: `${t(lang,"gallery_deliver_confirm")}\n\n${recap.client_name} — ${recap.property_address}\n${n} ${unit}`,
        confirmLabel: lang === "fr" ? "Livrer" : "Deliver",
        onConfirm: async () => {
          try { await api.post(`/galleries/${gid}/deliver`, { deliverable: dtype, origin_url: window.location.origin }); toast.success(t(lang,"gallery_delivered")); refresh(); }
          catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
        }
      });
    } catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
  };

  const downloadAsset = async (dtype, aid) => {
    try {
      const { data } = await api.get(`/galleries/${gid}/download/${dtype}/${aid}`, token ? { params: { t: token } } : {});
      window.location.href = data.url;
    } catch (e) {
      if (e.response?.status === 402) toast.error(t(lang, "gallery_pay_now"));
      else toast.error(t(lang, "err_generic"));
    }
  };

  // Mobile: hand the real file to the phone so it lands in the photo album
  const canShareFiles = typeof navigator !== "undefined" && !!navigator.canShare;
  const saveToPhotos = async (dtype, aid) => {
    try {
      const { data } = await api.get(`/galleries/${gid}/download/${dtype}/${aid}`, token ? { params: { t: token } } : {});
      const res = await fetch(data.url);
      const blob = await res.blob();
      const name = (data.url.split("/").pop() || "kreatif").split("?")[0];
      const file = new File([blob], name, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) await navigator.share({ files: [file] });
      else window.location.href = data.url;
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(t(lang, "err_generic"));
    }
  };

  const delAsset = (dtype, aid) => confirmAsk({
    message: t(lang, "gallery_delete_confirm"), confirmLabel: lang === "fr" ? "Supprimer" : "Delete",
    onConfirm: async () => {
      try { await api.delete(`/galleries/${gid}/deliverables/${dtype}/asset/${aid}`); toast.success(lang === "fr" ? "Fichier supprimé" : "File deleted"); refresh(); }
      catch (e) { toast.error(e.response?.data?.detail || t(lang, "err_generic")); }
    }
  });

  const downloadDeliverable = (dtype) => {
    const qs = [`dtype=${encodeURIComponent(dtype)}`]; if (token) qs.push(`t=${encodeURIComponent(token)}`);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/galleries/${gid}/download-zip?${qs.join("&")}`;
  };

  const approveAndDownload = async (dtype) => {
    try {
      await api.post(`/galleries/${gid}/deliverables/${dtype}/approve${token ? `?t=${token}` : ""}`, { origin_url: window.location.origin });
      downloadDeliverable(dtype);
      toast.success(lang === "fr" ? "Vidéo approuvée — téléchargement en cours" : "Video approved — download starting");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
  };

  const payBalance = async () => {
    try { const { data } = await api.post(`/galleries/${gid}/balance-checkout${token ? `?t=${token}` : ""}`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
  };

  const pay = async () => {    if (clientView) { toast(lang === "fr" ? "Mode essai — le paiement est désactivé ici." : "Trial mode — payment is disabled here."); return; }
    try { const { data: d } = await api.post(`/galleries/${gid}/checkout`, { origin_url: window.location.origin }, { params }); window.location.href = d.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || t(lang,"err_generic")); }
  };

  const reportProblem = (dtype) => {
    if (clientView) { toast(lang === "fr" ? "Aperçu — action désactivée" : "Preview — action disabled"); return; }
    promptAsk({ message: t(lang, "gallery_report_prompt"), confirmLabel: lang === "fr" ? "Envoyer" : "Send", onConfirm: async (c) => {
      if (!c || !c.trim()) return;
      try { await api.post(`/galleries/${gid}/deliverables/${dtype}/report`, { comment: c }, { params }); toast.success(t(lang, "gallery_report_sent")); refresh(); }
      catch (e) { toast.error(e.response?.data?.detail || t(lang, "err_generic")); }
    }});
  };

  const submitRevision = async () => {
    if (clientView) { toast(lang === "fr" ? "Aperçu — action désactivée" : "Preview — action disabled"); return; }
    if (!revText.trim()) return;
    const dtype = revModal.dtype;
    try {
      await api.post(`/galleries/${gid}/deliverables/${dtype}/revision`, { comment: revText }, { params });
      toast.success(t(lang,"gallery_revision_sent")); setRevModal(null); refresh();
    } catch (e) {
      if (e.response?.status === 402) {
        setRevModal(null);
        confirmAsk({
          message: fill(t(lang,"gallery_extra_round_confirm"), { fee: `${gallery.extra_revision_fee} $` }),
          confirmLabel: lang === "fr" ? "Continuer vers le paiement" : "Continue to payment",
          onConfirm: async () => {
            try { const { data: d } = await api.post(`/galleries/${gid}/revision-checkout`, { origin_url: window.location.origin }, { params }); window.location.href = d.checkout_url; }
            catch (er) { toast.error(er.response?.data?.detail || t(lang,"err_generic")); }
          }
        });
      } else toast.error(e.response?.data?.detail || t(lang,"err_generic"));
    }
  };

  const statusBadge = (st) => st === "delivered"
    ? <span className="text-xs px-2 py-1 rounded-full bg-emerald-900/40 text-emerald-400" data-testid="badge-delivered">{t(lang,"gallery_status_delivered")}</span>
    : <span className="text-xs px-2 py-1 rounded-full bg-zinc-800 text-zinc-400" data-testid="badge-production">{t(lang,"gallery_status_production")}</span>;

  return (
    <div className={lightTheme ? "kr-gallery-light min-h-screen" : ""}>
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="gallery-page">
      {is_team_real && preview === null && (
        <button onClick={() => setPreview("unpaid")} className="kr-btn kr-btn-ghost text-sm mb-4" data-testid="view-as-client"><Users className="w-4 h-4"/>{lang === "fr" ? "Voir comme le courtier" : "View as client"}</button>
      )}
      {clientView && (
        <div className="mb-4 p-3 rounded-lg border flex flex-wrap items-center gap-3" style={{ background: "rgba(216,48,28,0.08)", borderColor: "rgba(216,48,28,0.4)" }} data-testid="preview-banner">
          <span className="text-sm font-semibold text-[#D8301C]">{lang === "fr" ? "Mode essai — vue du courtier" : "Trial mode — client view"}</span>
          <span className="text-xs text-zinc-600 w-full order-last" data-testid="trial-banner-note">{lang === "fr" ? "Vous pouvez tout tester, rien n'est enregistré." : "You can try everything, nothing is saved."}</span>
          <div className="flex items-center gap-1 text-xs">
            <button onClick={() => setPreview("unpaid")} className={`px-2 py-1 rounded ${preview === "unpaid" ? "bg-[#D8301C] text-white" : "bg-black/5 text-zinc-700"}`} data-testid="preview-unpaid">{lang === "fr" ? "Avant paiement" : "Before payment"}</button>
            <button onClick={() => setPreview("paid")} className={`px-2 py-1 rounded ${preview === "paid" ? "bg-[#D8301C] text-white" : "bg-black/5 text-zinc-700"}`} data-testid="preview-paid">{lang === "fr" ? "Après paiement" : "After payment"}</button>
          </div>
          <button onClick={() => setPreview(null)} className="kr-btn kr-btn-ghost text-xs ml-auto" data-testid="exit-preview">{lang === "fr" ? "Revenir à la vue équipe" : "Back to team view"}</button>
        </div>
      )}
      <div className="kr-caption mb-2">{t(lang,"gallery_caption")}</div>
      <h1 className="font-display text-3xl mb-2">{booking?.property_address}{(gallery.is_test || gallery.test_amount) && <span className="ml-3 align-middle text-xs px-2 py-1 rounded-full bg-[#D8301C]/20 text-[#D8301C] font-semibold" data-testid="gallery-test-badge">TEST</span>}</h1>
      {(is_team || is_owner) && booking?.access_mode === "keybox" && booking?.id && <LockboxBox booking={booking} lang={lang} refresh={refresh}/>}
      {is_team && booking?.access_mode && (
        <div className="mb-3 flex flex-wrap gap-2 text-xs" data-testid="session-access-mode">
          <span className={`px-3 py-1 rounded-full font-semibold ${booking.access_mode === "owner_present" ? "bg-[#141414] text-[#E6412D]" : "bg-[#D8301C]/15 text-[#D8301C]"}`}>{t(lang,"access_mode")} : {t(lang, booking.access_mode === "owner_present" ? "access_owner" : booking.access_mode === "broker_present" ? "access_broker" : "access_keybox")}</span>
          {booking.onsite_min ? <span className="px-3 py-1 rounded-full bg-zinc-800 text-zinc-300">{fmtDur(booking.onsite_min, lang)} {lang === "fr" ? "sur place" : "on site"}</span> : null}
          {booking.travel?.duration_min ? <span className="px-3 py-1 rounded-full bg-zinc-800 text-zinc-300">{lang === "fr" ? "Trajet" : "Travel"} {booking.travel.duration_min} min · {booking.travel.distance_km} km</span> : null}
          {booking.video_format ? <span className="px-3 py-1 rounded-full bg-zinc-800 text-zinc-300" data-testid="gallery-video-format">{lang === "fr" ? "Format vidéo" : "Video format"} : {vfLabel(booking.video_format, lang)}</span> : null}
        </div>
      )}
      {is_team && booking?.id && <NotReadyBox booking={booking} lang={lang} refresh={refresh}/>}
      {is_team && booking && booking.address_validated === false && (
        <div className="mb-4 p-3 rounded-lg bg-[#141414] border border-[rgba(216,48,28,0.35)] text-sm text-[#E6412D] flex gap-2" data-testid="address-flag"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>{t(lang,"addr_flag_team")}</div>
      )}
      <div className="text-sm text-zinc-400 mb-6">{t(lang,"gallery_status")}: <span className="gold-text">{t(lang,`gallery_proj_${gallery.status}`)}</span></div>

      {!paid && !is_team && (gallery.status === "awaiting_payment" || clientView) && (
        <div className="kr-card p-4 mb-6 text-sm" data-testid="pay-note">
          {lang === "fr" ? "Vous pouvez tout consulter. Les téléchargements se débloquent dès le paiement de votre facture, en bas de page." : "You can browse everything. Downloads unlock as soon as your invoice, at the bottom of this page, is paid."}
        </div>
      )}
      {is_team && <AdminInvoiceBox gallery={gallery} lang={lang} refresh={refresh} confirmAsk={confirmAsk}/>}

      {tabList.length > 0 && (
        <div className={`sticky top-0 z-30 -mx-4 sm:-mx-8 px-4 sm:px-8 py-2 mb-4 backdrop-blur border-b flex flex-wrap gap-2 ${is_team ? "bg-[#0A0A0A]/95 border-[rgba(216,48,28,0.35)]" : "bg-white/95 border-[#e7e7ea]"}`} data-testid="gallery-tabs">
          <button onClick={() => changeTab("all")} className={`text-sm px-3 py-1 rounded-full transition-colors ${activeTab === "all" ? "bg-[#D8301C] text-white" : is_team ? "bg-white/10 text-zinc-300 hover:bg-white/20" : "bg-black/5 text-zinc-700 hover:bg-black/10"}`} data-testid="tab-all">{lang === "fr" ? "Tout" : "All"} ({dels.reduce((n, d) => n + (d.assets || []).length, 0)})</button>
          {tabList.map(d => (
            <button key={d.type} onClick={() => changeTab(d.type)} className={`text-sm px-3 py-1 rounded-full transition-colors ${activeTab === d.type ? "bg-[#D8301C] text-white" : is_team ? "bg-white/10 text-zinc-300 hover:bg-white/20" : "bg-black/5 text-zinc-700 hover:bg-black/10"}`} data-testid={`tab-${d.type}`}>{tabLabel(d.type)} ({(d.assets || []).length})</button>
          ))}
        </div>
      )}
      <div className="space-y-6">
        {shownDels.map(d => (
          <div key={d.type} className="kr-card p-5" data-testid={`deliverable-${d.type}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="font-display text-lg">{dLabel(d)}</div>
              <div className="flex items-center gap-3">
                {d.status === "in_production" && d.eta_label && <span className="text-xs text-zinc-500">{d.eta_label}</span>}
                {clientView && d.status !== "delivered" && <span className="text-xs px-2 py-1 rounded-full bg-white text-[#E6412D]" data-testid={`not-delivered-${d.type}`}>{lang === "fr" ? "Pas encore livré au courtier" : "Not yet delivered"}</span>}
                {statusBadge(d.status)}
              </div>
            </div>

            {d.status === "in_production" && !is_team && !clientView && (
              <div className="text-sm text-zinc-400" data-testid={`coming-${d.type}`}>{t(lang,"gallery_coming_soon").replace("{label}", dLabel(d))}</div>
            )}

            {(d.status === "delivered" || is_team || clientView) && (() => {
              const isVid = d.type === "video" || d.type === "drone";
              const vids = (d.assets || []).filter(a => a.kind === "video");
              const gridAssets = isVid ? (d.assets || []).filter(a => a.kind !== "video") : (d.assets || []);
              const showEmpty = isVid ? (vids.length === 0 && gridAssets.length === 0) : (gridAssets.length === 0);
              return (<>
              {isVid && vids.length > 0 && (
                <VideoReview gid={gid} token={token} d={d} is_team={is_team} paid={paid} lang={lang} comments={gallery.comments || []} refresh={refresh} extraFee={gallery.extra_revision_fee} uploadFiles={uploadFiles} downloadAsset={downloadAsset} preview={clientView} confirmAsk={confirmAsk} promptAsk={promptAsk} />
              )}
              {is_team && gridAssets.length > 0 && (
                <button onClick={() => selectAllSel(d.type, gridAssets.map(x => x.id))} className="kr-btn kr-btn-ghost text-xs mb-2" data-testid={`select-all-${d.type}`}>{lang === "fr" ? "Tout sélectionner" : "Select all"}</button>
              )}
              {gridAssets.length > 0 && (
              <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 ${isVid && vids.length > 0 ? "mt-4" : ""}`}>
                {gridAssets.map((a, idx) => {
                  const canDl = (paid && (clientView ? preview === "paid" : d.status === "delivered")) || is_team;
                  return (
                    <div key={a.id} className={`kr-card overflow-hidden relative ${isSel(d.type, a.id) ? "ring-2 ring-[#D8301C]" : ""}`} data-testid={`asset-${a.id}`}>
                      {is_team && (
                        <label className="absolute top-1 left-1 z-10 bg-black/40 rounded p-0.5 leading-none" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={isSel(d.type, a.id)} onChange={() => {}} onClick={e => toggleSel(d.type, a.id, idx, e.shiftKey, gridAssets.map(x => x.id))} className="w-4 h-4 accent-[#D8301C] cursor-pointer align-middle" data-testid={`select-${a.id}`} />
                        </label>
                      )}
                      {a.preview_status === "processing" ? (
                        <div className="w-full h-40 flex items-center justify-center text-xs text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mr-1"/>{t(lang,"gallery_processing")}</div>
                      ) : a.kind === "video" ? (
                        <video src={canDl ? (a.original_url || a.preview_url) : a.preview_url} controls controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={e => e.preventDefault()} className="w-full h-40 object-cover"/>
                      ) : (
                        <img src={a.preview_url || a.original_url} alt="" onClick={() => openLightbox(a.id)} className="w-full h-40 object-cover cursor-pointer"/>
                      )}
                      {canDl && (
                        <button onClick={() => downloadAsset(d.type, a.id)} className="block w-full p-2 text-center text-xs gold-text hover:underline" data-testid={`download-${a.id}`}>{t(lang,"gallery_download")}</button>
                      )}
                      {canDl && canShareFiles && !is_team && (
                        <button onClick={() => saveToPhotos(d.type, a.id)} className="block w-full px-2 pb-2 text-center text-xs text-zinc-400 hover:text-[#D8301C]" data-testid={`save-photos-${a.id}`}>
                          {lang === "fr" ? "Enregistrer dans mes photos" : "Save to my photos"}
                        </button>
                      )}
                      {is_team && a.uploaded_by_name && <div className="px-2 pb-1 text-[10px] text-zinc-500">{a.uploaded_by_name}</div>}
                      {is_team && a.kind !== "video" && (d.type === "photos" || d.type === "plans" || d.type === "drone") && (() => {
                        const types = (gallery.deliverables || []).map(x => x.type);
                        const dl = (ty) => ty === "plans" ? (lang === "fr" ? "Plans 2D et mesures" : "2D plans & measures") : ty === "drone" ? (lang === "fr" ? "Photos drone" : "Drone photos") : "Photos";
                        const crossTargets = ["photos", "plans", "drone"].filter(ty => ty !== d.type && types.includes(ty));
                        const val = d.type === "photos" ? (a.section || "photos") : `deliv:${d.type}`;
                        const onCh = (v) => { if (v.startsWith("deliv:")) moveToDeliverable(a.id, d.type, v.slice(6)); else moveAsset(a.id, v); };
                        return (
                          <select value={val} onChange={e => onCh(e.target.value)} className="block w-full text-[10px] bg-transparent border-t border-[rgba(216,48,28,0.35)] px-1 py-1" data-testid={`move-${a.id}`}>
                            {d.type === "photos"
                              ? <optgroup label={lang === "fr" ? "Section photo" : "Photo section"}>{(gallery.photo_sections || []).map(s => <option key={s.id} value={s.id}>{lang === "fr" ? s.fr : s.en}</option>)}</optgroup>
                              : <option value={`deliv:${d.type}`}>{dl(d.type)}</option>}
                            {crossTargets.length > 0 && <optgroup label={lang === "fr" ? "Déplacer vers le livrable" : "Move to deliverable"}>{crossTargets.map(ty => <option key={ty} value={`deliv:${ty}`}>{dl(ty)}</option>)}</optgroup>}
                          </select>
                        );
                      })()}
                      {is_team && <button onClick={() => delAsset(d.type, a.id)} className="block w-full p-1 text-center text-[10px] text-red-400" data-testid={`del-${a.id}`}>{t(lang,"gallery_delete")}</button>}
                    </div>
                  );
                })}
              </div>
              )}
              {showEmpty && <div className="text-zinc-400 text-sm" data-testid={`empty-${d.type}`}>{t(lang,"gallery_empty")}</div>}
              </>);
            })()}

            {d.type === "photos" && (gallery.photo_sections||[]).length > 0 && (
              <div className="mb-4 kr-card p-3" data-testid="photo-sections-bar">
                {gallery.photo_included != null && <div className="text-xs text-zinc-400 mb-2" data-testid="photo-count-indicator">{gallery.photo_counted} {lang==="fr"?"photos comptées":"photos counted"} / {gallery.photo_included} {lang==="fr"?"incluses":"included"}{gallery.photo_surplus>0?` · ${gallery.photo_surplus} ${lang==="fr"?"en surplus":"over"}`:""}</div>}
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-zinc-500">{lang==="fr"?"Sections :":"Sections:"}</span>
                  {(gallery.photo_sections||[]).filter(s=>s.n>0).map(s=>(<span key={s.id} className="text-xs px-2 py-1 rounded-full bg-[#D8301C]/10 text-[#D8301C]" data-testid={`sec-chip-${s.id}`}>{(lang==="fr"?s.fr:s.en)} ({s.n})</span>))}
                </div>
                {(d.assets || []).length > 0 && ((paid && (clientView ? preview==="paid" : d.status==="delivered")) || is_team) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <a href={zipUrl()} className="kr-btn kr-btn-primary text-xs" data-testid="dl-all">{lang==="fr"?"Tout télécharger":"Download all"}</a>
                    {(gallery.photo_sections||[]).filter(s=>s.n>0).map(s=>(<a key={s.id} href={zipUrl(s.id)} className="kr-btn kr-btn-ghost text-xs" data-testid={`dl-${s.id}`}>{lang==="fr"?s.fr:s.en}</a>))}
                  </div>
                )}
                {is_team && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-zinc-500">{lang==="fr"?"Déposer dans :":"Upload to:"}</span>
                    <select value={uploadSection} onChange={e=>setUploadSection(e.target.value)} className="text-xs bg-transparent border border-[rgba(216,48,28,0.35)] rounded px-2 py-1" data-testid="upload-section-select">
                      {(gallery.photo_sections||[]).map(s=><option key={s.id} value={s.id}>{lang==="fr"?s.fr:s.en}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

            {is_team && (
              <div className="mt-4">
                <div
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); if (e.dataTransfer.files?.length) uploadFiles(d.type, e.dataTransfer.files, d.type === "photos" ? uploadSection : undefined); }}
                  className="border-2 border-dashed border-[#D8301C]/50 rounded-xl p-6 text-center hover:border-[#D8301C] transition-colors"
                  data-testid={`dropzone-${d.type}`}>
                  <Upload className="w-6 h-6 mx-auto mb-2 text-[#D8301C]"/>
                  <label className="cursor-pointer inline-block">
                    <span className="gold-text font-medium">{t(lang,"gallery_dropzone_title")}</span>
                    <span className="block text-xs text-zinc-500 mt-1">{t(lang,"gallery_dropzone_hint")} · {dLabel(d)}</span>
                    <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={e => e.target.files.length && uploadFiles(d.type, e.target.files, d.type === "photos" ? uploadSection : undefined)} data-testid={`upload-${d.type}`}/>
                  </label>
                </div>
                {uploads[d.type] && (() => {
                  const items = uploads[d.type].items || [];
                  const total = items.length;
                  const doneCount = items.filter(i => i.status === "done").length;
                  const failCount = items.filter(i => i.status === "failed").length;
                  const activeCount = items.filter(i => i.status === "uploading" || i.status === "pending").length;
                  const overall = total ? Math.round(items.reduce((s, i) => s + (i.status === "done" ? 100 : i.status === "failed" ? 0 : (i.pct || 0)), 0) / total) : 0;
                  const allDone = activeCount === 0;
                  return (
                    <div className="mt-3 p-3 rounded-xl border border-[rgba(216,48,28,0.35)] bg-[#141414]" data-testid={`upload-tracker-${d.type}`}>
                      {!allDone && (<>
                        <div className="text-xs text-zinc-300 mb-1" data-testid={`upload-overall-${d.type}`}>{lang === "fr" ? `Téléversement en cours : ${doneCount} sur ${total} fichiers, ${overall} %` : `Uploading: ${doneCount} of ${total} files, ${overall}%`}</div>
                        <div className="w-full h-2 rounded-full bg-[rgba(216,48,28,0.35)] overflow-hidden mb-2"><div className="h-full rounded-full transition-all" style={{ width: `${overall}%`, background: "#D8301C" }} /></div>
                      </>)}
                      {allDone && (
                        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                          <div className={`text-xs font-medium ${failCount ? "text-[#E6412D]" : "text-emerald-400"}`} data-testid={`upload-summary-${d.type}`}>{failCount === 0 ? (lang === "fr" ? `${doneCount} fichiers téléversés avec succès.` : `${doneCount} files uploaded successfully.`) : (lang === "fr" ? `${doneCount} fichiers téléversés, ${failCount} échecs.` : `${doneCount} files uploaded, ${failCount} failed.`)}</div>
                          <div className="flex gap-2">
                            {failCount > 0 && <button onClick={() => retryFailed(d.type)} className="kr-btn kr-btn-primary text-xs" data-testid={`retry-failed-${d.type}`}>{lang === "fr" ? "Réessayer les échecs" : "Retry failed"}</button>}
                            <button onClick={() => clearUploads(d.type)} className="kr-btn kr-btn-ghost text-xs" data-testid={`upload-dismiss-${d.type}`}>{lang === "fr" ? "Fermer" : "Dismiss"}</button>
                          </div>
                        </div>
                      )}
                      <div className="space-y-1 max-h-56 overflow-auto mt-1">
                        {items.map(it => (
                          <div key={it.id} className="flex items-start gap-2 text-[11px]" data-testid={`upload-item-${it.id}`}>
                            <span className="shrink-0 mt-0.5">{it.status === "done" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : it.status === "failed" ? <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> : it.status === "uploading" ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#D8301C]" /> : <Clock className="w-3.5 h-3.5 text-zinc-500" />}</span>
                            <span className="flex-1 min-w-0">
                              <span className="flex justify-between gap-2"><span className="truncate text-zinc-300">{it.name}</span><span className="text-zinc-500 shrink-0">{humanSize(it.size)}</span></span>
                              {it.status === "uploading" && (<span className="block mt-0.5"><span className="block w-full h-1 rounded-full bg-[rgba(216,48,28,0.35)] overflow-hidden"><span className="block h-full rounded-full" style={{ width: `${it.pct || 0}%`, background: "#D8301C" }} /></span><span className="text-[10px] text-zinc-500">{it.pct || 0}%{it.kind === "video" && it.eta != null ? ` · ${lang === "fr" ? "temps restant estimé" : "est. time left"} ~${humanTime(it.eta)}` : ""}</span></span>)}
                              {it.status === "pending" && <span className="text-[10px] text-zinc-500">{lang === "fr" ? "En attente" : "Pending"}</span>}
                              {it.status === "failed" && <span className="text-[10px] text-red-400">{it.error} <button onClick={() => retryItem(d.type, it.id)} className="underline text-[#D8301C]" data-testid={`retry-${it.id}`}>{lang === "fr" ? "Réessayer" : "Retry"}</button></span>}
                              {it.status === "done" && it.duplicate && <span className="text-[10px] text-zinc-500">{lang === "fr" ? "Doublon ignoré" : "Duplicate skipped"}</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {(d.assets||[]).length > 0 && (
                  <button onClick={() => deliver(d.type)} className="kr-btn kr-btn-primary text-sm mt-3" data-testid={`deliver-${d.type}`}><CheckCircle2 className="w-4 h-4"/>{t(lang,"gallery_deliver_to_broker")}</button>
                )}
              </div>
            )}

            {!is_team && d.status === "delivered" && (
              <div className="mt-4">
                {["video","drone"].includes(d.type) ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      {paid && d.type === "video" && (
                        d.approved
                          ? <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid="video-approved-badge">
                              {lang === "fr" ? "Approuvée" : "Approved"} · {new Date(d.approved_at).toLocaleDateString(lang==="en"?"en-CA":"fr-CA")}
                            </span>
                          : <button onClick={() => approveAndDownload(d.type)} className="kr-btn kr-btn-primary text-sm" data-testid="approve-download-video">
                              <Download className="w-4 h-4"/>{lang === "fr" ? "Approuver et télécharger" : "Approve and download"}
                            </button>
                      )}
                      <button onClick={() => { setRevText(""); setRevModal({ dtype: d.type }); }} className="kr-btn kr-btn-ghost text-sm" data-testid={`revision-${d.type}`}>
                        {(gallery.rounds_left > 0 && !d.approved) ? t(lang,"gallery_request_revision") : `${t(lang,"gallery_extra_round")} (${gallery.extra_revision_fee} $)`}
                      </button>
                      {gallery.rounds_left > 0 && !d.approved && (
                        <span className="text-xs text-zinc-500">
                          {gallery.revision_texts?.project || t(lang,"gallery_included_round")}
                          {d.revision_deadline ? ` ${t(lang,"gallery_until")} ${new Date(d.revision_deadline).toLocaleDateString(lang==="en"?"en-CA":"fr-CA")}` : ""}
                        </span>
                      )}
                    </div>
                    {paid && d.type === "video" && !d.approved && (
                      <p className="text-xs text-zinc-500" data-testid="approve-notice">
                        {lang === "fr"
                          ? "En téléchargeant, vous approuvez la vidéo. Toute modification après l'approbation est facturée."
                          : "By downloading, you approve the video. Any change after approval is billed."}
                      </p>
                    )}
                  </div>
                ) : (
                  <button onClick={() => reportProblem(d.type)} className="kr-btn kr-btn-ghost text-sm" data-testid={`report-${d.type}`}>{t(lang,"gallery_report_problem")}</button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {!is_team && (
        <div className="kr-card p-6 mt-8" data-testid="client-invoice-box">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="font-display text-lg">{t(lang,"gallery_invoice")}</div>
            {paid && !(gallery.balance_due > 0)
              ? <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid="invoice-paid-badge">{lang === "fr" ? "Payée" : "Paid"}</span>
              : paid
                ? <div className="text-right" data-testid="invoice-balance-badge"><div className="text-xs text-zinc-500">{lang === "fr" ? "Solde à payer" : "Balance due"}</div><div className="gold-text text-2xl font-display">{fmtCAD(gallery.balance_due, 2)}</div></div>
                : <div className="gold-text text-2xl font-display" data-testid="invoice-total">{fmtCAD(gallery.invoice_total, 2)}</div>}
          </div>
          {gallery.test_amount ? <div className="text-xs mt-1 text-[#D8301C] font-semibold" data-testid="invoice-test-badge">{lang === "fr" ? `Montant de test : ${fmtCAD(gallery.test_amount, 2)}` : `Test amount: ${fmtCAD(gallery.test_amount, 2)}`}</div> : null}
          <InvoiceRecap gallery={gallery} lang={lang}/>
          {paid && gallery.balance_due > 0 && (
            <div className="mt-4 p-3 rounded-lg border border-[#D8301C]/40 bg-[#D8301C]/5 text-sm" data-testid="balance-box">
              <div className="font-semibold text-[#D8301C]">{lang === "fr" ? "Solde à payer" : "Balance due"} : {fmtCAD(gallery.balance_due, 2)}</div>
              <ul className="mt-1 text-xs text-zinc-600">
                {(gallery.balance_lines || []).map(l => <li key={l.id}>{l.label} — {fmtCAD(l.amount, 2)}</li>)}
              </ul>
            </div>
          )}
          {paid && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="paid-downloads">
              {dels.filter(d => (d.assets || []).length > 0 && d.status === "delivered").map(d => (
                <button key={d.type} onClick={() => downloadDeliverable(d.type)} className="kr-btn kr-btn-primary text-sm" data-testid={`download-all-${d.type}`}>
                  <Download className="w-4 h-4"/>{tabLabel(d.type)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {paid && !is_team && gallery.balance_due > 0 && (
        <>
          <div className="h-24" />
          <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-white/95 backdrop-blur border-[#e7e7ea]" data-testid="sticky-balance-bar">
            <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-zinc-700">
                {lang === "fr" ? "Solde à payer : " : "Balance due: "}
                <span className="font-display text-lg text-[#D8301C]" data-testid="sticky-balance-total">{fmtCAD(gallery.balance_due, 2)}</span>
              </div>
              <button onClick={payBalance} className="kr-btn kr-btn-primary text-sm" data-testid="btn-pay-balance"><DollarSign className="w-4 h-4"/>{lang === "fr" ? "Payer le solde" : "Pay the balance"}</button>
            </div>
          </div>
        </>
      )}

      {!paid && !is_team && gallery.status === "awaiting_payment" && (        <>
          <div className="h-24" />
          <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-white/95 backdrop-blur border-[#e7e7ea]" data-testid="sticky-pay-bar">
            <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-zinc-700">
                {lang === "fr" ? "Solde à payer : " : "Balance due: "}
                <span className="font-display text-lg text-[#D8301C]" data-testid="sticky-pay-total">{fmtCAD(gallery.invoice_total, 2)}</span>
              </div>
              <button onClick={pay} className="kr-btn kr-btn-primary text-sm" data-testid="btn-pay"><DollarSign className="w-4 h-4"/>{lang === "fr" ? "Payer et télécharger" : "Pay and download"}</button>
            </div>
          </div>
        </>
      )}

      {lightbox !== null && lightboxImages[lightbox] && (
        <Lightbox images={lightboxImages} index={lightbox} setIndex={setLightbox} onClose={() => setLightbox(null)} onDownload={(it) => downloadAsset(it.dtype, it.id)} lang={lang} />
      )}
      {revModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setRevModal(null)}>
          <div className="kr-card p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-3">{t(lang,"gallery_request_revision")}</h3>
            <textarea rows="5" value={revText} onChange={e => setRevText(e.target.value)} placeholder={t(lang,"gallery_revision_placeholder")} data-testid="revision-textarea"/>
            <p className="text-sm text-[#E6412D] mt-3">{gallery.revision_texts?.confirm || t(lang,"gallery_confirm_text")}</p>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setRevModal(null)} className="kr-btn kr-btn-ghost" data-testid="revision-review">{t(lang,"gallery_review_request")}</button>
              <button onClick={submitRevision} className="kr-btn kr-btn-primary" data-testid="revision-submit">{t(lang,"gallery_confirm_send")}</button>
            </div>
          </div>
        </div>
      )}
      {confirmState && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="confirm-modal" onClick={() => setConfirmState(null)}>
          <div className="kr-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="text-sm whitespace-pre-line mb-4">{confirmState.message}</div>
            {confirmState.withInput && (
              <textarea autoFocus value={confirmState.inputValue} onChange={e => setConfirmState(s => ({ ...s, inputValue: e.target.value }))} className="w-full mb-4 text-sm" rows={3} data-testid="confirm-input" />
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmState(null)} className="kr-btn kr-btn-ghost text-sm" data-testid="confirm-cancel">{lang === "fr" ? "Annuler" : "Cancel"}</button>
              <button onClick={() => { const cs = confirmState; setConfirmState(null); if (cs.onConfirm) cs.onConfirm(cs.withInput ? cs.inputValue : undefined); }} className="kr-btn kr-btn-primary text-sm" data-testid="confirm-ok">{confirmState.confirmLabel || (lang === "fr" ? "Confirmer" : "Confirm")}</button>
            </div>
          </div>
        </div>
      )}
      {is_team && sel.ids.length > 0 && (<>
        <div className="h-24" />
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#141414] border-t border-[rgba(216,48,28,0.35)]" data-testid="selection-bar">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
            <span className="text-sm font-semibold text-zinc-100 shrink-0" data-testid="selection-count">{sel.ids.length} {lang === "fr" ? (sel.ids.length > 1 ? "fichiers sélectionnés" : "fichier sélectionné") : (sel.ids.length > 1 ? "files selected" : "file selected")}</span>

            <div className="hidden sm:flex items-center gap-3 flex-1 min-w-0">
              <button onClick={downloadSelection} className="kr-btn kr-btn-ghost text-sm" data-testid="sel-download">{lang === "fr" ? "Télécharger la sélection" : "Download selection"}</button>
              <select onChange={e => { if (e.target.value) moveSelection(e.target.value); e.target.value = ""; }} className="text-sm bg-[#141414] border border-[rgba(216,48,28,0.35)] rounded px-2 py-1 text-zinc-100" data-testid="sel-move" defaultValue="">
                <option value="">{lang === "fr" ? "Déplacer vers…" : "Move to…"}</option>
                {sel.type === "photos" && <optgroup label={lang === "fr" ? "Section photo" : "Photo section"}>{(gallery.photo_sections || []).map(s => <option key={s.id} value={s.id}>{lang === "fr" ? s.fr : s.en}</option>)}</optgroup>}
                <optgroup label={lang === "fr" ? "Livrable" : "Deliverable"}>{["photos", "plans", "drone"].filter(ty => ty !== sel.type && (gallery.deliverables || []).some(x => x.type === ty)).map(ty => <option key={ty} value={`deliv:${ty}`}>{ty === "plans" ? (lang === "fr" ? "Plans 2D et mesures" : "2D plans & measures") : ty === "drone" ? (lang === "fr" ? "Photos drone" : "Drone photos") : "Photos"}</option>)}</optgroup>
              </select>
              <button onClick={deleteSelection} className="kr-btn kr-btn-ghost text-sm text-red-400" data-testid="sel-delete">{lang === "fr" ? "Supprimer la sélection" : "Delete selection"}</button>
              <button onClick={clearSel} className="kr-btn kr-btn-ghost text-sm ml-auto" data-testid="sel-clear">{lang === "fr" ? "Tout désélectionner" : "Deselect all"}</button>
            </div>

            <div className="relative ml-auto sm:hidden">
              <button onClick={() => setActionsOpen(o => !o)} className="kr-btn kr-btn-primary text-sm" data-testid="sel-actions-btn">{lang === "fr" ? "Actions" : "Actions"} ▾</button>
              {actionsOpen && (
                <div className="absolute bottom-full right-0 mb-2 w-60 bg-[#141414] border border-[rgba(216,48,28,0.35)] rounded-lg shadow-xl p-2 flex flex-col gap-1" data-testid="sel-actions-menu">
                  <button onClick={() => { setActionsOpen(false); downloadSelection(); }} className="text-left text-sm px-3 py-2 rounded hover:bg-white/5 text-zinc-100" data-testid="sel-download-m">{lang === "fr" ? "Télécharger la sélection" : "Download selection"}</button>
                  <select onChange={e => { if (e.target.value) { moveSelection(e.target.value); setActionsOpen(false); } e.target.value = ""; }} className="text-sm bg-[#141414] border border-[rgba(216,48,28,0.35)] rounded px-2 py-2 text-zinc-100" data-testid="sel-move-m" defaultValue="">
                    <option value="">{lang === "fr" ? "Déplacer vers…" : "Move to…"}</option>
                    {sel.type === "photos" && <optgroup label={lang === "fr" ? "Section photo" : "Photo section"}>{(gallery.photo_sections || []).map(s => <option key={s.id} value={s.id}>{lang === "fr" ? s.fr : s.en}</option>)}</optgroup>}
                    <optgroup label={lang === "fr" ? "Livrable" : "Deliverable"}>{["photos", "plans", "drone"].filter(ty => ty !== sel.type && (gallery.deliverables || []).some(x => x.type === ty)).map(ty => <option key={ty} value={`deliv:${ty}`}>{ty === "plans" ? (lang === "fr" ? "Plans 2D et mesures" : "2D plans & measures") : ty === "drone" ? (lang === "fr" ? "Photos drone" : "Drone photos") : "Photos"}</option>)}</optgroup>
                  </select>
                  <button onClick={() => { setActionsOpen(false); deleteSelection(); }} className="text-left text-sm px-3 py-2 rounded hover:bg-white/5 text-red-400" data-testid="sel-delete-m">{lang === "fr" ? "Supprimer la sélection" : "Delete selection"}</button>
                  <button onClick={() => { setActionsOpen(false); clearSel(); }} className="text-left text-sm px-3 py-2 rounded hover:bg-white/5 text-zinc-100" data-testid="sel-clear-m">{lang === "fr" ? "Tout désélectionner" : "Deselect all"}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </>)}
    </div>
    </div>
  );
}

// ===== Main App =====
function useSeoTags({ lang, isPublic, title, description }) {
  useEffect(() => {
    document.documentElement.lang = lang === "en" ? "en-CA" : "fr-CA";
    if (title) document.title = title;
    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    if (description) setMeta("description", description);
    setMeta("robots", isPublic ? "index,follow" : "noindex,nofollow");
  }, [lang, isPublic, title, description]);
}

function Shell() {
  const [lang, setLang] = useLang();
  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();

  const path = location.pathname;
  const isPublic = path === "/" || path === "/reserver";
  const titleMap = {
    "/": "Réserver un photographe immobilier en ligne | Kréatif",
    "/reserver": "Réservation en ligne | Kréatif",
    "/admin": "Administration | Kréatif",
    "/seances": "Séances | Kréatif",
    "/mon-espace": "Mon espace | Kréatif",
    "/equipe": "Accès équipe | Kréatif",
    "/connexion": "Connexion | Kréatif",
  };
  const title = titleMap[path] || (path.startsWith("/galerie") ? "Galerie privée | Kréatif" : "Kréatif");
  useSeoTags({
    lang, isPublic, title,
    description: "Réservez photos, vidéo et drone immobilier en quelques clics. Prix affichés d'avance. Rive-Nord, Laval, Montréal, Laurentides. Aucun paiement à la réservation.",
  });

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoaded(true); return; }
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null); toast.success("Déconnecté");
  };

  if (location.hash?.includes("session_id=")) return <AuthCallback/>;

  return (
    <>
      <div className="kr-ambient" aria-hidden="true" />
      <CardGlow />
      <Toaster theme="dark" position="top-right"/>
      <Header lang={lang} setLang={setLang} user={user} onLogout={logout}/>
      <Routes>
        <Route path="/" element={<Landing lang={lang}/>}/>
        <Route path="/reserver" element={<BookingWizard lang={lang}/>}/>
        <Route path="/auth/callback" element={<AuthCallback/>}/>
        <Route path="/admin" element={
          <RequireRole user={user} loaded={loaded} roles={["admin"]} lang={lang} area="administration">
            <AdminDashboard lang={lang}/>
          </RequireRole>}/>
        <Route path="/seances" element={
          loaded && user && !["admin","team","photographer"].includes(user.role) ? <CourtierDashboard lang={lang}/> : (
          <RequireRole user={user} loaded={loaded} roles={["admin","team","photographer"]} lang={lang} area="équipe">
            <SessionsPage user={user} lang={lang}/>
          </RequireRole>)}/>
        <Route path="/mon-espace" element={user ? (["admin","team","photographer"].includes(user.role) ? <SessionsPage user={user} lang={lang}/> : <CourtierDashboard lang={lang}/>) : <Landing lang={lang}/>}/>
        <Route path="/connexion" element={user ? <Landing lang={lang}/> : <LoginPage lang={lang}/>}/>
        <Route path="/equipe" element={<TeamLogin user={user} loaded={loaded} lang={lang}/>}/>
        <Route path="/galerie/:gid" element={<GalleryPage lang={lang}/>}/>
      </Routes>
      <footer className="max-w-7xl mx-auto px-4 sm:px-8 py-12 border-t border-[rgba(216,48,28,0.35)] mt-20 text-center text-zinc-500 text-sm">
        kreatif.ca — <a href="https://www.kreatif.ca" target="_blank" rel="noopener noreferrer" className="hover:text-[#D8301C] transition">www.kreatif.ca</a>
        <span className="mx-2 text-zinc-700">·</span>
        <Link to="/equipe" className="hover:text-[#D8301C] transition" data-testid="footer-team-access">{lang === "fr" ? "Accès équipe" : "Team access"}</Link>
      </footer>
    </>
  );
}

export default function App() {
  return <BrowserRouter><Shell/></BrowserRouter>;
}
